import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import "highlight.js/styles/github.css";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { LOCALES } from "../../lang";
import arEGMessages from "../../lang/translations/ar_EG.json";
import enUSMessages from "../../lang/translations/en_US.json";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Typography,
  Box,
  // Chip,
  Fade,
  Slide,
  Tooltip,
  // Badge,
  // Fab,
  CircularProgress,
  // LinearProgress,
  Skeleton,
  useTheme,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import {
  Close,
  Send,
  SmartToy,
  // Psychology,
  ThumbUp,
  ThumbDown,
  // BarChart,
  // TableChart,
  // PictureAsPdf,
  ContentCopy,
  Fullscreen,
  FullscreenExit,
  History,
  Add,
  Delete,
  Download,
  CheckCircle,
  Error,
  // Login,
  // PersonAdd,
  // Lock,
  Refresh,
  Stop,
  // Warning,
  // Assignment,
  VolumeUp,
  VolumeOff,
  Mic,
  Search,
  ExpandMore,
  ExpandLess,
  Pause,
  PlayArrow,
  OpenInNew,
  KeyboardDoubleArrowUp,
} from "@mui/icons-material";
import {
  streamChatResponse,
  getChatHistory,
  getConversationMessages,
  saveChat,
  deleteChat,
  submitFeedback,
  exportMessage,
  exportData,
  handleSpeakMessage,
  speechToText,
  API_VERSION,
} from "./mockApi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ChatBot.scss";
import { END_POINTS } from "constants/APIConstant";

// Compare props so we skip re-render unless graphUrl or isDarkMode actually change (avoids rerender on scroll/typing)
function graphIframePropsEqual(prev, next) {
  return prev.graphUrl === next.graphUrl && prev.isDarkMode === next.isDarkMode;
}

const GRAPH_RELOAD_DELAY_MS = 2000;
const GRAPH_MAX_RETRIES = 3;

// Keep graph rendering consistent across screen sizes:
// - fixed "desktop-like" canvas width
// - scroll inside the popup/bubble on small screens (Excel-like)
const GRAPH_IFRAME_FIXED_WIDTH = 640;
const GRAPH_IFRAME_MIN_HEIGHT = 300;
const GRAPH_IFRAME_DEFAULT_HEIGHT = 500;

const CHAT_HISTORY_PAGE_LIMIT = 20;

const HISTORY_BUCKET_LABEL_AR = {
  today: "اليوم",
  yesterday: "أمس",
  last_week: "الأسبوع الماضي",
  this_week: "هذا الأسبوع",
  last_month: "الشهر الماضي",
  this_month: "هذا الشهر",
  more_than_a_month_ago: "أكثر من شهر مضى",
  older: "أقدم",
  other: "أخرى",
};

const HISTORY_SECTION_DEFAULT_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  last_week: "Last Week",
  this_week: "This Week",
  last_month: "Last Month",
  this_month: "This Month",
  more_than_a_month_ago: "More than a Month Ago",
  older: "Older",
  other: "Other",
};

function labelToHistoryGroupKey(label) {
  const k = String(label || "other")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
  return k || "other";
}

function normalizeHistoryGroupKey(input) {
  const normalized = labelToHistoryGroupKey(input);
  const aliases = {
    today: "today",
    yesterday: "yesterday",
    lastweek: "last_week",
    last_week: "last_week",
    this_week: "last_week",
    lastmonth: "last_month",
    last_month: "last_month",
    this_month: "last_month",
    morethanamonthago: "more_than_a_month_ago",
    more_than_a_month_ago: "more_than_a_month_ago",
    more_than_month_ago: "more_than_a_month_ago",
    older: "more_than_a_month_ago",
  };
  return aliases[normalized] || normalized;
}

function dedupeChatsById(chats) {
  const seen = new Set();
  const out = [];
  for (const c of chats || []) {
    const id = c?.id;
    if (id == null || id === "") continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(c);
  }
  return out;
}

function flattenHistorySections(state) {
  const seen = new Set();
  const out = [];
  for (const key of state.order || []) {
    const sec = state.byKey[key];
    if (!sec?.chats) continue;
    for (const chat of sec.chats) {
      if (chat?.id == null || chat.id === "") continue;
      if (seen.has(chat.id)) continue;
      seen.add(chat.id);
      out.push(chat);
    }
  }
  return out;
}

function mergeHistorySectionGroups(prev, groups) {
  const order = [...(prev.order || [])];
  const byKey = { ...(prev.byKey || {}) };
  for (const g of groups || []) {
    const key = normalizeHistoryGroupKey(g.key || g.label);
    const incoming = dedupeChatsById(g.chats || []);
    const resolvedLabel =
      HISTORY_SECTION_DEFAULT_LABELS[key] || g.label || g.key || key;
    if (!byKey[key]) {
      order.push(key);
      byKey[key] = { label: resolvedLabel, chats: incoming };
    } else {
      byKey[key] = {
        label: resolvedLabel || byKey[key].label,
        chats: dedupeChatsById([...(byKey[key].chats || []), ...incoming]),
      };
    }
  }
  return { order, byKey };
}

function replaceHistorySectionGroups(groups) {
  return mergeHistorySectionGroups({ order: [], byKey: {} }, groups);
}

function mapChatsAcrossHistorySections(state, mapper) {
  const byKey = {};
  for (const key of state.order || []) {
    const sec = state.byKey[key];
    if (!sec) continue;
    byKey[key] = {
      ...sec,
      chats: (sec.chats || []).map(mapper),
    };
  }
  return { order: [...(state.order || [])], byKey };
}

function upsertChatInHistorySections(state, chat, preferredKey = "today") {
  const normalizedPreferredKey = normalizeHistoryGroupKey(preferredKey);
  const byKey = {};
  for (const key of state.order || []) {
    const sec = state.byKey[key];
    if (!sec) continue;
    byKey[key] = {
      ...sec,
      chats: (sec.chats || []).filter((c) => c.id !== chat.id),
    };
  }
  let order = [...(state.order || [])];
  if (!byKey[normalizedPreferredKey]) {
    order = [normalizedPreferredKey, ...order];
    byKey[normalizedPreferredKey] = {
      label:
        HISTORY_SECTION_DEFAULT_LABELS[normalizedPreferredKey] ||
        normalizedPreferredKey,
      chats: [],
    };
  }
  byKey[normalizedPreferredKey] = {
    ...byKey[normalizedPreferredKey],
    chats: dedupeChatsById([
      chat,
      ...(byKey[normalizedPreferredKey].chats || []),
    ]),
  };
  return { order, byKey };
}

function removeChatFromHistorySections(state, chatId) {
  const byKey = {};
  for (const key of state.order || []) {
    const sec = state.byKey[key];
    if (!sec) continue;
    byKey[key] = {
      ...sec,
      chats: (sec.chats || []).filter((c) => c.id !== chatId),
    };
  }
  const newOrder = (state.order || []).filter(
    (key) => (byKey[key]?.chats || []).length > 0
  );
  const trimmed = {};
  for (const key of newOrder) {
    trimmed[key] = byKey[key];
  }
  return { order: newOrder, byKey: trimmed };
}

function historySectionTitle(label, dir) {
  if (dir !== "rtl") return label;
  const lookupKey = labelToHistoryGroupKey(label);
  return HISTORY_BUCKET_LABEL_AR[lookupKey] || label;
}

// Component to handle graph iframe with error fallback — memoized and stable state to avoid rerenders on parent actions
const GraphIframe = React.memo(function GraphIframe({ graphUrl, isDarkMode }) {
  const [hasError, setHasError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [retryKey, setRetryKey] = React.useState(0);
  const iframeRef = React.useRef(null);
  const timeoutRef = React.useRef(null);
  const settledRef = React.useRef(false);
  const retryCountRef = React.useRef(0);

  React.useEffect(() => {
    retryCountRef.current = 0;
  }, [graphUrl]);

  React.useEffect(() => {
    settledRef.current = false;
    setHasError(false);
    setIsLoading(true);
    timeoutRef.current = setTimeout(() => {
      if (settledRef.current) return;
      settledRef.current = true;
      setHasError(true);
      setIsLoading(false);
    }, 10000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [graphUrl, retryKey]);

  React.useEffect(() => {
    if (!hasError) return;
    const retryTimer = setTimeout(() => {
      if (retryCountRef.current >= GRAPH_MAX_RETRIES) return;
      retryCountRef.current += 1;
      setRetryKey((k) => k + 1);
    }, GRAPH_RELOAD_DELAY_MS);
    return () => clearTimeout(retryTimer);
  }, [hasError]);

  const handleIframeError = React.useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (settledRef.current) return;
    settledRef.current = true;
    setHasError(true);
    setIsLoading(false);
  }, []);

  const handleIframeLoad = React.useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (settledRef.current) return;
    setTimeout(() => {
      if (settledRef.current) return;
      settledRef.current = true;
      try {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          /* loaded */
        }
      } catch (e) {
        // CORS blocks contentWindow; keep showing iframe (do not set hasError to avoid extra rerender)
        console.error("Graph iframe failed to load", e);
      }
      setIsLoading(false);
    }, 500);
  }, []);

  const openInNewTab = React.useCallback(() => {
    window.open(graphUrl, "_blank", "noopener,noreferrer");
  }, [graphUrl]);

  const openButtonSx = React.useMemo(
    () => ({
      fontFamily: "Tajawal, Cairo, sans-serif",
      fontWeight: 600,
      fontSize: "0.875rem",
      textTransform: "none",
      borderRadius: "10px",
      px: 2,
      py: 1.25,
      boxShadow: isDarkMode
        ? "0 2px 8px rgba(0,0,0,0.2)"
        : "0 2px 12px rgba(var(--main-color-rgb, 9, 134, 237), 0.25)",
      backgroundColor: "var(--main-color)",
      color: "#fff",
      "&:hover": {
        backgroundColor: "var(--main-color)",
        opacity: 0.92,
        boxShadow: isDarkMode
          ? "0 4px 12px rgba(0,0,0,0.3)"
          : "0 4px 16px rgba(var(--main-color-rgb, 9, 134, 237), 0.35)",
        transform: "translateY(-1px)",
      },
      "&:active": { transform: "translateY(0)" },
      transition: "all 0.2s ease",
    }),
    [isDarkMode]
  );

  const openButtonOutlinedSx = React.useMemo(
    () => ({
      fontFamily: "Tajawal, Cairo, sans-serif",
      fontWeight: 600,
      fontSize: "0.8125rem",
      textTransform: "none",
      borderRadius: "10px",
      px: 1.5,
      py: 0.75,
      borderWidth: "1.5px",
      borderColor: "var(--main-color)",
      color: "var(--main-color)",
      "&:hover": {
        borderWidth: "1.5px",
        borderColor: "var(--main-color)",
        backgroundColor: "rgba(var(--main-color-rgb, 9, 134, 237), 0.08)",
        transform: "translateY(-1px)",
      },
      "&:active": { transform: "translateY(0)" },
      transition: "all 0.2s ease",
    }),
    []
  );

  if (hasError) {
    return (
      <Box
        sx={{
          width: "100%",
          margin: "16px 0",
          border: "1px solid",
          borderColor: isDarkMode
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.12)",
          borderRadius: "12px",
          padding: "24px",
          textAlign: "center",
          backgroundColor: isDarkMode
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.02)",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Tajawal, sans-serif",
            marginBottom: "20px",
            color: isDarkMode
              ? "rgba(255, 255, 255, 0.8)"
              : "rgba(0, 0, 0, 0.7)",
            lineHeight: 1.6,
          }}
        >
          <FormattedMessage id="chatbot.graphIframeError" />
        </Typography>
        <Button
          variant="contained"
          startIcon={
            <OpenInNew sx={{ fontSize: "1.1rem", marginInlineEnd: "4px" }} />
          }
          onClick={openInNewTab}
          sx={openButtonSx}
        >
          <FormattedMessage id="chatbot.openGraphInNewTab" />
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        margin: "16px 0",
      }}
    >
      {/* Button to open in new tab - always visible as fallback */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "10px",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={
            <OpenInNew sx={{ fontSize: "1rem", marginInlineEnd: "4px" }} />
          }
          onClick={openInNewTab}
          sx={openButtonOutlinedSx}
        >
          <FormattedMessage id="chatbot.openInNewTab" />
        </Button>
      </Box>

      {/* Iframe container — overflow:auto enables horizontal scroll if graph is wider than the bubble */}
      <Box
        sx={{
          width: "100%",
          border: "1px solid",
          borderColor: isDarkMode
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.2)",
          borderRadius: "8px",
          overflow: "auto !important",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          position: "relative",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isDarkMode
                ? "rgba(0, 0, 0, 0.3)"
                : "rgba(255, 255, 255, 0.8)",
              zIndex: 1,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <iframe
          key={retryKey}
          ref={iframeRef}
          src={graphUrl}
          style={{
            width: "100%",
            minWidth: GRAPH_IFRAME_FIXED_WIDTH,
            // minHeight: "400px",
            // maxHeight: "2500px",
            height: Math.max(
              GRAPH_IFRAME_MIN_HEIGHT,
              GRAPH_IFRAME_DEFAULT_HEIGHT
            ),
            minHeight: GRAPH_IFRAME_MIN_HEIGHT,
            border: "none",
            display: "block",
            WebkitOverflowScrolling: "touch",
            backgroundColor: "#fff",
          }}
          title="Graph"
          allowFullScreen
          onError={handleIframeError}
          onLoad={handleIframeLoad}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        />
      </Box>
    </Box>
  );
}, graphIframePropsEqual);

// Wrapper used only when rendering graph from markdown link — stable key + memo so parent rerenders (scroll/typing) do not rerender the iframe box
const GraphIframeCell = React.memo(function GraphIframeCell({
  graphUrl,
  isDarkMode,
}) {
  return <GraphIframe graphUrl={graphUrl} isDarkMode={isDarkMode} />;
},
graphIframePropsEqual);

// Cache for markdown components so the same reference is reused when only scroll/typing/input change (avoids rerender of ReactMarkdown and graph iframe)
const MARKDOWN_COMPONENTS_CACHE_MAX = 50;
const markdownComponentsCache = new Map();
const markdownComponentsCacheKeys = [];

function getCachedMarkdownComponents(key, factory) {
  if (markdownComponentsCache.has(key)) {
    return markdownComponentsCache.get(key);
  }
  const value = factory();
  markdownComponentsCache.set(key, value);
  markdownComponentsCacheKeys.push(key);
  if (markdownComponentsCacheKeys.length > MARKDOWN_COMPONENTS_CACHE_MAX) {
    const oldest = markdownComponentsCacheKeys.shift();
    markdownComponentsCache.delete(oldest);
  }
  return value;
}

// Extract table element attributes from rehype node (raw HTML from API response).
// Ensures rowSpan/colSpan and other table attributes are applied so UI is not broken.
function getTableElementProps(node) {
  if (!node || !node.properties) return {};
  const p = node.properties;
  const props = {};
  if (p.rowspan != null || p.rowSpan != null)
    props.rowSpan = Math.max(1, Number(p.rowSpan ?? p.rowspan) || 1);
  if (p.colspan != null || p.colSpan != null)
    props.colSpan = Math.max(1, Number(p.colSpan ?? p.colspan) || 1);
  if (p.className != null) props.className = p.className;
  else if (p.class != null) props.className = p.class;
  if (p.id != null) props.id = p.id;
  if (p.border != null) props.border = p.border;
  if (p.style != null) {
    if (typeof p.style === "object" && p.style !== null) props.style = p.style;
    else if (typeof p.style === "string" && p.style.trim()) {
      const styleObj = {};
      p.style.split(";").forEach((part) => {
        const [key, val] = part.split(":").map((s) => s && s.trim());
        if (key && val) {
          const camel = key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
          styleObj[camel] = val;
        }
      });
      if (Object.keys(styleObj).length) props.style = styleObj;
    }
  }
  return props;
}

// Memoized v1 graph iframe — يعرض الرسم من message.graph (srcdoc) ولا يُعاد رسمه إلا عند تغيّر الرسالة أو fullscreen أو ارتفاع الرسم فقط، لتفادي الـ rerender عند scroll/typing/sidebar
const GRAPH_FIXED_WIDTH_DEFAULT = 640;
const GRAPH_MIN_HEIGHT_DEFAULT = 300;
const GRAPH_MAX_HEIGHT_DEFAULT = 2500;

const V1GraphIframeMemo = React.memo(
  function V1GraphIframeMemo({
    messageId,
    processedHtml,
    isFullscreen,
    graphHeight,
    onHeightLoad,
    fixedWidth = GRAPH_FIXED_WIDTH_DEFAULT,
    minHeight = GRAPH_MIN_HEIGHT_DEFAULT,
    maxHeight = GRAPH_MAX_HEIGHT_DEFAULT,
  }) {
    const height = graphHeight ?? minHeight;
    return (
      <Box
        className="message-graph"
        sx={{
          marginTop: 2,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          overflow: "auto",
          backgroundColor: "#fff",
          width: "100%",
          maxWidth: "100%",
          position: "relative",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <iframe
          key={`graph-${messageId}-${
            isFullscreen ? "fullscreen" : "minimized"
          }`}
          srcDoc={processedHtml}
          className="message-graph-iframe"
          style={{
            width: fixedWidth,
            minWidth: fixedWidth,
            height,
            minHeight,
            maxHeight,
            border: "none",
            display: "block",
            backgroundColor: "#fff",
          }}
          title="Chart Visualization"
          sandbox="allow-scripts allow-same-origin"
          onLoad={(e) => {
            const iframe = e.target;
            const updateHeight = () => {
              try {
                const doc =
                  iframe.contentDocument ||
                  (iframe.contentWindow && iframe.contentWindow.document);
                if (!doc) return;
                const raw =
                  doc.documentElement.scrollHeight ||
                  doc.body.scrollHeight ||
                  minHeight;
                const h = Math.max(
                  minHeight,
                  Math.min(maxHeight, Math.ceil(raw) + 12)
                );
                if (typeof onHeightLoad === "function")
                  onHeightLoad(messageId, h);
              } catch (_) {}
            };
            [400, 800, 1200].forEach((delay) =>
              setTimeout(updateHeight, delay)
            );
            try {
              if (
                iframe.contentWindow &&
                iframe.contentWindow.Chart &&
                iframe.contentWindow.Chart.instances
              ) {
                setTimeout(() => {
                  const instances = iframe.contentWindow.Chart.instances;
                  const chartList = Array.isArray(instances)
                    ? instances
                    : Object.values(instances);
                  chartList.forEach((chart) => {
                    if (chart && typeof chart.resize === "function")
                      chart.resize();
                  });
                  if (iframe.contentWindow)
                    iframe.contentWindow.dispatchEvent(new Event("resize"));
                  updateHeight();
                }, 150);
              }
            } catch (_) {}
          }}
          onError={(e) => {
            console.error("Graph iframe failed to load", e);
          }}
        />
      </Box>
    );
  },
  (prev, next) =>
    prev.messageId === next.messageId &&
    prev.processedHtml === next.processedHtml &&
    prev.isFullscreen === next.isFullscreen &&
    prev.graphHeight === next.graphHeight
);

const createExportedExcelFileName = ({ fileIndex = 0, totalFiles = 1 }) => {
  const suffix = totalFiles > 1 ? `-${fileIndex + 1}` : "";
  return `Exported_Data${suffix}.xlsx`;
};

const MD_LINK_PLACEHOLDER_PREFIX = "___MDLINKPH_";

const cleanMarkdownLinkUrl = (url) => {
  let cleanUrl = String(url || "")
    .replace(/<\s*br\s*\/?>/gi, "")
    .trim()
    .replace(/:\s*\/\s*\//g, "://")
    .replace(/\s+/g, "");
  if (
    cleanUrl &&
    !cleanUrl.match(/^https?:\/\//) &&
    !cleanUrl.startsWith("/")
  ) {
    cleanUrl = `https://${cleanUrl}`;
  }
  return cleanUrl;
};

const isSpecialMarkdownLinkLabel = (label) => {
  const t = String(label || "").trim().toUpperCase();
  return t === "GRAPH" || t === "EXCEL" || t === "COVER";
};

const escapeHtmlText = (text) =>
  String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeHtmlAttr = (text) =>
  escapeHtmlText(text).replace(/"/g, "&quot;");

/** Preserve [text](url) while newlines are converted to <br /> (stream may split ] and ( across lines). */
const preserveMarkdownLinks = (text) => {
  const links = [];
  let i = 0;
  const out = text.replace(
    /\[([^\]]+)\]\s*(?:<\s*br\s*\/?>|\n|\r\n|\s)*\(\s*([^)]*)\s*\)/gi,
    (match, linkText, url) => {
      const label = String(linkText).trim();
      const ph = `${MD_LINK_PLACEHOLDER_PREFIX}${i}__`;
      links.push({ ph, label, url: cleanMarkdownLinkUrl(url) });
      i += 1;
      return ph;
    }
  );
  return { text: out, links };
};

const restoreMarkdownLinks = (text, links) => {
  let out = text;
  for (const { ph, label, url } of links) {
    out = out.split(ph).join(`[${label}](${url})`);
  }
  return out;
};

/** Repair links broken by <br /> between label and URL; render normal links as HTML <a> for rehypeRaw. */
const ensureRenderableMarkdownLinks = (text) => {
  return text.replace(
    /\[([^\]]+)\]\s*(?:<\s*br\s*\/?>|\n|\r\n|\s)*\(\s*([^)]*)\s*\)/gi,
    (match, linkText, url) => {
      const label = String(linkText).trim();
      if (!label || isSpecialMarkdownLinkLabel(label)) return match;
      const href = cleanMarkdownLinkUrl(url);
      if (!href) return match;
      return `<a href="${escapeHtmlAttr(
        href
      )}" target="_blank" rel="noopener noreferrer" dir="auto">${escapeHtmlText(
        label
      )}</a>`;
    }
  );
};

// Extract Excel download URLs from message text (markdown [EXCEL](url) or [*](*.xlsx))
const extractExcelUrlsFromText = (text) => {
  if (!text || typeof text !== "string") return [];
  const urls = [];
  // Match markdown links: [label](href) — allow optional whitespace between ] and (
  const linkRe = /\[([^\]]*)\]\s*\(([^)]+)\)/g;
  let m;
  while ((m = linkRe.exec(text)) !== null) {
    const label = (m[1] || "").trim().toUpperCase();
    // Normalize href: remove any spaces/newlines (stream may split the filename across chunks)
    let href = (m[2] || "").replace(/\s+/g, "").trim();
    if (!href) continue;
    const isExcelLabel = label === "EXCEL";
    const isXlsxHref = /\.xlsx$/i.test(href);
    if (!isExcelLabel && !isXlsxHref) continue;
    let fileUrl = href.replace(/^\/+/, "").trim();
    if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
      const match = fileUrl.match(/([^/]+\.xlsx)(?:\/?)?$/i);
      if (match) fileUrl = match[1];
      else fileUrl = fileUrl.replace(/^https?:\/\/[^/]*\/?/i, "");
    }
    urls.push(`${END_POINTS.CHATBOT_API_BASE_URL_FILE}/${fileUrl}`);
  }
  return urls;
};

const stripExcelLinksFromText = (text) => {
  if (!text || typeof text !== "string") return text || "";
  // Remove markdown excel links and common rtl-visual reversed form "(file.xlsx)[EXCEL]"
  return text
    .replace(/\[\s*EXCEL\s*\]\s*\(([^)]+)\)/gi, "")
    .replace(/\(([^)]+\.xlsx)\)\s*\[\s*EXCEL\s*\]/gi, "")
    .trim();
};

const extractGraphUrlsFromText = (text) => {
  if (!text || typeof text !== "string") return [];
  const urls = [];
  const graphLinkRe = /\[\s*GRAPH\s*\]\s*\(([^)]+)\)/gi;
  let m;
  while ((m = graphLinkRe.exec(text)) !== null) {
    let href = (m[1] || "").replace(/\s+/g, "").trim();
    if (!href) continue;
    if (!/\.html$/i.test(href)) continue;
    let fileName = href.replace(/^\/+/, "");
    if (fileName.startsWith("http://") || fileName.startsWith("https://")) {
      const parts = fileName.split("/");
      fileName = parts[parts.length - 1] || "";
    }
    if (!fileName || !/\.html$/i.test(fileName)) continue;
    urls.push(`${END_POINTS.CHATBOT_API_BASE_URL_GRAPH}/${fileName}`);
  }
  return Array.from(new Set(urls));
};

const stripGraphLinksFromText = (text) => {
  if (!text || typeof text !== "string") return text || "";
  return text.replace(/\[\s*GRAPH\s*\]\s*\(([^)]+)\)/gi, "").trim();
};

/** Build full graph iframe URL from href in [GRAPH](...) */
function normalizeGraphHrefToUrl(hrefRaw) {
  let href = (hrefRaw || "").replace(/\s+/g, "").trim();
  if (!href || !/\.html$/i.test(href)) return null;
  let fileName = href.replace(/^\/+/, "");
  if (fileName.startsWith("http://") || fileName.startsWith("https://")) {
    const parts = fileName.split("/");
    fileName = parts[parts.length - 1] || "";
  }
  if (!fileName || !/\.html$/i.test(fileName)) return null;
  return `${END_POINTS.CHATBOT_API_BASE_URL_GRAPH}/${fileName}`;
}

/** Build full Excel file URL from href in [EXCEL](...) or [label](*.xlsx) */
function normalizeExcelHrefToUrl(hrefRaw) {
  let href = (hrefRaw || "").replace(/\s+/g, "").trim();
  if (!href || !/\.xlsx$/i.test(href)) return null;
  let fileUrl = href.replace(/^\/+/, "").trim();
  if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
    const match = fileUrl.match(/([^/]+\.xlsx)(?:\/?)?$/i);
    if (match) fileUrl = match[1];
    else fileUrl = fileUrl.replace(/^https?:\/\/[^/]*\/?/i, "");
  }
  return `${END_POINTS.CHATBOT_API_BASE_URL_FILE}/${fileUrl}`;
}

/**
 * Split assistant HTML/markdown so [GRAPH](...) and Excel links stay in stream order
 * (rendered inline instead of only at the bottom of the bubble).
 */
function splitIntoInlineMediaSegments(text) {
  if (!text || typeof text !== "string")
    return [{ type: "markdown", content: text || "" }];

  const rawTokens = [];

  const pushMatches = (re, type) => {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      rawTokens.push({
        type,
        index: m.index,
        end: m.index + m[0].length,
        href: (m[1] || "").replace(/\s+/g, "").trim(),
      });
    }
  };

  pushMatches(/\[\s*GRAPH\s*\]\s*\(([^)]+)\)/gi, "graph");
  pushMatches(/\[\s*EXCEL\s*\]\s*\(([^)]+)\)/gi, "excel");
  pushMatches(/\[[^\]]*\]\s*\(([^)]+\.xlsx)\)/gi, "excel");

  rawTokens.sort((a, b) => a.index - b.index || b.end - a.end);

  const tokens = [];
  const seenRange = new Set();
  let lastEnd = -1;
  for (const t of rawTokens) {
    const rangeKey = `${t.index}:${t.end}`;
    if (seenRange.has(rangeKey)) continue;
    if (t.index < lastEnd) continue;
    seenRange.add(rangeKey);
    tokens.push(t);
    lastEnd = t.end;
  }

  const segments = [];
  let cursor = 0;
  for (const t of tokens) {
    if (t.index > cursor) {
      segments.push({ type: "markdown", content: text.slice(cursor, t.index) });
    }
    segments.push({ type: t.type, href: t.href });
    cursor = t.end;
  }
  if (cursor < text.length) {
    segments.push({ type: "markdown", content: text.slice(cursor) });
  }

  return segments.length ? segments : [{ type: "markdown", content: text }];
}

/**
 * Split a trailing Source / Reference / المصدر block so it can be rendered after chart & Excel
 * embeds. Those embeds are appended below the markdown in layout; moving this section avoids
 * showing citations above empty placeholders while the chart/Excel loads.
 */
function splitTrailingSourceSection(text) {
  if (!text || typeof text !== "string")
    return { main: text || "", sourceBlock: "" };
  const re =
    /(?:^|\n)((?:#{1,6}\s*)?(?:المصدر|Source|المرجع|References?)(?:\s*[:\uFE55\u061C]|\s*$)|\*\*(?:المصدر|Source|المرجع|References?)\*\*)/i;
  const m = text.match(re);
  if (!m || m.index === undefined) return { main: text, sourceBlock: "" };
  const splitAt = m.index;
  return {
    main: text.slice(0, splitAt).trimEnd(),
    sourceBlock: text.slice(splitAt).trim(),
  };
}

/** Graph/Excel creation SSE: show event strip (title only); no collapse control and no inner body text. */
function isGraphExcelHeaderOnlyEventBlock(eventType) {
  if (!eventType) return false;
  const t = String(eventType).trim().toLowerCase();
  return (
    t === "generate_excel" || t === "generate_graphs" || t === "generate_graph"
  );
}

// Component to display Excel like graph (v1): iframe embed or fallback card with download
const ExcelIframe = ({ excelUrls, isDarkMode }) => {
  const [hasError, setHasError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [srcIndex, setSrcIndex] = React.useState(0);
  const iframeRef = React.useRef(null);
  const timeoutRef = React.useRef(null);
  const firstUrl = excelUrls && excelUrls[0];

  React.useEffect(() => {
    setHasError(false);
    setIsLoading(true);
    setSrcIndex(0);
  }, [firstUrl]);

  React.useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (isLoading) {
        // Office/web previews can be slow; stop spinner but don't force fallback.
        setIsLoading(false);
      }
    }, 10000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isLoading, srcIndex, firstUrl]);

  const handleIframeLoad = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsLoading(false);
  };

  const handleIframeError = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (srcIndex === 0) {
      // Retry with direct file URL if Office embed is blocked.
      setSrcIndex(1);
      setIsLoading(true);
      return;
    }
    setHasError(true);
    setIsLoading(false);
  };

  const downloadExcelFile = async (url, fileIndex = 0, totalFiles = 1) => {
    if (!url) return;
    const finalFileName = createExportedExcelFileName({
      fileIndex,
      totalFiles,
    });

    try {
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(`Failed with status ${response.status}`);
      const blob = await response.blob();

      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = finalFileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Excel download failed, opening in new tab instead", error);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  if (!firstUrl) return null;

  const excelDownloadButtonSx = {
    fontFamily: "Tajawal, Cairo, sans-serif",
    fontWeight: 600,
    fontSize: "0.875rem",
    textTransform: "none",
    borderRadius: "10px",
    px: 2,
    py: 1.25,
    boxShadow: isDarkMode
      ? "0 2px 8px rgba(0,0,0,0.2)"
      : "0 2px 12px rgba(var(--main-color-rgb, 9, 134, 237), 0.25)",
    backgroundColor: "var(--main-color)",
    color: "#fff",
    "&:hover": {
      backgroundColor: "var(--main-color)",
      opacity: 0.92,
      boxShadow: isDarkMode
        ? "0 4px 12px rgba(0,0,0,0.3)"
        : "0 4px 16px rgba(var(--main-color-rgb, 9, 134, 237), 0.35)",
      transform: "translateY(-1px)",
    },
    "&:active": { transform: "translateY(0)" },
    transition: "all 0.2s ease",
  };

  const excelDownloadButtonOutlinedSx = {
    fontFamily: "Tajawal, Cairo, sans-serif",
    fontWeight: 600,
    fontSize: "0.8125rem",
    textTransform: "none",
    borderRadius: "10px",
    px: 1.5,
    py: 0.75,
    borderWidth: "1.5px",
    borderColor: "var(--main-color)",
    color: "var(--main-color)",
    "&:hover": {
      borderWidth: "1.5px",
      borderColor: "var(--main-color)",
      backgroundColor: "rgba(var(--main-color-rgb, 9, 134, 237), 0.08)",
      transform: "translateY(-1px)",
    },
    "&:active": { transform: "translateY(0)" },
    transition: "all 0.2s ease",
  };

  if (hasError) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: 300,
          border: "1px solid",
          borderColor: isDarkMode
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.12)",
          borderRadius: "12px",
          padding: "24px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isDarkMode
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.04)",
        }}
      >
        <Box sx={{ fontSize: 48, marginBottom: 1, opacity: 0.7 }}>📄</Box>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Tajawal, Cairo, sans-serif",
            marginBottom: 2,
            color: isDarkMode
              ? "rgba(255, 255, 255, 0.8)"
              : "rgba(0, 0, 0, 0.7)",
          }}
        >
          {excelUrls.length === 1 ? (
            <FormattedMessage id="chatbot.excelReadyToDownload" />
          ) : (
            <FormattedMessage
              id="chatbot.excelFilesReadyToDownload"
              values={{ count: excelUrls.length }}
            />
          )}
        </Typography>
        {excelUrls.map((url, i) => (
          <Button
            key={i}
            variant="contained"
            startIcon={<Download sx={{ fontSize: "1.1rem" }} />}
            onClick={() => downloadExcelFile(url, i, excelUrls.length)}
            sx={{ ...excelDownloadButtonSx, marginTop: i > 0 ? 1.5 : 0 }}
          >
            {excelUrls.length === 1 ? (
              <FormattedMessage id="chatbot.downloadExcelFile" />
            ) : (
              <FormattedMessage
                id="chatbot.downloadFileNumber"
                values={{ number: i + 1 }}
              />
            )}
          </Button>
        ))}
      </Box>
    );
  }

  const srcCandidates = [
    "https://view.officeapps.live.com/op/embed.aspx?src=" +
      encodeURIComponent(firstUrl),
    firstUrl,
  ];
  const iframeSrc = srcCandidates[Math.min(srcIndex, srcCandidates.length - 1)];

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Download button row — always visible above the viewer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          marginBottom: "10px",
          flexWrap: "wrap",
        }}
      >
        {excelUrls.map((url, i) => (
          <Button
            key={i}
            variant="outlined"
            size="small"
            startIcon={<Download sx={{ fontSize: "1rem" }} />}
            onClick={() => downloadExcelFile(url, i, excelUrls.length)}
            sx={excelDownloadButtonOutlinedSx}
          >
            {excelUrls.length === 1 ? (
              <FormattedMessage id="chatbot.downloadExcelFile" />
            ) : (
              <FormattedMessage
                id="chatbot.downloadFileNumber"
                values={{ number: i + 1 }}
              />
            )}
          </Button>
        ))}
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          minWidth: "100%",
          maxWidth: "100%",
          minHeight: 300,
          boxSizing: "border-box",
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isDarkMode
                ? "rgba(0, 0, 0, 0.3)"
                : "rgba(255, 255, 255, 0.8)",
              zIndex: 1,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          style={{
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%",
            height: "400px",
            minHeight: 300,
            border: "none",
            display: "block",
            boxSizing: "border-box",
          }}
          title="Excel"
          onError={handleIframeError}
          onLoad={handleIframeLoad}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        />
        {/* Office iframe is cross-origin, so hide only Excel app icon via local mask overlay */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            bottom: 0,
            width: 40,
            height: 25,
            backgroundColor: "#2f3640",
            borderTopRightRadius: "6px",
            zIndex: 10,
            pointerEvents: "auto",
            cursor: "default",
          }}
        />
      </Box>
    </Box>
  );
};

const ChatBot = () => {
  const intl = useIntl();
  const navigate = (path) => {
    if (typeof path === "string" && path) {
      try {
        window.location.assign(path);
      } catch {
        window.location.href = path;
      }
    }
  };

  const {
    fullName,
    firstName,
    lastName,
    email,
    token,
    id: userId,
  } = useSelector(({ auth }) => {
    return auth;
  });

  const dir = useSelector(({ theme }) => theme.direction);
  const locale = useSelector(({ theme }) => theme.locale);
  // Event box titles: read from the same JSON files as i18n (avoids stale copies; save JSON + restart dev server to see changes)
  const chatbotLocaleMessages = useMemo(() => {
    return locale === LOCALES.ENGLISH ? enUSMessages : arEGMessages;
  }, [locale]);
  const muiTheme = useTheme();
  const isSmallMobile = useMediaQuery("(max-width:420px)");
  const isTinyMobile = useMediaQuery("(max-width:360px)");
  const isDarkMode = muiTheme.palette.mode === "dark";

  // Get toast position based on locale
  const getToastPosition = () => {
    return locale === "ar" ? "top-left" : "top-right";
  };

  // Track if we're in dictionary zone (should stop accepting chunks)
  const inDictionaryZone = useRef(false);

  const normalizeStreamChunk = (prevText, chunk) => {
    if (!chunk) return prevText;

    // If we've already entered dictionary zone, don't add any more text
    if (inDictionaryZone.current) {
      return prevText; // Return previous text unchanged
    }

    let text;
    // When stream sends "<br" then ">" in separate chunks, prevText ends with "<br" and chunk is ">" — convert to newline immediately
    const brOpenMatch = prevText && String(prevText).match(/<br\s*$/i);
    const chunkStr = String(chunk);
    const chunkIsClosingBr =
      chunk &&
      (chunkStr === ">" || chunkStr.trim() === ">" || chunkStr.startsWith(">"));
    if (brOpenMatch && chunkIsClosingBr) {
      const rest = chunkStr === ">" ? "" : chunkStr.slice(1);
      text = String(prevText).slice(0, -brOpenMatch[0].length) + "\n" + rest;
    } else if (
      chunkStr.trim() === ">" &&
      (/\n\s*$/.test(prevText) || !prevText)
    ) {
      // Chunk is only ">" (e.g. after newline) — treat as line break, don't append literal ">"
      text = String(prevText).trimEnd() + "\n";
    } else {
      text = prevText + chunk;
    }

    // 1️⃣ <br> = newline (same rule as preprocessMarkdown so streamed markdown parses correctly)
    text = convertBrToNewlines(text);

    // 2️⃣ Check for ANY dictionary marker
    const markers = [
      { pattern: "links:", index: text.indexOf("links:") },
      { pattern: "links_cover:", index: text.indexOf("links_cover:") },
      { pattern: "{'graph':", index: text.indexOf("{'graph':") },
      { pattern: '{"graph":', index: text.indexOf('{"graph":') },
    ];

    // Filter out -1 (not found) and get the minimum index
    const validMarkers = markers.filter((m) => m.index !== -1);

    if (validMarkers.length > 0) {
      const earliestMarker = validMarkers.reduce((min, current) =>
        current.index < min.index ? current : min
      );

      inDictionaryZone.current = true; // Set flag to stop accepting more chunks
      text = text.substring(0, earliestMarker.index).trimEnd();
    }

    return text;
  };

  // <br> = newline. Stream can split as "202" + "1" + "<br" + ">" (chunks may add \n → "202\n1\n<br\n>").
  // (0) Collapse whitespace between digits so "202\n1<br>" → "2021<br>"; (1) digits+<br> → digits+\n; (2) any char+<br>; (3) standalone <br>; (4) <br at EOL.
  const convertBrToNewlines = (text) => {
    if (!text) return text;
    let t = String(text);
    // Collapse whitespace/newlines between digits so "202\n1<br>" is treated as "2021<br>"
    t = t.replace(/(\d)\s+(\d)/g, "$1$2");
    // \s* allows newlines between br and > (e.g. "<br\n>")
    t = t.replace(/(\d+)<\s*br\s*\/?\s*>\s*/gi, "$1\n");
    t = t.replace(/(.)<\s*br\s*\/?\s*>\s*/gi, "$1\n");
    t = t.replace(/<\s*br\s*\/?\s*>\s*/gi, "\n");
    t = t.replace(/<\s*br\s*\r?\n\s*>/gi, "\n");
    t = t.replace(/<\s*br\s*$/gim, "\n");
    return t;
  };

  // Strip JSON code blocks and bare JSON objects from event box content, keeping human-readable text
  const cleanEventBlockContent = (text) => {
    if (!text) return text;
    // Remove ```json ... ``` fenced code blocks
    let cleaned = text.replace(/```json[\s\S]*?```/gi, "").trim();
    // Remove ```...``` blocks that contain only a JSON object
    cleaned = cleaned
      .replace(/```[\s\S]*?```/g, (match) => {
        const inner = match
          .replace(/^```[^\n]*\n?/, "")
          .replace(/```$/, "")
          .trim();
        return inner.startsWith("{") && inner.endsWith("}") ? "" : match;
      })
      .trim();
    // Remove standalone JSON object lines (entire line is a JSON object)
    cleaned = cleaned.replace(/^\s*\{[^{}]*\}\s*$/gm, "").trim();
    // Collapse extra blank lines left behind
    cleaned = cleaned.replace(/\n{3,}/g, "\n\n").trim();
    return cleaned || text;
  };

  // Normalize age ranges: "-15 19" / " -15 19" / "19 15-" → "15 - 19" (for tables/markdown/HTML)
  const normalizeAgeRangesInText = (str) => {
    if (!str || typeof str !== "string") return str;
    let s = str;
    // (1) minus before first number: " -15 19" or ">-15 19" or "\n-15 19" → "15 - 19"
    s = s.replace(/(^|>|\s|\n)-(\d+)\s+(\d+)(?=\s*<|\s|$|\)|\n)/g, "$1$2 - $3");
    // (2) minus after second number: "19 15-" or "19 15 -" → "15 - 19" (smaller first)
    s = s.replace(/(\d+)\s+(\d+)\s*-(?=\s*<|\s|$|\)|\n)/g, (_, a, b) => {
      const n = parseInt(a, 10);
      const m = parseInt(b, 10);
      return n <= m ? `${n} - ${m}` : `${m} - ${n}`;
    });
    return s;
  };

  const preprocessMarkdown = (text) => {
    // Handle null, undefined, or empty values
    if (text == null || text === "") return text || "";

    // Ensure text is a string
    if (typeof text !== "string") {
      try {
        text = String(text);
      } catch (error) {
        console.warn(
          "Error converting text to string in preprocessMarkdown:",
          error
        );
        return "";
      }
    }

    let processed = text;

    // Remove any line that is only ">" (or "> " etc.) first so it never appears during stream
    processed = processed.replace(/^>\s*$/gm, "");

    // Apply br → newline first so every downstream step (tables, headings, lists) sees real line breaks.
    processed = convertBrToNewlines(processed);
    // Preserve markdown links before \n → <br /> so streamed "[text]\n(url)" stays valid.
    const { text: textWithLinkPlaceholders, links: preservedLinks } =
      preserveMarkdownLinks(processed);
    processed = textWithLinkPlaceholders;
    // Line breaks: "  \n" → "<br />" immediately so nothing visible (no placeholder) appears during stream; protect <br /> in ** step below.
    processed = processed.replace(/\n/g, "  \n");
    processed = processed.replace(/  \n/g, "<br />");
    processed = restoreMarkdownLinks(processed, preservedLinks);

    // Normalize age ranges in tables (markdown or HTML) — applied here and again at end (final pass)
    processed = normalizeAgeRangesInText(processed);

    // --- Horizontal rule (---) and headings: when stream sends "---###" with no newline, split so --- renders as <hr> and ### as heading
    processed = processed.replace(/---(\s*#{1,6}\s)/g, "---\n\n$1");

    // Normalize standalone horizontal rule: line of only dashes (3+) and optional spaces → "---" so it always renders as <hr/>
    // (Do not match table separator rows like "| --- | --- |" which contain pipes)
    processed = processed.replace(/^\s*[-]{3,}\s*$/gm, "---");

    // Tables: GFM requires no blank line between table rows; collapse multiple blank lines between two pipe-rows so table parses
    processed = processed.replace(
      /(\|\s*[^\n|]+(?:\|[^\n|]*)+\|)\s*\n\s*\n+(\s*\|)/g,
      "$1\n$2"
    );

    // --- Early bold fix: response may have spaces before **, after **, or around the text — normalize then ensure bold renders
    // 1) Remove space(s) before ** at line start (e.g. "  **text**" or "\n  **text**")
    processed = processed.replace(/(^|\n)\s+\*\*/g, "$1**");
    // 2) Fix space(s) after opening ** (e.g. "** معدل**" or "**  text**") so CommonMark sees left-flanking **
    processed = processed.replace(/\*\*\s+([^\s*\n])/g, "**$1");
    // 3) Collapse **...** (any chars/newlines); trim and collapse spaces inside so "**  معدل  **" → "**معدل**"
    processed = processed.replace(/\*\*([\s\S]*?)\*\*/g, (_, inner) => {
      const t = inner.replace(/\s+/g, " ").trim();
      return t ? `**${t}**` : "****";
    });
    // 4) When there's no space before/after **, parser may show asterisks literally — add one space at line boundaries
    processed = processed.replace(
      /(^|\n)(\*\*[^*\n]+?\*\*)(?=$|\n)/g,
      (_, before, bold) => `${before} ${bold} `
    );
    // 5) If content starts with **, add a leading newline so markdown parser treats it as block-level bold
    if (processed.match(/^\s*\*\*[^*\n]/)) {
      processed = processed.replace(/^\s*/, "\n");
    }

    // Remove empty graph JSON so it is not shown as literal in UI (e.g. {"graph": ""} or {'graph': ''})
    processed = processed
      .replace(/\{\s*["']graph["']\s*:\s*[""]\s*\}/g, "")
      .replace(/\{\s*["']graph["']\s*:\s*['']\s*\}/g, "")
      .trim();

    // After trim: if content starts with **, ensure a newline before it so ReactMarkdown renders bold (not literal asterisks)
    if (processed.match(/^\*\*[^*\n]/)) {
      processed = "\n" + processed;
    }

    // Fix table formatting: convert double pipes || to proper table rows with newlines (kept as base fix)
    // Pattern: | ... ||-----|| ... | becomes | ... |\n|-----|\n| ... |
    processed = processed.replace(/\|\|/g, "|\n|");

    // Additional fix for streamed content: rows sometimes arrive as "| row |" + "|" with no newline between rows
    // 1) Split when a full row is followed by start of next: "| row |" + "|" → "| row |\n|"
    let prevTable = "";
    while (prevTable !== processed) {
      prevTable = processed;
      processed = processed.replace(/(\|[^\n]*\|)\s*\|/g, "$1\n|");
    }
    // 2) Merge consecutive table-separator lines (only | - and spaces) into one line so GFM sees a single separator row
    //    e.g. "| ----------------|\n| ------------|\n|" → "|------------------|------------|"
    prevTable = "";
    while (prevTable !== processed) {
      prevTable = processed;
      processed = processed.replace(
        /(^\|[|\s\-]*\|?)\s*\n\s*^\|([|\s\-]*\|?)$/gm,
        "$1 $2"
      );
    }

    // Fix: intro text on the same line as a table header → split with blank line
    // e.g. "some text: | Col1 | Col2 |" or "text)| Col1 |" → text then blank line then table
    // \s* (not \s+) handles cases with zero spaces between the text and the first |
    processed = processed.replace(/^([^|\n]+\S)\s*(\|)/gm, "$1\n\n$2");
    // Stream: heading/title ending with year like "(2021)" can arrive glued to table "| المحافظة"
    processed = processed.replace(/([0-9])\)\s*(\|\s*)/g, "$1)\n\n$2");

    // Fix: table row ending | immediately followed by a bullet list item (|- text)
    // e.g. "| 76 |- notes" → "| 76 |\n\n- notes"
    processed = processed.replace(/\|(- )/g, "|\n\n$1");

    // Fix: text ending with ) immediately followed by "- " bullet (no newline between them)
    // e.g. "(2021)- إجمالي..." → "(2021)\n\n- إجمالي..."
    processed = processed.replace(/\)(- )/g, ")\n\n$1");

    // Stream often sends ")---|" with no newlines: end of link url, separator ---, start of next table. Split so we get 2 tables.
    processed = processed.replace(/\)---\s*\|/g, ")\n\n---\n\n| ");

    // Separate consecutive tables: GFM needs a blank line between two tables so they render as 2 tables not 1
    // // Table row ending with | then newline then bold (**) → add blank line so first table ends
    // processed = processed.replace(/\|\s*\n(\s*\*\*)/g, "|\n\n$1");
    // // Bold line then newline then table (|) → add blank line so second table is its own block
    // processed = processed.replace(/(\*\*[^*]*\*\*)\s*\n(\s*\|)/g, "$1\n\n$2");
    // --- Tables, titles, and source links: ensure correct separation so nothing is rendered as table rows ---
    // 0) Same line: last table row glued to link (e.g. "| الرابع | أخرى | 5.1 |[بحث القوى...") — split so the row stays in table and link goes below
    processed = processed.replace(
      /(\|(?:[^|\n]+\|)+[^|\n]*\|)\s*(\[)/g,
      (_, row, bracket) => row + "\n\n" + bracket
    );
    // 1) Table row then source link [text](url): link must appear BELOW table, not as a row → blank line before [
    processed = processed.replace(/\|\s*\r?\n(\s*\[)/g, "|\n\n$1");
    processed = processed.replace(
      /\|\s*\r?\n\s*<br\s*\/?>\s*\r?\n(\s*\[)/gi,
      "|\n\n$1"
    );
    // 2) Table row then bold (**) = next table title → blank line so first table ends before title
    processed = processed.replace(/\|\s*\r?\n(\s*\*\*)/g, "|\n\n$1");
    // 3) Line containing link ](url) then "---" or "**" (full line) → blank line so title/separator is below link, not inside table
    processed = processed.replace(
      /(\]\([^)]+\)[^\n]*)\r?\n(\s*(?:---|\*\*)[^\n]*)/g,
      "$1\n\n$2"
    );
    // 4) "---" then bold title → blank line so title is distinct
    processed = processed.replace(/---\s*\r?\n(\s*\*\*)/g, "---\n\n$1");
    // 5) Bold title then table (|) → blank line so second table is its own block
    processed = processed.replace(
      /(\*\*[\s\S]*?\*\*)\s*\r?\n(\s*\|)/g,
      "$1\n\n$2"
    );
    // 6) Title line ending with e.g. (2022), then table → blank line (title without **)
    processed = processed.replace(/(^[^\n|]+\))\s*\r?\n(\s*\|)/gm, "$1\n\n$2");

    // 6b) Same line: table row then bold (no newline) — e.g. "| 8317 |**ثانياً: حسب المحافظات**" → جدول ثم سطر فارغ ثم عنوان الجدول الثاني
    processed = processed.replace(/(\|[^\n]*\|)\s*(\*\*)/g, "$1\n\n$2");
    // 6c) Same line: bold at line start then table (no newline) — e.g. "**ثانياً: حسب المحافظات**| المحافظة |" → عنوان ثم سطر فارغ ثم الجدول الثاني (لا نطبق داخل خلية جدول)
    processed = processed.replace(
      /(^|\r?\n)(\*\*[^*]+\*\*)\s*(\|)/g,
      "$1$2\n\n$3"
    );
    // 6d) Two bold titles on one line (e.g. "...2023):****أولاً: حسب...") → split so GFM renders two headings (لا نطابق ) فقط حتى لا نكسر (2023))
    processed = processed.replace(/\):\s*\*\*\s*\*\*/g, "):**\n\n**");

    // 7) Rows that look like table rows but are section titles/links for the NEXT table (not data): put them outside the table
    //    So "last 2 rows" of table 1 (title + link/cover for table 2) render as normal text below table 1, not as table rows
    const titleRowPattern = /(\|\s*[^\n]+\|)\s*\r?\n(\|\s*([^\n]+?)\s*\|)/g;
    processed = processed.replace(
      titleRowPattern,
      (match, prevRow, fullTitleRow, content) => {
        const c = content.trim();
        const isTitleOrLink =
          /بحث\s*القوى|نشرة\s*إحصاءات|إحصاءات\s*الخدمات|وحدة\s*القياس|وفقا\s*ل(?:لجهاز|الجهاز)|لو\s*حابب|COVER|\[\s*C\s*OVER\s*\]|]\s*\(https?:\/\/|]\s*\(\//i.test(
            c
          );
        if (isTitleOrLink) {
          return `${prevRow}\n\n${c}\n`;
        }
        return match;
      }
    );
    // 8) Any remaining pipe-delimited line that contains title/link/cover/unit/attribution (e.g. "last rows" below table) → render as paragraph, not table row (repeat to handle multiple such rows)
    let prev = "";
    while (prev !== processed) {
      prev = processed;
      processed = processed.replace(
        /\n(\|\s*([^\n]*?(?:بحث\s*القوى|نشرة|إحصاءات\s*الخدمات|وحدة\s*القياس|وفقا\s*ل(?:لجهاز|الجهاز)|لو\s*حابب|COVER|\[\s*C\s*OVER\s*\]|]\s*\(https?:\/\/|]\s*\(\/)[^\n]*?)\s*\|)/g,
        "\n\n$2\n"
      );
    }
    // 8b) Lines that start with | and contain link/cover but may not end with | (e.g. streamed "| [link](url)" or "| [COVER](path)") → render outside table
    processed = processed.replace(
      /\n\|\s*([^\n]*?(?:بحث\s*القوى|نشرة|إحصاءات\s*الخدمات|وحدة\s*القياس|وفقا\s*ل(?:لجهاز|الجهاز)|لو\s*حابب|COVER|\[\s*C\s*OVER\s*\]|]\s*\([^)]*\))[^\n]*)\s*$/gm,
      (_, content) => "\n\n" + content.replace(/\s*\|?\s*$/, "").trim() + "\n"
    );

    // 9) جدول "عدد الأسرة في المستشفيات حسب المحافظات" (نوع المستشفى | المحافظة | عدد الأسرة | الوحدة): نهاية الجدول الحقيقية هي آخر صف بيانات (الصف السابع).
    //    كل ما بعده (يمكنك طلب...، وفقا للجهاز...، إحصاءات الخدمات...، الروابط، غلاف النشرة، إلخ) يجب أن يظهر أسفل الجدول وليس كصفوف داخل الجدول.
    //    نُدخل سطراً فارغاً بعد آخر صف حتى ينهي المحلل الجدول ويعرض الباقي كفقرة/روابط أسفله.
    const hospitalBedsTableWithTrailing =
      /(\|\s*[^\n]*(?:نوع|المستشفى)[^\n]*(?:المحافظة|عدد\s*الأسرة|الوحدة)[^\n]*\|)\r?\n(\|\s*[|\s\-]+\|?(?:\r?\n(?:[\s\-]+|\|\s*[|\s\-]*\|?))*)\r?\n(\|\s*[^\n]+\|)\r?\n(\|\s*[^\n]+\|)\r?\n(\|\s*[^\n]+\|)\r?\n(\|\s*[^\n]+\|)\r?\n(\|\s*[^\n]+\|)\r?\n(\|\s*[^\n]+\|)\r?\n(\|\s*[^\n]+\|)(?:\r?\n)?(?=(?:يمكنك|وفقا|إحصاءات|\[))/g;
    processed = processed.replace(
      hospitalBedsTableWithTrailing,
      (_m, header, separator, r1, r2, r3, r4, r5, r6, r7) =>
        `${header}\n${separator}\n${r1}\n${r2}\n${r3}\n${r4}\n${r5}\n${r6}\n${r7}\n\n`
    );

    // 10) أي جدول: إذا كان آخر صف يليه مباشرة "ملاحظة:" أو "وفقا للجهاز المركزي..." فالمحتوى من هنا ليس جزءاً من الجدول ويجب أن يظهر أسفله.
    //     (مثلاً جدول بحث القوى العاملة - نوع رئيس الأسرة / العدد / النسبة — ثم ملاحظة ثم وفقا للجهاز ثم الرابط والغلاف.)
    //     نُدخل سطراً فارغاً بعد آخر صف قبل "ملاحظة" أو "وفقا" لإنهاء الجدول.
    processed = processed.replace(
      /(\|\s*[^\n]+\|)(?:\r?\n)?(?=(?:وفقا\s*للجهاز|ملاحظة\s*:))/g,
      "$1\n\n"
    );

    // Remove --- when it appears on the same line as a title (e.g. "---معدل البطالة في مصر (2024):") so only the title shows
    processed = processed.replace(/^---\s*([^\r\n]+)/m, "$1");
    processed = processed.replace(/(\r?\n)---\s*([^\r\n]+)/g, "$1$2");
    // Preserve standalone --- lines so they render as horizontal rule (divider) per Markdown spec

    // After --- stripping (or whenever the API packs text without blank lines), a line
    // that STARTS with ** (bold heading) needs a blank line before it so the markdown
    // parser renders it as bold rather than treating ** as literal characters.
    // Covers: **heading:** **heading.** **heading** (any trailing punctuation or none).
    processed = processed.replace(/([^\n])\n(\*\*)/g, "$1\n\n$2");

    // When a heading and a bullet list item end up on the same line (common when the LLM
    // emits "---### heading- item" with no line break), split them into separate blocks.
    // e.g. "### 2. عدد الأسرة في المستشفيات (2021)- إجمالي..."
    //   → "### 2. عدد الأسرة في المستشفيات (2021)\n\n- إجمالي..."
    // The [^\s-] guard before "- " avoids false positives inside Arabic words.
    processed = processed.replace(/(^#{1,6} [^\n]+[^\s-])(- )/gm, "$1\n\n$2");

    // Normalize bold markers: trim any leading/trailing whitespace inside **...**.
    // Handles both LLM patterns ("** text **") and processSSEContent's colon-normalization
    // artefact ("**text: **" — trailing space before closing ** breaks CommonMark bold).
    processed = processed.replace(
      /\*\*[ \t]*([^*\n]+?)[ \t]*\*\*/g,
      (_, inner) => {
        const t = inner.trim();
        return t ? `**${t}**` : "****";
      }
    );

    // A bold heading that is glued to the end of the preceding line should start
    // its own paragraph.  Only split when the bold is at the end of its line so
    // normal inline bold (e.g. "this is **important** text") is unaffected.
    // e.g. "(2021)**ملاحظات:**" → "(2021)\n\n**ملاحظات:**"
    processed = processed.replace(
      /([^\n*\s])\s*(\*\*[^*\n]+\*\*\s*)$/gm,
      "$1\n\n$2"
    );

    // Ensure source/reference lines start on a new paragraph
    // e.g. "...data.\nالمصدر: ..." → "...data.\n\nالمصدر: ..."
    processed = processed.replace(
      /([^\n])\n((?:المصدر|Source|المرجع|Reference)\s*:)/g,
      "$1\n\n$2"
    );
    // Ensure text immediately after a table row (no blank line) is separated
    // e.g. "| cell |\nSome text" → "| cell |\n\nSome text" so table ends and cover/link appear below
    processed = processed.replace(/\|\s*\r?\n([^|\n\r-])/g, "|\n\n$1");
    // Ensure line containing [COVER] or publication link [text](url) after a table row has blank line above
    // so the cover always appears below the table, not as part of table content
    processed = processed.replace(
      /\|\s*\r?\n(\s*\[[^\]]*\]\s*\([^)]+\))/g,
      "|\n\n$1"
    );
    // Ensure numbered list items are not merged into the previous line
    // e.g. "text\n1. item" → "text\n\n1. item"
    processed = processed.replace(/([^\n])\n(\d+\.\s)/g, "$1\n\n$2");

    // [COVER] token (v1): normalize streamed variants ([C OVER ], [C OVER]) then convert to image/link
    // Stream may send "[C", "OVER", "]" so we get "[COVER]" or "[C OVER ]" — normalize first
    processed = processed.replace(/\[\s*C\s*OVER\s*\]/gi, "[COVER]");
    // If path is broken, not a URL, or garbage — do not display cover at all.
    const isValidCoverPath = (path) => {
      const s = String(path).trim().replace(/\s+/g, "");
      if (!s || s.length < 5) return false;
      if (/^COVER$/i.test(s) || /^[a-zA-Z]+$/.test(s)) return false; // stream fragments
      // Only allow when it clearly looks like a URL or image path
      if (s.startsWith("http://") || s.startsWith("https://")) return true;
      if (
        s.startsWith("/") &&
        (s.includes("Publication") || s.includes("Image") || s.length > 3)
      )
        return true;
      if (/\/Publication\/Image\//i.test(s)) return true;
      if (/[\w.-]+\.(png|jpe?g|gif|webp)(\?|$)/i.test(s)) return true; // has image extension
      if (/^[a-f0-9-]{20,}(\.(png|jpe?g|gif|webp))?$/i.test(s)) return true; // UUID or UUID.png
      if (s.length >= 32 && /[a-f0-9-]{8,}/i.test(s)) return true; // long path with uuid-like segment
      return false; // anything else: don't show cover
    };
    // Remove incomplete [COVER]( with no closing ) so we never render a broken image
    processed = processed.replace(/\[COVER\]\([^)]*$/g, "");
    // Relative src paths: prefix with base; bare filenames (e.g. UUID.png) get /Publication/Image/ so image loads in table cells.
    const COVER_IMAGE_BASE_URL = "https://capmas.gov.eg:8080";
    const resolveCoverSrc = (src) => {
      const s = String(src).trim().replace(/\s+/g, "");
      if (!s) return COVER_IMAGE_BASE_URL;
      if (/^https?:\/\/\//.test(s)) {
        return `${COVER_IMAGE_BASE_URL}/${s.replace(/^https?:\/\/\/+/, "")}`;
      }
      if (s.startsWith("http://") || s.startsWith("https://")) return s;
      if (s.startsWith("/")) {
        return s.includes("Publication") || s.includes("Image")
          ? `${COVER_IMAGE_BASE_URL}${s}`
          : `${COVER_IMAGE_BASE_URL}/Publication/Image${s}`;
      }
      return `${COVER_IMAGE_BASE_URL}/Publication/Image/${s.replace(
        /^\//,
        ""
      )}`;
    };
    // Use single quotes in generated cover/link HTML so it doesn't break when inside markdown table cells (| cell |)
    const coverLinkAttrs = (
      href,
      src,
      style = "max-width:150px;cursor:pointer;border-radius:4px;display:block;margin:8px 0"
    ) =>
      `href='${href.replace(
        /'/g,
        "&#39;"
      )}' target='_blank' rel='noopener noreferrer'`;
    // On load error (404, bad path, CORS): hide img and wrapper so nothing shows — as if image never existed
    const coverImgTag = (href, imgSrc) =>
      `<a ${coverLinkAttrs(href)}><img src='${imgSrc.replace(
        /'/g,
        "&#39;"
      )}' style='max-width:150px;cursor:pointer;border-radius:4px;display:block;margin:8px 0' onerror='this.style.display=&quot;none&quot;;var p=this.parentElement;if(p)p.style.display=&quot;none&quot;' /></a>`;
    const normalizeHref = (u) => {
      const s = String(u)
        .trim()
        .replace(/\s+/g, "")
        .replace(/:\s*\/\s*\//g, "://");
      return s.startsWith("http") ? s : "https://" + s;
    };
    const isImageUrl = (u) =>
      /\.(png|jpe?g|gif|webp)(\?|$)/i.test(u) ||
      /\/Publication\/Image\//i.test(u);
    const usePubUrlForCover = (pubUrl, imgUrl) => {
      if (!pubUrl || isImageUrl(pubUrl)) return false;
      return true;
    };
    // Paired: [COVER](img_url)[Bulletin Name](source_url) → <a href="source_url"><img src="img_url" /></a>
    processed = processed.replace(
      /\[COVER\]\(([^)]+)\)\s*\[([^\]]*)\]\(([^)]+)\)/gi,
      (m, imgUrl, name, sourceUrl) => {
        if (!isValidCoverPath(imgUrl)) {
          const linkUrl = String(sourceUrl)
            .trim()
            .replace(/\s+/g, "")
            .replace(/:\s*\/\s*\//g, "://");
          const href = linkUrl.startsWith("http")
            ? linkUrl
            : "https://" + linkUrl;
          return `<a ${coverLinkAttrs(href, "")}>${(name || "")
            .trim()
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")}</a>`;
        }
        const imgSrc = resolveCoverSrc(imgUrl);
        const linkUrl = sourceUrl
          .trim()
          .replace(/\s+/g, "")
          .replace(/:\s*\/\s*\//g, "://");
        const href = linkUrl.startsWith("http")
          ? linkUrl
          : "https://" + linkUrl;
        return coverImgTag(href, imgSrc);
      }
    );
    // Publication link then [COVER](img): any URL (including relative); same line or newline/br
    processed = processed.replace(
      /\[([^\]]*)\]\(([^)]+)\)\s*(?:<\s*br\s*\/?\s*>|\r?\n|\s)+\s*\[COVER\]\(([^)]+)\)/gi,
      (m, linkText, pubUrl, imgUrl) => {
        if (!usePubUrlForCover(pubUrl, imgUrl)) return m;
        const href = normalizeHref(pubUrl);
        const title = linkText
          .trim()
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
        if (!isValidCoverPath(imgUrl))
          return `<a ${coverLinkAttrs(href, "")}>${title}</a>`;
        const imgSrc = resolveCoverSrc(imgUrl);
        return `<a ${coverLinkAttrs(href)}>${title}</a>\n\n${coverImgTag(
          href,
          imgSrc
        )}`;
      }
    );
    // Same td: [title](pubUrl) ... [COVER](imgUrl) — any content between, no | (so same cell)
    processed = processed.replace(
      /(\[[^\]]*\]\(([^)]+)\))([^|\[\]]*?)\[COVER\]\(([^)]+)\)/gi,
      (m, linkPart, pubUrl, between, imgUrl) => {
        if (!usePubUrlForCover(pubUrl, imgUrl)) return m;
        if (!isValidCoverPath(imgUrl)) return `${linkPart}${between}`;
        const href = normalizeHref(pubUrl);
        const imgSrc = resolveCoverSrc(imgUrl);
        return `${linkPart}${between}${coverImgTag(href, imgSrc)}`;
      }
    );
    // Same td: [COVER](imgUrl) ... [title](pubUrl)
    processed = processed.replace(
      /\[COVER\]\(([^)]+)\)([^|\[\]]*?)(\[[^\]]*\]\(([^)]+)\))/gi,
      (m, imgUrl, between, linkPart, pubUrl) => {
        if (!usePubUrlForCover(pubUrl, imgUrl)) return m;
        if (!isValidCoverPath(imgUrl)) return `${between}${linkPart}`;
        const href = normalizeHref(pubUrl);
        const imgSrc = resolveCoverSrc(imgUrl);
        return `${coverImgTag(href, imgSrc)}${between}${linkPart}`;
      }
    );
    // Table: [title](pubUrl) | [COVER](imgUrl) — use pubUrl for cover href
    processed = processed.replace(
      /(\[[^\]]*\]\(([^)]+)\))\s*\|\s*\[COVER\]\(([^)]+)\)/gi,
      (m, linkPart, pubUrl, imgUrl) => {
        if (!usePubUrlForCover(pubUrl, imgUrl)) return m;
        if (!isValidCoverPath(imgUrl)) return `${linkPart} | `;
        const href = normalizeHref(pubUrl);
        const imgSrc = resolveCoverSrc(imgUrl);
        return `${linkPart} | ${coverImgTag(href, imgSrc)}`;
      }
    );
    // Table: [COVER](imgUrl) | [title](pubUrl)
    processed = processed.replace(
      /\[COVER\]\(([^)]+)\)\s*\|\s*(\[[^\]]*\]\(([^)]+)\))/gi,
      (m, imgUrl, linkPart, pubUrl) => {
        if (!usePubUrlForCover(pubUrl, imgUrl)) return m;
        if (!isValidCoverPath(imgUrl)) return ` | ${linkPart}`;
        const href = normalizeHref(pubUrl);
        const imgSrc = resolveCoverSrc(imgUrl);
        return `${coverImgTag(href, imgSrc)} | ${linkPart}`;
      }
    );
    // Standalone: [COVER](img_url) → image as link (only when no publication link in context)
    processed = processed.replace(/\[COVER\]\(([^)]+)\)/gi, (m, imgUrl) => {
      if (!isValidCoverPath(imgUrl)) return "";
      const imgSrc = resolveCoverSrc(imgUrl);
      return coverImgTag(imgSrc, imgSrc);
    });

    // Stream can glue links without separators:
    // [GRAPH](a.html)[GRAPH](b.html) / [title](url)[COVER](img)
    // Force separation so markdown parser creates distinct <a> nodes.
    processed = processed
      .replace(/\)\s*(?=\[GRAPH\]\()/gi, ")\n")
      .replace(/\)\s*(?=\[COVER\]\()/gi, ")\n")
      .replace(/\)\s*(?=\[[^\]]+\]\()/g, ") ");

    // Stream may also split URLs inside raw HTML attributes:
    // <a href="https: //www.capmas.gov.eg/..."> -> malformed as https://%20//...
    // Normalize href/src URLs before rendering HTML.
    const normalizeHtmlAttrUrl = (rawUrl) =>
      String(rawUrl || "")
        .trim()
        .replace(/:\s*\/\s*\//g, "://")
        .replace(/\s+/g, "");
    processed = processed.replace(
      /(href|src)\s*=\s*"([^"]*)"/gi,
      (_, attr, url) => `${attr}="${normalizeHtmlAttrUrl(url)}"`
    );
    processed = processed.replace(
      /(href|src)\s*=\s*'([^']*)'/gi,
      (_, attr, url) => `${attr}='${normalizeHtmlAttrUrl(url)}'`
    );

    // Normalize markdown links: [text] (url) or [text](https: //...) → clean [text](url) (HTML conversion at end)
    processed = processed.replace(
      /\[([^\]]+)\]\s*\(\s*([^)]+)\s*\)/g,
      (_match, linkText, url) => {
        const label = String(linkText).trim();
        const cleanUrl = cleanMarkdownLinkUrl(url);
        return `[${label}](${cleanUrl})`;
      }
    );

    // Preserve code blocks (```code```) before processing
    const codeBlockPlaceholder = "___CODE_BLOCK___";
    const codeBlocks = [];
    let codeBlockIndex = 0;

    processed = processed.replace(/```[\s\S]*?```/g, (match) => {
      const placeholder = `${codeBlockPlaceholder}${codeBlockIndex}${codeBlockPlaceholder}`;
      codeBlocks.push({ placeholder, code: match });
      codeBlockIndex++;
      return placeholder;
    });

    // Preserve inline code (`code`) before processing
    const inlineCodePlaceholder = "___INLINE_CODE___";
    const inlineCodes = [];
    let inlineCodeIndex = 0;

    processed = processed.replace(/`([^`\n]+)`/g, (match) => {
      const placeholder = `${inlineCodePlaceholder}${inlineCodeIndex}${inlineCodePlaceholder}`;
      inlineCodes.push({ placeholder, code: match });
      inlineCodeIndex++;
      return placeholder;
    });

    // Normalize bold markers that contain a trailing space before the closing **
    // Example: "**الإجمالي: **7.9%" → "**الإجمالي:** 7.9%"
    processed = processed.replace(/\*\*([^*]+?)\s+\*\*/g, "**$1** ");

    // Fix 1: Ensure space after # markers (##text → ## text)
    processed = processed.replace(/(#{1,6})([^\s#\n])/g, "$1 $2");

    // Fix 2: Extract all headings and their positions
    const headingPlaceholder = "___HEADING___";
    const headings = [];
    let counter = 0;

    // Find and extract all markdown headings
    processed = processed.replace(
      /(#{1,6}\s+)([^\n#]+?)(?=\s*#{1,6}\s+|\s*$)/g,
      (match, marker, content) => {
        const heading = (marker + content).trim();
        headings.push(heading);
        return ` ${headingPlaceholder}${counter++}${headingPlaceholder} `;
      }
    );

    // Fix 3: Restore headings with proper line breaks before and after
    for (let i = 0; i < headings.length; i++) {
      const placeholder = `${headingPlaceholder}${i}${headingPlaceholder}`;
      processed = processed.replace(placeholder, `\n\n${headings[i]}\n\n`);
    }

    // Fix 4: Clean up whitespace (but preserve code blocks)
    processed = processed.replace(/[ \t]+\n/g, "\n"); // Remove trailing spaces
    processed = processed.replace(/\n[ \t]+/g, "\n"); // Remove leading spaces
    processed = processed.replace(/\n{3,}/g, "\n\n"); // Max 2 consecutive newlines

    // Fix 5: Handle edge cases
    // Ensure regular text and headings are properly separated
    processed = processed.replace(/([^\n])\n(#{1,6}\s)/g, "$1\n\n$2");
    processed = processed.replace(/(#{1,6}\s+[^\n]+)\n([^\n#\s])/g, "$1\n\n$2");

    // Clean up start and end
    processed = processed.trim();

    // Remove any remaining multiple spaces (but not inside code blocks)
    processed = processed.replace(/  +/g, " ");

    // Restore inline code
    inlineCodes.forEach(({ placeholder, code }) => {
      processed = processed.replace(placeholder, code);
    });

    // Restore code blocks
    codeBlocks.forEach(({ placeholder, code }) => {
      processed = processed.replace(placeholder, code);
    });

    // Final safety passes (run after all other transformations + code restoration)
    // 1. Any **bold** at the start of a line with only a single \n before it gets a blank line
    processed = processed.replace(/([^\n])\n(\*\*[^\n]+\*\*)/g, "$1\n\n$2");
    // 2. Standalone --- is preserved so it renders as horizontal line (divider)
    // 3. Collapse triple+ newlines
    processed = processed.replace(/\n{3,}/g, "\n\n");

    // 3b. Any leftover "  \n" (e.g. from later steps) → <br />; no invisible placeholder so nothing shows as ">" during stream.
    processed = processed.replace(/  \n/g, "<br />");
    processed = processed
      .split("\u2060\u2060\u2060\u2060\u2060\u2060")
      .join("<br />")
      .split("\u200B\u200B\u200B\u200B\u200B\u200B")
      .join("<br />");

    // 4. Convert **...** to <strong> so bold always renders (rehypeRaw allows HTML; avoids parser ignoring **)
    // Protect <br /> inside ** from being escaped to <br /&gt; so line breaks stay valid.
    processed = processed.replace(/\*\*([\s\S]*?)\*\*/g, (_, inner) => {
      const t = inner.replace(/\s+/g, " ").trim();
      if (!t) return "";
      const withBrProtected = t.replace(/<br\s*\/?>/gi, "__BR__");
      const safe = withBrProtected
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/__BR__/g, "<br />");
      return `<strong>${safe}</strong>`;
    });

    // Prevent ">" during stream: (1) strip blockquote markers at line start; (2) remove stray ">" after <br /> (stream may send ">" in next chunk)
    processed = processed.replace(/^>\s*/gm, "");
    processed = processed.replace(/(<br\s*\/?>\s*)>/gi, "$1");

    // Final pass: normalize age ranges again so "-15 19" never slips through (e.g. inside HTML after other steps)
    processed = normalizeAgeRangesInText(processed);

    // RTL fix only inside table cells: wrap numeric ranges in dir="ltr" so they don't reverse (e.g. "59 50-")
    // Skip cells that contain publication title/link/cover (markdown or already-rendered HTML) so we don't wrap
    // digits inside URLs (e.g. UUID 9f2efd52-826b-4724-876e would get corrupted by span around "52-826")
    const isPublicationLinkOrCover = (text) =>
      /بحث\s*القوى|نشرة\s*إحصاءات|إحصاءات\s*الخدمات|COVER|\[\s*C\s*OVER\s*\]|]\s*\(https?:\/\/|]\s*\(\/|\[COVER\]/i.test(
        text
      ) || /href\s*=|\bsrc\s*=/.test(text);
    processed = processed.replace(
      /<td([^>]*)>([\s\S]*?)<\/td>/gi,
      (_, attrs, content) => {
        if (isPublicationLinkOrCover(content))
          return `<td${attrs}>${content}</td>`;
        const wrapped = content.replace(
          /(\d+)\s*-\s*(\d+)/g,
          '<span dir="ltr" style="unicode-bidi: embed;">$1 - $2</span>'
        );
        return `<td${attrs}>${wrapped}</td>`;
      }
    );

    // Publication/source links → HTML <a> so they always render (even if remark misses broken stream syntax)
    processed = ensureRenderableMarkdownLinks(processed);

    return processed;
  };

  // Strip markdown formatting for plain text copy
  const stripMarkdown = (text) => {
    if (!text) return text || "";

    // Ensure text is a string
    if (typeof text !== "string") {
      try {
        text = String(text);
      } catch (error) {
        console.warn(
          "Error converting text to string in stripMarkdown:",
          error
        );
        return "";
      }
    }

    let stripped = text;

    // Remove headings (# ## ### etc.) but keep the text
    stripped = stripped.replace(/^#{1,6}\s+/gm, "");

    // Remove bold (**text** or __text__)
    stripped = stripped.replace(/(\*\*|__)(.*?)\1/g, "$2");

    // Remove italic (*text* or _text_)
    stripped = stripped.replace(/(\*|_)(.*?)\1/g, "$2");

    // Remove strikethrough (~~text~~)
    stripped = stripped.replace(/~~(.*?)~~/g, "$1");

    // Remove inline code (`code`)
    stripped = stripped.replace(/`([^`]+)`/g, "$1");

    // Remove code blocks (```code```)
    stripped = stripped.replace(/```[\s\S]*?```/g, "");

    // Remove links [text](url) but keep text
    stripped = stripped.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

    // Remove images ![alt](url)
    stripped = stripped.replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1");

    // Remove blockquote markers (>)
    stripped = stripped.replace(/^>\s+/gm, "");

    // Remove horizontal rules (---, ___, ***)
    stripped = stripped.replace(/^[-_*]{3,}$/gm, "");

    // Remove list markers (-, *, +, 1.)
    stripped = stripped.replace(/^[\s]*[-*+]\s+/gm, "• ");
    stripped = stripped.replace(/^[\s]*\d+\.\s+/gm, "");

    // Clean up excessive whitespace
    stripped = stripped.replace(/\n{3,}/g, "\n\n");
    stripped = stripped.trim();

    return stripped;
  };

  // Process graph HTML to ensure it's properly formatted
  const processGraphHTML = (html) => {
    if (!html) return html;

    try {
      let processed = html;

      // Add Cairo font if not present
      if (!processed.includes("fonts.googleapis.com")) {
        const fontLink =
          '<link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap" rel="stylesheet">';
        if (processed.includes("</head>")) {
          processed = processed.replace("</head>", fontLink + "\n</head>");
        }
      }

      // Add body styling for better rendering (body grows with content so parent can read height)
      if (!processed.includes("body {") && processed.includes("</head>")) {
        const style = `<style>
    html, body { margin: 0; width: 100%; box-sizing: border-box; }
    html { min-height: 100%; }
    body {
      padding: 12px 16px 16px 16px;
      font-family: 'Cairo', 'Tajawal', Arial, sans-serif;
      direction: rtl;
      background: #fff;
      min-height: 100%;
      height: auto;
      overflow: visible;
      pointer-events: none;
    }
    body > * { pointer-events: auto; }
    canvas,
    #unemploymentChart, #unemploymentByAge, #migrationChart, canvas[id] {
      width: 100% !important;
      max-width: 100% !important;
      height: 100% !important;
      min-height: 100% !important;
      display: block !important;
      pointer-events: none;
    }
    .chart-container, div[class*="chart"], div[id*="chart"] {
      width: 100% !important;
      height: 100% !important;
      min-height: 100% !important;
      overflow: visible !important;
      position: relative !important;
    }
    .chartjs-render-monitor, canvas + div, .chartjs-size-monitor {
      width: 100% !important;
      height: 100% !important;
      position: relative !important;
    }
    .chartjs-chart, .chartjs-chart canvas {
      width: 100% !important;
      height: 100% !important;
      min-height: 100% !important;
    }
    text[class*="axis"], text[class*="label"], .chartjs-axis-label, .y-axis-label, .x-axis-label {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      font-size: 12px !important;
      fill: #333 !important;
    }
    /* Hide or disable legend rectangles that cause flickering */
    .chartjs-legend,
    .legend,
    [class*="legend"] {
      pointer-events: none !important;
      user-select: none !important;
    }
    /* Prevent interaction with chart elements that cause flickering */
    .chartjs-tooltip,
    .chartjs-tooltip-key,
    rect[class*="legend"],
    rect[fill]:not([data-chart-element]) {
      pointer-events: none !important;
      cursor: default !important;
    }
    /* Specifically target standalone rectangles that match bar colors - hide them */
    rect:not([data-chart-element]):not([class*="chart"]):not([id*="chart"]):not([class*="axis"]):not([class*="grid"]) {
      pointer-events: none !important;
      display: none !important; /* Hide standalone rectangles that cause flickering */
    }
    /* Ensure SVG charts render correctly */
    svg {
      width: 100% !important;
      height: auto !important;
      max-width: 100% !important;
      overflow: visible !important;
    }
    /* Allow only the main chart container to be interactive */
    div[class*="chart-container"],
    div[id*="chart"] {
      pointer-events: auto !important;
    }
  </style>`;
        processed = processed.replace("</head>", style + "\n</head>");
      }

      // Try to make Chart.js responsive if it's set to false
      if (processed.includes("responsive: false")) {
        processed = processed.replace(
          "responsive: false",
          "responsive: true, maintainAspectRatio: false"
        );
      }

      // Ensure responsive is set to true
      if (
        !processed.includes("responsive: true") &&
        processed.includes("new Chart")
      ) {
        processed = processed.replace(/new Chart\(/g, "new Chart(");
        // Add responsive: true if not present
        if (!processed.includes("responsive:")) {
          processed = processed.replace(
            /(options\s*[:=]\s*\{)/g,
            "$1\n        responsive: true,\n        maintainAspectRatio: false,"
          );
        }
      }

      // Remove fixed width/height from canvas if present
      processed = processed.replace(/width="\d+"/g, "");
      processed = processed.replace(/height="\d+"/g, "");

      // Add script to handle resize and ensure y-axis labels are visible
      if (processed.includes("</body>") || processed.includes("</html>")) {
        const resizeScript = `
    <script>
      (function() {
        // Wait for DOM to be ready
        function initChartResize() {
          // Resize all Chart.js instances
          if (window.Chart && window.Chart.instances) {
            var instances = window.Chart.instances;
            var chartList = Array.isArray(instances) ? instances : Object.values(instances);
            chartList.forEach(function(chart) {
              if (chart && typeof chart.resize === 'function') {
                chart.resize();
              }
            });
          }
          
          // Force redraw of SVG charts
          var svgs = document.querySelectorAll('svg');
          svgs.forEach(function(svg) {
            if (svg) {
              var bbox = svg.getBBox();
              if (bbox.width > 0 && bbox.height > 0) {
                svg.setAttribute('width', '100%');
                svg.setAttribute('height', 'auto');
                svg.style.maxWidth = '100%';
                svg.style.height = 'auto';
              }
            }
          });
          
          // Ensure y-axis labels are visible
          var yAxisLabels = document.querySelectorAll('text[class*="axis"], text[class*="label"], .y-axis-label, .chartjs-axis-label');
          yAxisLabels.forEach(function(label) {
            if (label) {
              label.style.display = 'block';
              label.style.visibility = 'visible';
              label.style.opacity = '1';
            }
          });
        }
        
        // Run on load
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initChartResize);
        } else {
          initChartResize();
        }
        
        // Run after a short delay to ensure charts are initialized
        setTimeout(initChartResize, 200);
        setTimeout(initChartResize, 500);
        setTimeout(initChartResize, 1000);
        
        // Listen for resize events
        window.addEventListener('resize', function() {
          setTimeout(initChartResize, 100);
        });
        
        // Use ResizeObserver if available
        if (window.ResizeObserver) {
          var resizeObserver = new ResizeObserver(function(entries) {
            setTimeout(initChartResize, 100);
          });
          
          var chartContainers = document.querySelectorAll('canvas, svg, .chart-container, div[class*="chart"]');
          chartContainers.forEach(function(container) {
            if (container) {
              resizeObserver.observe(container);
            }
          });
        }
      })();
    </script>
    `;

        if (processed.includes("</body>")) {
          processed = processed.replace("</body>", resizeScript + "\n</body>");
        } else if (processed.includes("</html>")) {
          processed = processed.replace("</html>", resizeScript + "\n</html>");
        }
      }

      return processed;
    } catch (error) {
      console.error("Error processing graph HTML:", error);
      return html;
    }
  };

  // Build HTML for Excel code iframe (generate_excel) - same visual block as Excel files
  const processExcelHTML = (code) => {
    if (!code || !String(code).trim()) return "";
    const escaped = String(code)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
    const fontLink =
      '<link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap" rel="stylesheet">';
    return `<!DOCTYPE html><html dir="ltr"><head><meta charset="utf-8">${fontLink}<style>
html, body { margin: 0; width: 100%; box-sizing: border-box; }
body { padding: 12px 16px; font-family: 'Cairo', 'Consolas', monospace; font-size: 13px; line-height: 1.5; background: #fff; color: #333; overflow: visible; white-space: pre-wrap; word-wrap: break-word; min-height: 280px; }
pre, code { margin: 0; font-family: inherit; }
</style></head><body><pre><code>${escaped}</code></pre></body></html>`;
  };

  // Detect language of text (Arabic or English)
  const detectLanguage = (text) => {
    if (!text || text.trim().length === 0) return "ar"; // Default to Arabic

    // Remove whitespace and special characters for detection
    const cleanText = text.trim();

    // Check for Arabic characters (Unicode range: \u0600-\u06FF)
    // This includes Arabic letters, numbers, and punctuation
    const arabicRegex = /[\u0600-\u06FF]/;

    // Count Arabic characters
    const arabicMatches = cleanText.match(arabicRegex);
    const arabicCount = arabicMatches ? arabicMatches.length : 0;

    // Check for English characters (a-z, A-Z)
    const englishRegex = /[a-zA-Z]/;
    const englishMatches = cleanText.match(englishRegex);
    const englishCount = englishMatches ? englishMatches.length : 0;

    // If any Arabic characters exist, it's Arabic
    if (arabicCount > 0) {
      return "ar";
    }

    // If no Arabic and has English, it's English
    if (englishCount > 0) {
      return "en";
    }

    // Default to Arabic if no clear language detected
    return "ar";
  };

  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [currentChatId, setCurrentChatId] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const inputRafRef = useRef(null);
  const inputSyncTimeoutRef = useRef(null);
  const pendingInputValueRef = useRef("");
  const MAX_INPUT_LENGTH = 4000; // Maximum characters allowed (similar to ChatGPT/Claude)
  const STREAM_OFFLINE_GRACE_MS = 8000; // Abort stream after this many ms without internet
  const PAGINATION_LIMIT = 5; // Align with API default (pairs per page); pagination uses total_pages from backend
  // const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [isHistoryClosing, setIsHistoryClosing] = useState(false);
  const [messageActions, setMessageActions] = useState({});
  const [apiError, setApiError] = useState(null);
  const [lastFailedMessage, setLastFailedMessage] = useState(null);
  const [streamingProgress, setStreamingProgress] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [supportsHover, setSupportsHover] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isRecordingPaused, setIsRecordingPaused] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [reachedMaxTime, setReachedMaxTime] = useState(false);
  const recordingTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const pausedTimeRef = useRef(0);
  const shouldProcessRecordingRef = useRef(true); // Flag to determine if recording should be processed in onstop
  const MAX_RECORDING_TIME = 25; // Maximum recording time in seconds
  const GRAPH_FIXED_WIDTH = 640;
  const GRAPH_MIN_HEIGHT = 300;
  const GRAPH_MAX_HEIGHT = 2500;
  const [graphHeights, setGraphHeights] = useState({}); // per-message iframe height (dynamic by content)
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [historySearchQuery, setHistorySearchQuery] = useState("");
  const [historySections, setHistorySections] = useState({
    order: [],
    byKey: {},
  });
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: CHAT_HISTORY_PAGE_LIMIT,
    totalConvs: 0,
    totalPages: 0,
    hasMore: false,
  });
  const [isLoadingChatHistory, setIsLoadingChatHistory] = useState(false);
  const [isLoadingMoreChatHistory, setIsLoadingMoreChatHistory] =
    useState(false);
  const chatHistoryLoadMoreLockRef = useRef(false);
  const historyListScrollRef = useRef(null);
  const [expandedHistorySections, setExpandedHistorySections] = useState({});
  const [collapsedEventBlocks, setCollapsedEventBlocks] = useState({}); // Track collapsed state: {messageId_eventType: boolean}
  const [deleteConfirmChat, setDeleteConfirmChat] = useState(null); // { id, title } | null
  const [isDeletingChat, setIsDeletingChat] = useState(false);

  const chatHistory = useMemo(
    () => flattenHistorySections(historySections),
    [historySections]
  );

  const applyChatHistoryResult = useCallback((result, { append } = {}) => {
    if (!result) return;
    const { groups = [], pagination: p } = result;
    setHistorySections((prev) =>
      append
        ? mergeHistorySectionGroups(prev, groups)
        : replaceHistorySectionGroups(groups)
    );
    if (p) {
      setHistoryPagination({
        page: p.page ?? 1,
        limit: p.limit ?? CHAT_HISTORY_PAGE_LIMIT,
        totalConvs: p.totalConvs ?? 0,
        totalPages: p.totalPages ?? 0,
        hasMore: Boolean(p.hasMore),
      });
    }
  }, []);

  const handleHistoryListScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      const nearBottom = remaining < 140;
      if (!nearBottom) return;
      // Avoid treating "fits in viewport" (remaining === 0) as infinite-scroll bottom on first paint
      if (el.scrollHeight <= el.clientHeight + 4 && el.scrollTop < 1) {
        return;
      }
      if (!token || !userId) return;
      if (historySearchQuery.trim()) return;
      if (!historyPagination.hasMore) return;
      if (isLoadingMoreChatHistory || isLoadingChatHistory) return;
      if (chatHistoryLoadMoreLockRef.current) return;

      chatHistoryLoadMoreLockRef.current = true;
      setIsLoadingMoreChatHistory(true);
      const nextPage = (historyPagination.page || 1) + 1;
      const limit = historyPagination.limit || CHAT_HISTORY_PAGE_LIMIT;

      getChatHistory(token, userId, { page: nextPage, limit })
        .then((result) => {
          applyChatHistoryResult(result, { append: true });
        })
        .catch((err) => {
          console.error("Failed to load more chat history:", err);
        })
        .finally(() => {
          chatHistoryLoadMoreLockRef.current = false;
          setIsLoadingMoreChatHistory(false);
        });
    },
    [
      token,
      userId,
      historySearchQuery,
      historyPagination.hasMore,
      historyPagination.page,
      historyPagination.limit,
      isLoadingMoreChatHistory,
      isLoadingChatHistory,
      applyChatHistoryResult,
    ]
  );
  const abortControllerRef = useRef(null);
  const streamOfflineTimeoutRef = useRef(null); // Timeout to abort stream after connection loss
  const streamAbortedDueToOfflineRef = useRef(false); // So we show connection error (not silent) when aborting due to offline
  const streamOfflineListenersRef = useRef({
    onOffline: null,
    onOnline: null,
    cleanup: null,
  }); // Hold handlers for cleanup
  const messagesEndRef = useRef(null);

  const clearInputRaf = useCallback(() => {
    if (inputRafRef.current != null) {
      window.cancelAnimationFrame(inputRafRef.current);
      inputRafRef.current = null;
    }
    if (inputSyncTimeoutRef.current != null) {
      window.clearTimeout(inputSyncTimeoutRef.current);
      inputSyncTimeoutRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearInputRaf();
    },
    [clearInputRaf]
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const hoverMediaQuery = window.matchMedia(
      "(hover: hover) and (pointer: fine)"
    );
    const updateSupportsHover = () => setSupportsHover(hoverMediaQuery.matches);

    updateSupportsHover();

    if (hoverMediaQuery.addEventListener) {
      hoverMediaQuery.addEventListener("change", updateSupportsHover);
      return () =>
        hoverMediaQuery.removeEventListener("change", updateSupportsHover);
    }

    hoverMediaQuery.addListener(updateSupportsHover);
    return () => hoverMediaQuery.removeListener(updateSupportsHover);
  }, []);

  useEffect(() => {
    if (isOpen || showLoginDialog) {
      setShowInfoTooltip(false);
    }
  }, [isOpen, showLoginDialog]);
  const inputFieldRef = useRef(null); // Ref to focus input after response (usability)
  // const fullscreenRef = useRef(null);
  // const streamingRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesLoadMoreSentinelRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(false); // New messages: stick to bottom when true
  const isInitialLoadRef = useRef(true); // Track if this is the initial load
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGINATION_LIMIT,
    totalMessages: 0,
    totalPages: 0,
    hasMore: false,
    /** Highest API page index loaded so far (open = 1; scroll-up loads 2, 3, …). */
    maxLoadedPage: 1,
  });
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const isLoadingMoreRef = useRef(false); // Prevent multiple simultaneous loads
  const paginationTotalPagesRef = useRef(0); // Sync totalPages to avoid requesting page > totalPages (stale state)
  const scrollTimeoutRef = useRef(null); // For debouncing scroll events
  const lastScrollTopRef = useRef(0); // Track last scroll position to detect scroll direction
  /** Avoid auto-fetching page 2 on open when scrollTop is already 0 and sentinel is visible. */
  const hasUserScrolledForPaginationRef = useRef(false);
  const graphHTMLCacheRef = useRef({}); // Cache processGraphHTML results keyed by message.id
  const handleGraphHeightLoad = useCallback((messageId, height) => {
    setGraphHeights((prev) =>
      prev[messageId] === height ? prev : { ...prev, [messageId]: height }
    );
  }, []);
  const skipScrollToBottomRef = useRef(false); // Skip scroll when opening a chat from history
  const prevIsOpenRef = useRef(false); // Track previous isOpen to detect re-open (minimize then open)
  const audioRef = useRef(null); // For playing audio from TTS API
  const mediaSourceRef = useRef(null); // For MediaSource API
  const sourceBufferRef = useRef(null); // For SourceBuffer
  const streamReaderRef = useRef(null); // For stream reader
  const ttsAbortControllerRef = useRef(null); // For aborting TTS
  const currentSpeakingMessageIdRef = useRef(null); // Current TTS message (to avoid showing error for aborted/old request)
  const isUserStoppedRef = useRef(false); // Track if user manually stopped the audio
  const mediaRecorderRef = useRef(null); // For recording audio (STT)
  const audioChunksRef = useRef([]); // For storing audio chunks during recording
  const recordingStreamRef = useRef(null); // For storing the recording stream
  const chatHistoryPanelRef = useRef(null);
  const convIdRef = useRef(null); // Store current conversationId for API calls
  const prevUserIdRef = useRef(undefined); // Track user identity so we only reset on actual user change, not on every re-render (e.g. after connection error)
  const streamingAccumulatedTextRef = useRef(""); // v1: source of truth for current event text so onEventChange clear is not overwritten by onChunk (state is async)
  const currentEventNameRef = useRef(null); // v1: track current event name to accumulate streams of same type

  // Reset conversation state only when user actually changes (logout or login as different user)
  // Compare by value (not reference) so context re-renders don't wipe the chat
  useEffect(() => {
    const userIdKey =
      userId != null && typeof userId === "object"
        ? JSON.stringify(userId)
        : String(userId ?? "");
    const hadUser =
      prevUserIdRef.current !== undefined && prevUserIdRef.current !== null;
    const hasUser = Boolean(token && userId);
    const userIdChanged =
      hadUser && hasUser && prevUserIdRef.current !== userIdKey;

    if (!token || !userId) {
      prevUserIdRef.current = null;
      setCurrentChatId(null);
      setHistorySections({ order: [], byKey: {} });
      setHistoryPagination({
        page: 1,
        limit: CHAT_HISTORY_PAGE_LIMIT,
        totalConvs: 0,
        totalPages: 0,
        hasMore: false,
      });
      setExpandedHistorySections({});
      syncInputValue("");
      convIdRef.current = null;
      setApiError(null);
      setLastFailedMessage(null);
      setMessageActions({});
      setStreamingMessageId(null);
      setIsStreaming(false);
      setInfoMessage(null);
      setShowHistory(false);
      setGraphHeights({});
      setMessages([]);
      return;
    }

    // Only reset chat when switching to a different user (or first login), not on same user / token refresh / re-render
    if (!hadUser || userIdChanged) {
      prevUserIdRef.current = userIdKey;
      setCurrentChatId(null);
      setHistorySections({ order: [], byKey: {} });
      setHistoryPagination({
        page: 1,
        limit: CHAT_HISTORY_PAGE_LIMIT,
        totalConvs: 0,
        totalPages: 0,
        hasMore: false,
      });
      setExpandedHistorySections({});
      syncInputValue("");
      convIdRef.current = null;
      setApiError(null);
      setLastFailedMessage(null);
      setMessageActions({});
      setStreamingMessageId(null);
      setIsStreaming(false);
      setInfoMessage(null);
      setShowHistory(false);
      setGraphHeights({});

      const welcomeMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: intl.formatMessage({ id: "chatbot.welcomeMessage" }),
        suggestions: [
          intl.formatMessage({ id: "chatbot.suggestion1" }),
          intl.formatMessage({ id: "chatbot.suggestion2" }),
          intl.formatMessage({ id: "chatbot.suggestion3" }),
        ],
        isUser: false,
        timestamp: new Date(),
        textDirection: dir,
      };
      setMessages([welcomeMessage]);
    } else {
      prevUserIdRef.current = userIdKey;
    }
  }, [token, userId]);

  // Load chat history when chatbot is opened and when token/userId changes
  useEffect(() => {
    // Don't load history if chatbot is closed
    if (!isOpen) {
      return;
    }

    // Only load if we have token and userId (user is authenticated)
    if (!token || !userId) {
      setHistorySections({ order: [], byKey: {} });
      setHistoryPagination({
        page: 1,
        limit: CHAT_HISTORY_PAGE_LIMIT,
        totalConvs: 0,
        totalPages: 0,
        hasMore: false,
      });
      return;
    }

    const loadHistory = async () => {
      chatHistoryLoadMoreLockRef.current = false;
      setIsLoadingChatHistory(true);
      try {
        const result = await getChatHistory(token, userId, {
          page: 1,
          limit: CHAT_HISTORY_PAGE_LIMIT,
        });
        applyChatHistoryResult(result, { append: false });
      } catch (error) {
        console.error("Failed to load chat history:", error);
        applyChatHistoryResult(
          {
            groups: [],
            pagination: {
              page: 1,
              limit: CHAT_HISTORY_PAGE_LIMIT,
              totalConvs: 0,
              totalPages: 0,
              hasMore: false,
            },
          },
          { append: false }
        );
      } finally {
        setIsLoadingChatHistory(false);
      }
    };

    loadHistory();
  }, [isOpen, token, userId, applyChatHistoryResult]); // Run when isOpen, token, or userId changes

  // Stop TTS (reading) when chatbot is closed so audio doesn't keep playing
  useEffect(() => {
    if (isOpen) return;

    isUserStoppedRef.current = true;
    if (ttsAbortControllerRef.current) {
      try {
        ttsAbortControllerRef.current.abort();
      } catch (e) {}
      ttsAbortControllerRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        const src = audioRef.current.src;
        audioRef.current.src = "";
        if (src && src.startsWith("blob:")) URL.revokeObjectURL(src);
      } catch (e) {}
      audioRef.current = null;
    }
    setIsSpeaking(false);
    setSpeakingMessageId(null);
    setMessageActions((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((msgId) => {
        if (next[msgId]?.speak) next[msgId] = { ...next[msgId], speak: false };
      });
      return next;
    });
    setTimeout(() => {
      isUserStoppedRef.current = false;
    }, 1000);
  }, [isOpen]);

  // Reset fullscreen when chat closes so the launcher icon is shown again
  useEffect(() => {
    if (!isOpen) {
      setIsFullscreen(false);
    }
  }, [isOpen]);

  // Re-render charts when fullscreen mode changes
  useEffect(() => {
    // Force re-render of all graph iframes when fullscreen state changes
    // This ensures charts are properly rendered in both minimized and maximized views
    const timer = setTimeout(() => {
      // Trigger a resize event to force chart re-rendering
      window.dispatchEvent(new Event("resize"));

      // Also try to resize charts in iframes
      const iframes = document.querySelectorAll(
        'iframe[title="Chart Visualization"]'
      );
      iframes.forEach((iframe) => {
        try {
          if (iframe.contentWindow) {
            // Trigger resize in iframe
            iframe.contentWindow.dispatchEvent(new Event("resize"));

            // Try to resize Chart.js instances
            if (
              iframe.contentWindow.Chart &&
              iframe.contentWindow.Chart.instances
            ) {
              const instances = iframe.contentWindow.Chart.instances;
              const chartList = Array.isArray(instances)
                ? instances
                : Object.values(instances);
              chartList.forEach((chart) => {
                if (chart && typeof chart.resize === "function") {
                  chart.resize();
                }
              });
            }
          }
        } catch (err) {
          // Ignore cross-origin errors
        }
      });
    }, 300); // Delay to ensure DOM is updated

    return () => clearTimeout(timer);
  }, [isFullscreen]); // Run when fullscreen state changes

  const scrollToBottom = () => {
    if (autoScroll && !isUserScrolling) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  /** Scroll the messages container to the latest turn (works even when autoScroll gating is off). */
  const scrollThreadToEnd = useCallback((behavior = "auto") => {
    const root = messagesContainerRef.current;
    if (!root) return;
    const run = () => {
      const targetTop = root.scrollHeight - root.clientHeight;
      root.scrollTop = Math.max(0, targetTop);
      messagesEndRef.current?.scrollIntoView({
        block: "end",
        behavior,
        inline: "nearest",
      });
    };
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }, []);

  /** After opening a conversation, layout (tables, markdown, slides) may settle late — retry scroll. */
  const scheduleScrollThreadToEndAfterConversationOpen = useCallback(() => {
    const delays = [0, 80, 200, 400, 700, 1100];
    delays.forEach((ms) => {
      window.setTimeout(() => scrollThreadToEnd("auto"), ms);
    });
    window.setTimeout(() => {
      skipScrollToBottomRef.current = false;
      isInitialLoadRef.current = false;
    }, 100);
  }, [scrollThreadToEnd]);

  const messageTimestampMs = (m) => {
    const t = m?.timestamp;
    if (t instanceof Date) return t.getTime();
    const n = new Date(t).getTime();
    return Number.isFinite(n) ? n : 0;
  };

  // Next API page when scrolling up (2, 3, … after opening on page 1 only)
  const loadMoreMessages = useCallback(async () => {
    if (
      isLoadingMoreRef.current ||
      isLoadingMoreMessages ||
      !pagination.hasMore ||
      !currentChatId
    ) {
      return;
    }

    const maxLoaded = pagination.maxLoadedPage ?? pagination.page ?? 1;
    const nextPage = maxLoaded + 1;
    const totalPages =
      paginationTotalPagesRef.current ?? pagination.totalPages ?? 0;

    if (totalPages > 0 && nextPage > totalPages) {
      return;
    }

    isLoadingMoreRef.current = true;
    setIsLoadingMoreMessages(true);

    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;
    const prevScrollTop = container?.scrollTop ?? 0;

    try {
      const result = await getConversationMessages(
        currentChatId,
        token,
        userId,
        nextPage,
        PAGINATION_LIMIT
      );

      if (!result || !result.pagination) {
        setPagination((prev) => ({ ...prev, hasMore: false }));
        return;
      }

      const resultPage = result.pagination.page ?? nextPage;
      const resultTotalPages =
        result.pagination.totalPages ?? pagination.totalPages ?? 0;
      const isEmptyMessages =
        !result.messages ||
        !Array.isArray(result.messages) ||
        result.messages.length === 0;

      paginationTotalPagesRef.current = resultTotalPages;

      const isInvalidPage =
        resultTotalPages > 0 && resultPage > resultTotalPages;
      const isQuestionsOnly =
        result.messages &&
        result.messages.length > 0 &&
        result.messages.every((m) => m.isUser === true);

      if (isInvalidPage || isEmptyMessages || isQuestionsOnly) {
        setPagination((prev) => ({
          ...prev,
          page: isInvalidPage ? resultTotalPages : resultPage,
          limit: result.pagination.limit ?? PAGINATION_LIMIT,
          totalMessages: result.pagination.totalMessages ?? prev.totalMessages,
          totalPages: resultTotalPages,
          hasMore: false,
          maxLoadedPage: prev.maxLoadedPage ?? maxLoaded,
        }));
        return;
      }

      const prevMessages = messagesRef.current;
      let needsScrollRestore = false;
      if (result.messages.length > 0) {
        const existingIds = new Set(prevMessages.map((msg) => msg.id));
        const newMessages = result.messages.filter(
          (msg) => !existingIds.has(msg.id)
        );
        if (newMessages.length > 0) {
          const prevMinTs = prevMessages.length
            ? Math.min(...prevMessages.map(messageTimestampMs))
            : Infinity;
          const newMinTs = Math.min(...newMessages.map(messageTimestampMs));
          needsScrollRestore =
            Number.isFinite(prevMinTs) && newMinTs < prevMinTs;
          const merged = [...prevMessages, ...newMessages];
          merged.sort((a, b) => {
            const d = messageTimestampMs(a) - messageTimestampMs(b);
            if (d !== 0) return d;
            if (a.isUser !== b.isUser) return a.isUser ? -1 : 1;
            return String(a.id).localeCompare(String(b.id));
          });
          setMessages(merged);
        }
      }

      setPagination({
        page: resultPage,
        limit: result.pagination.limit ?? PAGINATION_LIMIT,
        totalMessages:
          result.pagination.totalMessages ?? pagination.totalMessages,
        totalPages: resultTotalPages,
        maxLoadedPage: resultPage,
        hasMore: resultTotalPages > 0 && resultPage < resultTotalPages,
      });

      if (needsScrollRestore) {
        requestAnimationFrame(() => {
          const c = messagesContainerRef.current;
          if (!c) return;
          const h = c.scrollHeight;
          c.scrollTop = h - prevScrollHeight + prevScrollTop;
        });
      }
    } catch (error) {
      console.error("Failed to load more messages:", error);
      toast.error(
        intl.formatMessage({
          id: "chatbot.loadMoreError",
          defaultMessage: "فشل تحميل المزيد من الرسائل",
        }),
        {
          position: getToastPosition(),
        }
      );
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMoreMessages(false);
    }
  }, [pagination, currentChatId, token, userId, isLoadingMoreMessages, intl]);

  // Detect user scrolling and toggle auto-scroll, also check for pagination
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isAtBottom = distanceFromBottom < 150; // 150px threshold from bottom
    const isNearTop = scrollTop < 150;

    if (Math.abs(scrollTop - lastScrollTopRef.current) > 2) {
      hasUserScrolledForPaginationRef.current = true;
    }
    lastScrollTopRef.current = scrollTop;

    // Clear any pending scroll timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Debounce scroll event to prevent multiple rapid calls
    scrollTimeoutRef.current = setTimeout(() => {
      if (
        isNearTop &&
        hasUserScrolledForPaginationRef.current &&
        pagination.hasMore &&
        !isLoadingMoreMessages &&
        !isLoadingMoreRef.current &&
        !isStreaming
      ) {
        loadMoreMessages();
      }
    }, 200); // 200ms debounce

    if (isAtBottom) {
      // User scrolled to bottom, re-enable auto-scroll for new messages
      setAutoScroll(true);
      setIsUserScrolling(false);
      // Mark that initial load is done once user scrolls to bottom
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
    } else {
      // User scrolled up, disable auto-scroll
      setAutoScroll(false);
      setIsUserScrolling(true);
    }
  }, [
    pagination.hasMore,
    isLoadingMoreMessages,
    loadMoreMessages,
    isStreaming,
  ]);

  // Infinite scroll: load next page when top sentinel nears viewport (scroll-up / toward older)
  useEffect(() => {
    const root = messagesContainerRef.current;
    const target = messagesLoadMoreSentinelRef.current;
    if (!root || !target || !currentChatId) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (
          !hit ||
          !hasUserScrolledForPaginationRef.current ||
          !pagination.hasMore ||
          isLoadingMoreMessages ||
          isLoadingMoreRef.current ||
          isStreaming
        ) {
          return;
        }
        loadMoreMessages();
      },
      {
        root,
        rootMargin: "200px 0px 0px 0px",
        threshold: 0,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [
    currentChatId,
    pagination.hasMore,
    isLoadingMoreMessages,
    isStreaming,
    loadMoreMessages,
    messages.length,
  ]);

  // Add scroll listener to messages container
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
      // Cleanup timeout on unmount
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  // Auto-scroll when messages change or during streaming (skip when opening a chat from history)
  useEffect(() => {
    if (skipScrollToBottomRef.current) return;
    if (autoScroll) {
      // Add a small delay to ensure the message is rendered
      const timeoutId = setTimeout(() => {
        scrollToBottom();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [messages, autoScroll, isStreaming]); // Trigger on messages change and streaming updates

  // When chat window is re-opened (minimized then opened again), scroll to latest message
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (
      !wasOpen &&
      isOpen &&
      messages.length > 0 &&
      !skipScrollToBottomRef.current
    ) {
      setAutoScroll(true);
      setIsUserScrolling(false);
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen, messages.length]);

  // Update welcome message when language changes
  useEffect(() => {
    // Only update if there's exactly one message and it's from the bot (welcome message)
    if (
      messages.length === 1 &&
      !messages[0].isUser &&
      messages[0].suggestions
    ) {
      // Check if it's a welcome message (has suggestions which are typical for welcome messages)
      const welcomeMessage = {
        id: messages[0].id, // Keep the same ID
        text: intl.formatMessage({ id: "chatbot.welcomeMessage" }),
        suggestions: [
          intl.formatMessage({ id: "chatbot.suggestion1" }),
          intl.formatMessage({ id: "chatbot.suggestion2" }),
          intl.formatMessage({ id: "chatbot.suggestion3" }),
        ],
        isUser: false,
        timestamp: messages[0].timestamp, // Keep the same timestamp
        textDirection: dir, // Use site direction for welcome message
      };
      setMessages([welcomeMessage]);
    }
  }, [locale, dir, intl]); // Update when language or direction changes

  // Generate unique chat ID
  const generateChatId = () => {
    return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Reload chat history from API (first page; replaces accumulated infinite-scroll data)
  const reloadChatHistory = useCallback(async () => {
    if (!token || !userId) {
      return;
    }

    try {
      chatHistoryLoadMoreLockRef.current = false;
      const result = await getChatHistory(token, userId, {
        page: 1,
        limit: CHAT_HISTORY_PAGE_LIMIT,
      });
      applyChatHistoryResult(result, { append: false });
    } catch (error) {
      console.error("Failed to reload chat history:", error);
    }
  }, [token, userId, applyChatHistoryResult]);

  // Save chat to history
  const saveChatToHistory = async (chatId, messages) => {
    // Check if chat already exists in history to avoid duplicates
    const existingChat = chatHistory.find((chat) => chat.id === chatId);
    if (existingChat) {
      return; // Don't save if already exists
    }

    const chatTitle =
      messages.length > 0
        ? messages[0].text.substring(0, 50) +
          (messages[0].text.length > 50 ? "..." : "")
        : intl.formatMessage({ id: "chatbot.newChat" });

    const newChat = {
      id: chatId,
      title: chatTitle,
      messages: [...messages],
      createdAt: new Date(),
      lastMessage: messages[messages.length - 1]?.text || "",
    };

    try {
      // Save chat silently without reloading history
      // The chat will be included in the next getChatHistory call naturally
      await saveChat(newChat, token, userId);

      // Update local state silently without API reload
      setHistorySections((prev) => {
        if (flattenHistorySections(prev).some((chat) => chat.id === chatId)) {
          return prev;
        }
        return upsertChatInHistorySections(prev, newChat, "today");
      });
    } catch (error) {
      console.error("Failed to save chat:", error);
    }
  };

  // Load chat from history
  const loadChatFromHistory = async (chatId) => {
    const chat = chatHistory.find((c) => c.id === chatId);
    if (chat) {
      // إلغاء أي stream جاري حتى لا تظهر إجابة المحادثة السابقة في المحادثة المحمّلة
      if (abortControllerRef.current) {
        try {
          abortControllerRef.current.abort();
        } catch (e) {}
        abortControllerRef.current = null;
      }
      setStreamingMessageId(null);
      setIsStreaming(false);
      setStreamingProgress(0);
      setInfoMessage(null);

      skipScrollToBottomRef.current = true;
      setCurrentChatId(chatId);
      // Update convIdRef with the best available conversation ID.
      // Prefer explicit conversationId, fall back to chat.id (which is already
      // set to the backend conv_id or _id when the history was fetched).
      convIdRef.current = chat.conversationId || chat.id || null;
      // Reset pagination when loading a new chat
      paginationTotalPagesRef.current = 0;
      setPagination({
        page: 1,
        limit: PAGINATION_LIMIT,
        totalMessages: 0,
        totalPages: 0,
        hasMore: false,
        maxLoadedPage: 1,
      });
      isLoadingMoreRef.current = false;
      hasUserScrolledForPaginationRef.current = false;
      // Reset initial load flag and disable auto-scroll for new chat
      isInitialLoadRef.current = true;
      setAutoScroll(true);
      setIsUserScrolling(false);

      // Show loading state
      setMessages([
        {
          id: `loading_${Date.now()}`,
          text: intl.formatMessage({ id: "chatbot.loading" }) || "Loading...",
          isUser: false,
          timestamp: new Date(),
          isLoading: true,
        },
      ]);

      try {
        const conversationId = chatId;
        const first = await getConversationMessages(
          conversationId,
          token,
          userId,
          1,
          PAGINATION_LIMIT
        );

        if (!first || !first.pagination) {
          throw new Error("Invalid conversation response");
        }

        const totalPages = first.pagination.totalPages ?? 0;
        paginationTotalPagesRef.current = totalPages;

        if (first.messages && first.messages.length > 0) {
          setMessages(first.messages);
          const loadedPage = first.pagination.page ?? 1;
          setPagination({
            page: loadedPage,
            limit: first.pagination.limit ?? PAGINATION_LIMIT,
            totalMessages: first.pagination.totalMessages ?? 0,
            totalPages,
            maxLoadedPage: loadedPage,
            hasMore: totalPages > 1 && loadedPage < totalPages,
          });

          requestAnimationFrame(() => {
            scheduleScrollThreadToEndAfterConversationOpen();
          });
        } else {
          paginationTotalPagesRef.current = 0;
          setPagination({
            page: 1,
            limit: PAGINATION_LIMIT,
            totalMessages: 0,
            totalPages: 0,
            hasMore: false,
            maxLoadedPage: 1,
          });
          // If no messages from API, check if chat has local messages
          if (chat.messages && chat.messages.length > 0) {
            setMessages(chat.messages);
          } else {
            // Start with welcome message if no messages
            const welcomeMessage = {
              id: `msg_${Date.now()}_${Math.random()
                .toString(36)
                .substr(2, 9)}`,
              text: intl.formatMessage({ id: "chatbot.welcomeMessage" }),
              suggestions: [
                intl.formatMessage({ id: "chatbot.suggestion1" }),
                intl.formatMessage({ id: "chatbot.suggestion2" }),
                intl.formatMessage({ id: "chatbot.suggestion3" }),
              ],
              isUser: false,
              timestamp: new Date(),
              textDirection: dir,
            };
            setMessages([welcomeMessage]);
          }
          requestAnimationFrame(() => {
            scheduleScrollThreadToEndAfterConversationOpen();
          });
        }
      } catch (error) {
        console.error("Failed to load conversation messages:", error);

        paginationTotalPagesRef.current = 0;
        setPagination({
          page: 1,
          limit: PAGINATION_LIMIT,
          totalMessages: 0,
          totalPages: 0,
          hasMore: false,
          maxLoadedPage: 1,
        });
        // Fallback to local messages if API fails
        if (chat.messages && chat.messages.length > 0) {
          setMessages(chat.messages);
        } else {
          // Show error message
          setMessages([
            {
              id: `error_${Date.now()}`,
              text:
                intl.formatMessage({ id: "chatbot.loadError" }) ||
                "Failed to load conversation. Please try again.",
              isUser: false,
              timestamp: new Date(),
              isError: true,
            },
          ]);
        }
        requestAnimationFrame(() => {
          scheduleScrollThreadToEndAfterConversationOpen();
        });
      }
      // Don't close history panel - keep it open
    }
  };

  // Start new chat
  const startNewChat = () => {
    // إلغاء أي stream جاري حتى لا تظهر إجابة المحادثة السابقة في المحادثة الجديدة
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
      } catch (e) {}
      abortControllerRef.current = null;
    }
    setStreamingMessageId(null);
    setIsStreaming(false);
    setStreamingProgress(0);
    setInfoMessage(null);

    const newChatId = generateChatId();
    setCurrentChatId(newChatId);
    hasUserScrolledForPaginationRef.current = false;
    paginationTotalPagesRef.current = 0;
    setPagination({
      page: 1,
      limit: PAGINATION_LIMIT,
      totalMessages: 0,
      totalPages: 0,
      hasMore: false,
      maxLoadedPage: 1,
    });
    // Reset convIdRef for new chat
    convIdRef.current = null;
    if (showHistory && !isHistoryClosing) {
      setIsHistoryClosing(true);
      setTimeout(() => {
        setShowHistory(false);
        setIsHistoryClosing(false);
      }, 300); // Match animation duration
    }
    setMessageActions({});

    // Add welcome message for new chat
    const welcomeMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: intl.formatMessage({ id: "chatbot.welcomeMessage" }),
      suggestions: [
        intl.formatMessage({ id: "chatbot.suggestion1" }),
        intl.formatMessage({ id: "chatbot.suggestion2" }),
        intl.formatMessage({ id: "chatbot.suggestion3" }),
      ],
      isUser: false,
      timestamp: new Date(),
      textDirection: dir, // Use site direction for welcome message
    };

    setMessages([welcomeMessage]);
  };

  const openDeleteChatConfirm = useCallback(
    (chat, event) => {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      if (!chat?.id || isDeletingChat) return;
      setDeleteConfirmChat({
        id: chat.id,
        title:
          chat.title ||
          intl.formatMessage({ id: "chatbot.newChat" }) ||
          "New Chat",
      });
    },
    [intl, isDeletingChat]
  );

  const closeDeleteChatConfirm = useCallback(() => {
    if (isDeletingChat) return;
    setDeleteConfirmChat(null);
  }, [isDeletingChat]);

  // DELETE api/conversations/{conv_id}
  const confirmDeleteChatFromHistory = useCallback(async () => {
    const chatId = deleteConfirmChat?.id;
    if (!chatId || isDeletingChat) return;

    setIsDeletingChat(true);
    try {
      await deleteChat(chatId, token, userId);
      setHistorySections((prev) => removeChatFromHistorySections(prev, chatId));
      setHistoryPagination((prev) => ({
        ...prev,
        totalConvs: Math.max(0, (prev.totalConvs || 0) - 1),
      }));
      setDeleteConfirmChat(null);
      toast.success(
        intl.formatMessage({
          id: "chatbot.deleteChatSuccess",
          defaultMessage:
            dir === "rtl"
              ? "تم حذف المحادثة بنجاح"
              : "Conversation deleted successfully",
        }),
        {
          autoClose: 2800,
          position: "top-center",
          theme: "colored",
          className: "chatbot-feedback-toast chatbot-feedback-toast--success",
          toastId: "chatbot-delete-success",
        }
      );

      // If the open thread was deleted, reset it but keep the history panel open
      if (currentChatId === chatId) {
        if (abortControllerRef.current) {
          try {
            abortControllerRef.current.abort();
          } catch (e) {}
          abortControllerRef.current = null;
        }
        setStreamingMessageId(null);
        setIsStreaming(false);
        setStreamingProgress(0);
        setInfoMessage(null);
        setMessageActions({});
        convIdRef.current = null;
        hasUserScrolledForPaginationRef.current = false;
        paginationTotalPagesRef.current = 0;
        setPagination({
          page: 1,
          limit: PAGINATION_LIMIT,
          totalMessages: 0,
          totalPages: 0,
          hasMore: false,
          maxLoadedPage: 1,
        });
        const newChatId = generateChatId();
        setCurrentChatId(newChatId);
        setMessages([
          {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            text: intl.formatMessage({ id: "chatbot.welcomeMessage" }),
            suggestions: [
              intl.formatMessage({ id: "chatbot.suggestion1" }),
              intl.formatMessage({ id: "chatbot.suggestion2" }),
              intl.formatMessage({ id: "chatbot.suggestion3" }),
            ],
            isUser: false,
            timestamp: new Date(),
            textDirection: dir,
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to delete chat:", error);
      toast.error(
        intl.formatMessage({
          id: "chatbot.deleteChatError",
          defaultMessage:
            dir === "rtl"
              ? "تعذّر حذف المحادثة. يرجى المحاولة مرة أخرى."
              : "Failed to delete the conversation. Please try again.",
        }),
        {
          autoClose: 4000,
          position: "top-center",
          theme: "colored",
          className: "chatbot-feedback-toast chatbot-feedback-toast--error",
          toastId: "chatbot-delete-error",
        }
      );
    } finally {
      setIsDeletingChat(false);
    }
  }, [
    deleteConfirmChat,
    isDeletingChat,
    token,
    userId,
    intl,
    dir,
    currentChatId,
  ]);

  const displayedHistorySections = useMemo(() => {
    const q = historySearchQuery.trim().toLowerCase();
    if (!q) {
      return (historySections.order || []).map((key) => {
        const sec = historySections.byKey[key];
        return {
          key,
          label: sec?.label || key,
          chats: sec?.chats || [],
        };
      });
    }
    const filtered = chatHistory.filter((chat) =>
      String(chat.title || "")
        .toLowerCase()
        .includes(q)
    );
    if (filtered.length === 0) return [];
    return [
      {
        key: "__search_results",
        label:
          dir === "rtl"
            ? intl.formatMessage({
                id: "chatbot.historySearchResults",
                defaultMessage: "نتائج البحث",
              })
            : intl.formatMessage({
                id: "chatbot.historySearchResults",
                defaultMessage: "Search results",
              }),
        chats: filtered,
      },
    ];
  }, [
    historySections.order,
    historySections.byKey,
    historySearchQuery,
    chatHistory,
    dir,
    intl,
  ]);

  const hasHistorySearchQuery = Boolean(historySearchQuery.trim());
  const hasFilteredHistoryResults = displayedHistorySections.some(
    (s) => (s.chats || []).length > 0
  );
  const showNoHistorySearchResults =
    chatHistory.length > 0 &&
    hasHistorySearchQuery &&
    !hasFilteredHistoryResults;

  // Get user name
  const getUserName = () => {
    if (fullName) return fullName;
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (email) {
      return email.split("@")[0];
    }
    return "User";
  };

  // Get user initials
  const getUserInitials = () => {
    const name = getUserName();
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Toggle chat history bucket expansion (undefined = expanded)
  const toggleHistorySection = (sectionKey) => {
    setExpandedHistorySections((prev) => {
      const isOpen = prev[sectionKey] !== false;
      return { ...prev, [sectionKey]: !isOpen };
    });
  };

  const toggleEventBlock = (messageId, eventType) => {
    const key = `${messageId}_${eventType}`;
    setCollapsedEventBlocks((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Toggle all event blocks for a message (collapse or expand all)
  const toggleAllEventBlocks = (messageId, eventBlocks, collapseAll) => {
    const updates = {};
    eventBlocks.forEach((block) => {
      if (isGraphExcelHeaderOnlyEventBlock(block.eventType)) return;
      const key = `${messageId}_${block.eventType}`;
      updates[key] = collapseAll;
    });
    setCollapsedEventBlocks((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  // Get translated event name for event boxes (any event type from stream)
  const getEventTypeName = useCallback(
    (eventType) => {
      if (!eventType) return "";
      const trimmed = String(eventType).trim();
      // Backend may send either `education_sector` or `education_sector_agent`; translation keys use one form.
      // Try exact SSE name first (e.g. Final_Answer), then lowercase snake_case if needed.
      const variants =
        trimmed !== trimmed.toLowerCase()
          ? [trimmed, trimmed.toLowerCase()]
          : [trimmed];
      const keysToTry = [];
      variants.forEach((segment) => {
        keysToTry.push(`chatbot.event.${segment}`);
        keysToTry.push(
          segment.endsWith("_agent")
            ? `chatbot.event.${segment.replace(/_agent$/i, "")}`
            : `chatbot.event.${segment}_agent`
        );
      });
      for (const translationKey of keysToTry) {
        const value = chatbotLocaleMessages[translationKey];
        if (typeof value === "string" && value.length > 0) {
          return value;
        }
      }
      const translated = intl.formatMessage({
        id: keysToTry[0] || `chatbot.event.${trimmed}`,
        defaultMessage: "__CHATBOT_EVENT_I18N_MISSING__",
      });
      if (translated !== "__CHATBOT_EVENT_I18N_MISSING__") {
        return translated;
      }
      return trimmed.replace(/_/g, " ");
    },
    [chatbotLocaleMessages, intl]
  );

  // Handle streaming response
  const handleStreamingResponse = async (
    userMessage,
    skipUserMessage = false
  ) => {
    // Detect language of the question
    const questionLanguage = detectLanguage(userMessage);
    const textDirection = questionLanguage === "ar" ? "rtl" : "ltr";

    if (!skipUserMessage) {
      const userMessageObj = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: userMessage,
        isUser: true,
        timestamp: new Date(),
        questionLanguage: questionLanguage,
      };

      setMessages((prev) => [...prev, userMessageObj]);
    }

    // Set streaming state immediately (before API call)
    setIsStreaming(true);
    setStreamingProgress(10); // Start with 10% progress to show activity
    setApiError(null);
    setInfoMessage(null);

    // Reset dictionary zone flag for new message
    inDictionaryZone.current = false;

    // Create abort controller for this stream
    abortControllerRef.current = new AbortController();
    streamAbortedDueToOfflineRef.current = false;

    // Close stream after connection loss for a while (avoid infinite open stream)
    const cleanupStreamOfflineListeners = () => {
      const { onOffline, onOnline } = streamOfflineListenersRef.current;
      if (onOffline) window.removeEventListener("offline", onOffline);
      if (onOnline) window.removeEventListener("online", onOnline);
      streamOfflineListenersRef.current = {
        onOffline: null,
        onOnline: null,
        cleanup: null,
      };
      if (streamOfflineTimeoutRef.current) {
        clearTimeout(streamOfflineTimeoutRef.current);
        streamOfflineTimeoutRef.current = null;
      }
    };

    const onOffline = () => {
      if (!abortControllerRef.current) return;
      if (streamOfflineTimeoutRef.current) return; // already counting
      streamOfflineTimeoutRef.current = setTimeout(() => {
        streamOfflineTimeoutRef.current = null;
        streamAbortedDueToOfflineRef.current = true;
        cleanupStreamOfflineListeners();
        try {
          abortControllerRef.current?.abort();
        } catch (e) {}
      }, STREAM_OFFLINE_GRACE_MS);
    };

    const onOnline = () => {
      if (streamOfflineTimeoutRef.current) {
        clearTimeout(streamOfflineTimeoutRef.current);
        streamOfflineTimeoutRef.current = null;
      }
    };

    streamOfflineListenersRef.current = {
      onOffline,
      onOnline,
      cleanup: cleanupStreamOfflineListeners,
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    if (!navigator.onLine) onOffline(); // Already offline: start grace timer immediately

    // Create thinking message ID
    const thinkingMessageId = `thinking_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    // Create initial bot message for streaming
    const botMessageId = `msg_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const initialBotMessage = {
      id: botMessageId,
      text: "",
      details: [],
      suggestions: [],
      isUser: false,
      timestamp: new Date(),
      isStreaming: true,
      textDirection: textDirection,
      eventBlocks: [], // v1: accumulate all event types with their content
    };

    // Set streaming message ID immediately
    setStreamingMessageId(botMessageId);

    // Add thinking message immediately (before API call) to show progress
    const thinkingMessage = {
      id: thinkingMessageId,
      // text: intl.formatMessage({
      //   id: "chatbot.thinking",
      //   defaultMessage: "جاري التفكير...",
      // }),
      text: "",
      isUser: false,
      timestamp: new Date(),
      isThinking: true,
    };

    // Add both messages immediately to show progress indicator
    setMessages((prev) => [...prev, initialBotMessage, thinkingMessage]);

    // v1: ref is source of truth for current event text so when event switches we clear ref and next onChunk appends to ""
    streamingAccumulatedTextRef.current = "";
    currentEventNameRef.current = null;

    // معرف المحادثة لهذا الطلب: أول سؤال conv_id = null؛ بعد أول رد نأخذ القيمة من x-conversation-id ونضعها في convIdRef لاستخدامها في الطلبات التالية
    const currentChat = currentChatId
      ? chatHistory.find((chat) => chat.id === currentChatId)
      : null;
    const convIdFromHistory =
      convIdRef.current ||
      currentChat?.conversationId ||
      currentChat?.id ||
      null;

    try {
      await streamChatResponse(
        userMessage,
        currentChatId || generateChatId(), // Pass chatId instead of token (each conversation gets unique conv_id)
        abortControllerRef.current?.signal, // Pass abort signal
        // onChunk callback — use ref so v1 event switch clear is not overwritten by async state
        (chunk) => {
          const base = streamingAccumulatedTextRef.current;
          const next = normalizeStreamChunk(base, chunk);
          streamingAccumulatedTextRef.current = next;

          setMessages((prev) =>
            prev
              .filter((msg) => msg.id !== thinkingMessageId)
              .map((msg) => {
                if (msg.id === botMessageId) {
                  if (API_VERSION === "v1") {
                    // For v1, show current streaming event in a non-collapsed eventBlock
                    const currentEventName = currentEventNameRef.current;
                    const eventBlocks = msg.eventBlocks || [];

                    if (currentEventName && next.trim()) {
                      // Final_Answer streams directly into the main message bubble (not a box)
                      if (currentEventName === "Final_Answer") {
                        return {
                          ...msg,
                          text: next,
                          isStreaming: true,
                        };
                      }

                      // Update same eventType in-place to avoid duplicate blocks
                      // with the same name while streaming.
                      const existingEventIndex = eventBlocks.findIndex(
                        (block) => block.eventType === currentEventName
                      );

                      let updatedEventBlocks;
                      if (existingEventIndex >= 0) {
                        updatedEventBlocks = eventBlocks
                          .map((block, idx) =>
                            idx === existingEventIndex
                              ? {
                                  ...block,
                                  content: next,
                                  isStreaming: true,
                                }
                              : {
                                  ...block,
                                  isStreaming: false,
                                }
                          )
                          // Defensive dedupe in case older state already has duplicates
                          .filter(
                            (block, idx, arr) =>
                              arr.findIndex(
                                (b) => b.eventType === block.eventType
                              ) === idx
                          );
                      } else {
                        updatedEventBlocks = [
                          ...eventBlocks.map((block) => ({
                            ...block,
                            isStreaming: false,
                          })),
                          {
                            eventType: currentEventName,
                            content: next,
                            isCollapsed: true,
                            isStreaming: true,
                          },
                        ];
                      }

                      return {
                        ...msg,
                        eventBlocks: updatedEventBlocks,
                        text: "",
                        isStreaming: true,
                      };
                    }
                    return {
                      ...msg,
                      eventBlocks: eventBlocks,
                      text: "",
                      isStreaming: true,
                    };
                  } else {
                    // For v2, show text as it streams normally
                    return {
                      ...msg,
                      text: next,
                      isStreaming: true,
                    };
                  }
                }
                return msg;
              })
          );
          setInfoMessage(null);
          setStreamingProgress((prev) => Math.min(prev + 2, 90));
        },
        // onInfo callback - for "thinking" messages
        (info) => {
          setInfoMessage(info);
          // Add or update thinking message
          setMessages((prev) => {
            const hasThinking = prev.some(
              (msg) => msg.id === thinkingMessageId
            );
            if (hasThinking) {
              // Update existing thinking message
              return prev.map((msg) =>
                msg.id === thinkingMessageId ? { ...msg, text: info } : msg
              );
            } else {
              // Add new thinking message before bot message
              const botIndex = prev.findIndex((msg) => msg.id === botMessageId);
              const newMessages = [...prev];
              newMessages.splice(botIndex, 0, {
                id: thinkingMessageId,
                text: info,
                isUser: false,
                timestamp: new Date(),
                isThinking: true,
              });
              return newMessages;
            }
          });
        },
        // onComplete callback
        (apiResponse) => {
          cleanupStreamOfflineListeners();
          // Log graph data for debugging
          if (apiResponse.response.graph) {
            //console.log('Graph received in response:', apiResponse.response.graph.substring(0, 100) + '...');
          }

          // استخراج conv_id من الاستجابة (callRealAPI يضع قيمة هيدر x-conversation-id هنا)
          // أول سؤال يُرسل conv_id: null؛ الـ backend يرجع القيمة في الهيدر ونستخدمها في باقي أسئلة نفس الشات
          const convIdFromAPI =
            apiResponse.response.conv_id ||
            apiResponse.response.metadata?.conv_id;
          const messageIdFromAPI =
            apiResponse.response.messageId ||
            apiResponse.response.metadata?.messageId;

          // Update convIdRef for next API call (so we don't send null in subsequent requests)
          if (convIdFromAPI) {
            convIdRef.current = convIdFromAPI;
            // Align currentChatId to the backend conv_id so chatHistory.find() works correctly
            // after the history reload (which uses backend conv_id as the chat's id)
            setCurrentChatId(convIdFromAPI);
            // Also update the local history entry's id to match the backend id
            setHistorySections((prev) =>
              mapChatsAcrossHistorySections(prev, (c) =>
                c.id === currentChatId
                  ? { ...c, id: convIdFromAPI, conversationId: convIdFromAPI }
                  : c
              )
            );
          }

          // Remove thinking message and update bot message with API data
          // v1: keep streamed text (last event only) from ref; API returns full concatenated text
          const finalText =
            streamingAccumulatedTextRef.current != null &&
            String(streamingAccumulatedTextRef.current).trim() !== ""
              ? streamingAccumulatedTextRef.current
              : apiResponse.response.text;

          setMessages((prev) =>
            prev
              .filter((msg) => msg.id !== thinkingMessageId)
              .map((msg) => {
                if (msg.id === botMessageId) {
                  // Add final event content to eventBlocks if exists
                  let updatedEventBlocks = (msg.eventBlocks || []).map(
                    (block) => ({
                      ...block,
                      isStreaming: false, // Mark all blocks as completed
                    })
                  );
                  const currentEventName = currentEventNameRef.current;
                  const currentContent = streamingAccumulatedTextRef.current;

                  if (currentContent.trim() && currentEventName) {
                    const existingBlockIndex = updatedEventBlocks.findIndex(
                      (block) => block.eventType === currentEventName
                    );

                    if (existingBlockIndex >= 0) {
                      // Update existing block: avoid duplicating when block was just streamed (existing === current)
                      updatedEventBlocks = [...updatedEventBlocks];
                      const existingContent =
                        updatedEventBlocks[existingBlockIndex].content;
                      const isSameAsStreamed =
                        String(existingContent || "").trim() ===
                        String(currentContent || "").trim();
                      updatedEventBlocks[existingBlockIndex] = {
                        ...updatedEventBlocks[existingBlockIndex],
                        content: isSameAsStreamed
                          ? currentContent
                          : existingContent +
                            (existingContent ? "\n\n" : "") +
                            currentContent,
                        isStreaming: false,
                      };
                    } else {
                      // Create new event block
                      updatedEventBlocks = [
                        ...updatedEventBlocks,
                        {
                          eventType: currentEventName,
                          content: currentContent,
                          isCollapsed: true,
                          isStreaming: false,
                        },
                      ];
                    }
                  }

                  // After stream: show only Final_Answer in main bubble; hide event boxes for clean UI
                  let displayText = "";
                  const finalAnswerBlock = updatedEventBlocks.find(
                    (block) => block.eventType === "Final_Answer"
                  );

                  if (finalAnswerBlock) {
                    const apiTextForAnswer = (
                      apiResponse.response.text || ""
                    ).trim();
                    displayText =
                      apiTextForAnswer !== ""
                        ? apiTextForAnswer
                        : finalAnswerBlock.content;
                  } else {
                    displayText = finalText;
                  }

                  // Hide all event boxes after stream ends — only Final_Answer content remains visible
                  const displayEventBlocks = [];

                  // v1: Ensure [EXCEL](...) link is never lost — prefer API response text when it has a complete Excel link so display text is complete
                  const apiText = apiResponse.response.text || "";
                  const apiHasCompleteExcelLink =
                    typeof apiText === "string" && apiText.includes(".xlsx)");
                  const displayHasCompleteExcelLink =
                    typeof displayText === "string" &&
                    displayText.includes(".xlsx)");
                  if (
                    API_VERSION === "v1" &&
                    apiHasCompleteExcelLink &&
                    !displayHasCompleteExcelLink
                  ) {
                    displayText = apiText;
                  }
                  // v1: Excel block must always show when stream contained [EXCEL](...xlsx) — extract from both API text and streamed display text (stream chunks may add spaces, so we try both)
                  let excelUrlsFromApi = null;
                  if (API_VERSION === "v1") {
                    const fromApi =
                      typeof apiText === "string"
                        ? extractExcelUrlsFromText(apiText)
                        : [];
                    const fromDisplay =
                      typeof displayText === "string"
                        ? extractExcelUrlsFromText(displayText)
                        : [];
                    const combined =
                      (fromApi && fromApi.length ? fromApi : null) ||
                      (fromDisplay && fromDisplay.length ? fromDisplay : null);
                    if (combined && combined.length > 0)
                      excelUrlsFromApi = combined;
                  }

                  return {
                    ...msg,
                    text: displayText,
                    eventBlocks: displayEventBlocks,
                    details: apiResponse.response.details || [],
                    suggestions: apiResponse.response.suggestions || [],
                    isStreaming: false,
                    metadata: apiResponse.response.metadata,
                    textDirection: textDirection,
                    graph:
                      (apiResponse.response.graph &&
                        String(apiResponse.response.graph).trim()) ||
                      null, // Add graph data; empty string => do not show
                    excelCode:
                      (apiResponse.response.excelCode &&
                        String(apiResponse.response.excelCode).trim()) ||
                      null, // v1: generate_excel event (Python script); do not show in text
                    excelUrlsFromApi: excelUrlsFromApi ?? undefined, // v1: Excel download URLs from API so block always shows
                    links: apiResponse.response.links || null, // Add links data
                    links_cover: apiResponse.response.links_cover || null, // Add links_cover data
                    messageId: messageIdFromAPI || msg.messageId, // Add messageId from API if available
                    canExport: apiResponse.response.canExport === true, // Show export action only when API says true
                  };
                }
                return msg;
              })
          );
          setStreamingProgress(100);
          setIsStreaming(false);
          setStreamingMessageId(null);
          setInfoMessage(null);
          setTimeout(() => inputFieldRef.current?.focus(), 0);

          // If conv_id is returned from API, save it to chatHistory as conversationId
          if (convIdFromAPI && currentChatId) {
            setHistorySections((prevHistory) => {
              const flat = flattenHistorySections(prevHistory);
              const existingChatIndex = flat.findIndex(
                (chat) => chat.id === currentChatId
              );
              if (existingChatIndex !== -1) {
                return mapChatsAcrossHistorySections(prevHistory, (c) =>
                  c.id === currentChatId
                    ? { ...c, conversationId: convIdFromAPI }
                    : c
                );
              }
              const newChat = {
                id: currentChatId,
                conversationId: convIdFromAPI,
                title:
                  messages.length > 0
                    ? messages[0].text.substring(0, 50) +
                      (messages[0].text.length > 50 ? "..." : "")
                    : intl.formatMessage({ id: "chatbot.newChat" }),
                messages: [...messages],
                createdAt: new Date(),
                lastMessage: messages[messages.length - 1]?.text || "",
              };
              return upsertChatInHistorySections(prevHistory, newChat, "today");
            });
          }

          // Reload chat history from API after successful response
          // Use setTimeout to ensure the API has processed the conversation
          if (token && userId) {
            setTimeout(async () => {
              try {
                chatHistoryLoadMoreLockRef.current = false;
                const result = await getChatHistory(token, userId, {
                  page: 1,
                  limit: CHAT_HISTORY_PAGE_LIMIT,
                });
                applyChatHistoryResult(result, { append: false });
              } catch (error) {
                console.error(
                  "Failed to reload chat history after response:",
                  error
                );
              }
            }, 500); // Small delay to ensure backend has processed the conversation
          }
        },
        // onError callback - remove only the current failed answer, keep all other messages
        (error) => {
          cleanupStreamOfflineListeners();
          console.error("Streaming error:", error);
          setApiError(
            error.message ||
              intl.formatMessage({ id: "chatbot.connectionError" })
          );
          setLastFailedMessage(userMessage); // Save the failed message for retry
          setMessages((prev) =>
            prev.filter(
              (msg) => msg.id !== thinkingMessageId && msg.id !== botMessageId
            )
          );
          setIsStreaming(false);
          setStreamingMessageId(null);
          setInfoMessage(null);
          setTimeout(() => inputFieldRef.current?.focus(), 0);
        },
        token, // Pass token for authorization header
        userId, // Pass userId for userId header
        convIdFromHistory, // Pass conversationId from chatHistory if exists, otherwise null
        // onEventChange (v1 only): single atomic update so every event (e.g. labour_migration_agent) gets its box and main bubble stays clean
        (eventName) => {
          const currentContent = String(
            streamingAccumulatedTextRef.current || ""
          ).trim();
          const prevEventName = currentEventNameRef.current;
          streamingAccumulatedTextRef.current = "";
          currentEventNameRef.current = eventName;

          setMessages((prev) =>
            prev.map((msg) => {
              if (msg.id !== botMessageId) return msg;
              let eventBlocks = [...(msg.eventBlocks || [])];

              if (
                prevEventName === "Final_Answer" &&
                eventName !== "Final_Answer"
              ) {
                eventBlocks = eventBlocks.filter(
                  (b) => b.eventType !== "Final_Answer"
                );
              }

              if (prevEventName) {
                const idx = eventBlocks.findIndex(
                  (b) => b.eventType === prevEventName
                );
                if (idx >= 0) {
                  const existing = eventBlocks[idx].content || "";
                  const incoming = currentContent || "";
                  const shouldAppend =
                    incoming &&
                    !String(existing || "")
                      .trim()
                      .endsWith(incoming);
                  eventBlocks[idx] = {
                    ...eventBlocks[idx],
                    content: shouldAppend
                      ? existing
                        ? `${existing}\n\n${incoming}`
                        : incoming
                      : existing,
                    isStreaming: false,
                  };
                } else if (currentContent) {
                  eventBlocks.push({
                    eventType: prevEventName,
                    content: currentContent,
                    isCollapsed: true,
                    isStreaming: false,
                  });
                }
              }

              if (eventName && eventName !== "Final_Answer") {
                const hasNew = eventBlocks.some(
                  (b) => b.eventType === eventName && b.isStreaming
                );
                if (!hasNew) {
                  eventBlocks = eventBlocks.filter((b) => !b.isStreaming);
                  const existingIdx = eventBlocks.findIndex(
                    (b) => b.eventType === eventName
                  );
                  if (existingIdx >= 0) {
                    eventBlocks[existingIdx] = {
                      ...eventBlocks[existingIdx],
                      isStreaming: true,
                    };
                  } else {
                    eventBlocks.push({
                      eventType: eventName,
                      content: "",
                      isCollapsed: true,
                      isStreaming: true,
                    });
                  }
                }
              }

              // Defensive dedupe by event type (merge duplicate content if needed)
              eventBlocks = eventBlocks.reduce((acc, block) => {
                const existingIdx = acc.findIndex(
                  (b) => b.eventType === block.eventType
                );
                if (existingIdx === -1) {
                  acc.push(block);
                  return acc;
                }
                const existing = acc[existingIdx];
                const mergedContent = [existing.content, block.content]
                  .filter((c) => String(c || "").trim() !== "")
                  .filter(
                    (c, i, arr) =>
                      arr.findIndex(
                        (x) => String(x || "").trim() === String(c || "").trim()
                      ) === i
                  )
                  .join("\n\n");
                acc[existingIdx] = {
                  ...existing,
                  content: mergedContent || existing.content || block.content,
                  isStreaming: existing.isStreaming || block.isStreaming,
                };
                return acc;
              }, []);

              return {
                ...msg,
                eventBlocks,
                text: "",
              };
            })
          );
        }
      );
    } catch (error) {
      cleanupStreamOfflineListeners();
      // User stopped the stream: do not show any error; offline abort: show connection error
      const isAbort =
        error.name === "AbortError" ||
        (error.message &&
          String(error.message).toLowerCase().includes("abort"));
      const abortedDueToOffline = streamAbortedDueToOfflineRef.current;
      if (abortedDueToOffline) streamAbortedDueToOfflineRef.current = false;
      if (isAbort && !abortedDueToOffline) {
        setTimeout(() => inputFieldRef.current?.focus(), 0);
        return;
      }
      if (abortedDueToOffline) {
        setApiError(intl.formatMessage({ id: "chatbot.connectionError" }));
        setLastFailedMessage(userMessage);
        setMessages((prev) =>
          prev.filter(
            (msg) => msg.id !== thinkingMessageId && msg.id !== botMessageId
          )
        );
        setIsStreaming(false);
        setStreamingMessageId(null);
        setInfoMessage(null);
        setTimeout(() => inputFieldRef.current?.focus(), 0);
        return;
      }

      console.error("Error in streaming:", error);
      setApiError(
        error.message || intl.formatMessage({ id: "chatbot.unexpectedError" })
      );
      setLastFailedMessage(userMessage); // Save the failed message for retry
      setMessages((prev) =>
        prev.filter(
          (msg) => msg.id !== thinkingMessageId && msg.id !== botMessageId
        )
      );
      setIsStreaming(false);
      setStreamingMessageId(null);
      setInfoMessage(null);
      setTimeout(() => inputFieldRef.current?.focus(), 0);
    }
  };

  const getLatestInputValue = useCallback(() => {
    // Uncontrolled input: DOM is source of truth (ref/state can lag after clear).
    if (
      inputFieldRef.current &&
      typeof inputFieldRef.current.value === "string"
    ) {
      return inputFieldRef.current.value;
    }
    return pendingInputValueRef.current ?? inputValue ?? "";
  }, [inputValue]);

  const syncInputValue = useCallback(
    (nextValue) => {
      const safeValue = String(nextValue ?? "");
      pendingInputValueRef.current = safeValue;
      clearInputRaf();
      setInputValue(safeValue);
      if (
        inputFieldRef.current &&
        typeof inputFieldRef.current.value === "string" &&
        inputFieldRef.current.value !== safeValue
      ) {
        inputFieldRef.current.value = safeValue;
      }
    },
    [clearInputRaf]
  );

  const handleInputChange = useCallback(
    (e) => {
      let newValue = e.target.value;
      if (newValue.length > MAX_INPUT_LENGTH) {
        newValue = newValue.slice(0, MAX_INPUT_LENGTH);
        if (e.target.value !== newValue) e.target.value = newValue;
      }

      pendingInputValueRef.current = newValue;
      if (inputSyncTimeoutRef.current != null) return;
      // Throttle non-critical UI updates (counter/button state) while typing.
      // The input itself is uncontrolled, so keystrokes remain instant.
      inputSyncTimeoutRef.current = window.setTimeout(() => {
        inputSyncTimeoutRef.current = null;
        setInputValue(pendingInputValueRef.current);
      }, 80);
    },
    [MAX_INPUT_LENGTH]
  );

  const handleSendMessage = async () => {
    const latestInput = getLatestInputValue();
    if (!latestInput.trim() || isStreaming) return;

    // Validate input length
    if (latestInput.length > MAX_INPUT_LENGTH) {
      toast.warning(
        intl.formatMessage(
          {
            id: "chatbot.maxLengthExceeded",
            defaultMessage: "الرسالة طويلة جداً. الحد الأقصى هو {max} حرف",
          },
          { max: MAX_INPUT_LENGTH }
        ),
        {
          position: getToastPosition(),
        }
      );
      return;
    }

    const message = latestInput.trim();
    syncInputValue("");

    // Re-enable auto-scroll when sending a new message
    setAutoScroll(true);
    setIsUserScrolling(false);

    await handleStreamingResponse(message);
  };

  const handleRetry = async () => {
    if (!lastFailedMessage || isStreaming) return;

    // Clear error state
    setApiError(null);

    // Remove only stopped messages (failed answer was already removed, so no isError bubbles)
    setMessages((prev) => prev.filter((msg) => !msg.isStopped));

    // Re-enable auto-scroll when retrying
    setAutoScroll(true);
    setIsUserScrolling(false);

    // Retry without adding the user message again (it's already in the list)
    await handleStreamingResponse(lastFailedMessage, true);

    // Clear the failed message after retry
    setLastFailedMessage(null);
  };

  const handleRegenerate = async (messageId) => {
    if (isStreaming) return;

    // Find the bot message and its preceding user message
    const messageIndex = messages.findIndex((msg) => msg.id === messageId);
    if (messageIndex === -1) return;

    // Find the user message that prompted this bot response
    let userMessage = null;
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i].isUser) {
        userMessage = messages[i].text;
        break;
      }
    }

    if (!userMessage) return;

    // Don't remove any messages - just add a new response as if it's a new question
    // This way, all previous messages (both user and bot) remain intact
    // Re-enable auto-scroll
    setAutoScroll(true);
    setIsUserScrolling(false);

    // Add the user message again and generate a new response
    // This treats it as a completely new question/answer pair
    await handleStreamingResponse(userMessage, false);
  };

  const handleStopStreaming = () => {
    if (!isStreaming) return;

    setApiError(null);

    const currentMessage = messages.find(
      (msg) => msg.isUser && !msg.isStopped
    )?.text;
    if (currentMessage) {
      setLastFailedMessage(currentMessage);
    }

    // إلغاء الطلب الجاري فقط
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
      } catch (e) {
        // Ignore if already aborted
      }
      abortControllerRef.current = null;
    }

    // إيقاف الـ stream فقط: إن وصل جزء من النص نثبته، وإن لم يصل شيء لا نعرض رداً
    // v1: عند الإيقاف نعرض Final_Answer فقط في الفقاعة الرئيسية وباقي الـ events داخل الـ collapses فقط
    const stoppedCurrentEventName = currentEventNameRef.current;
    const stoppedCurrentContent = streamingAccumulatedTextRef.current;

    setMessages((prev) => {
      const withoutThinking = prev.filter((msg) => !msg.isThinking);
      return withoutThinking
        .filter((msg) => {
          if (msg.id !== streamingMessageId || !msg.isStreaming) return true;
          const hasText = msg.text && String(msg.text).trim().length > 0;
          const hasEventBlocks = msg.eventBlocks && msg.eventBlocks.length > 0;
          const hasRefContent =
            stoppedCurrentContent &&
            String(stoppedCurrentContent).trim().length > 0;
          return hasText || hasEventBlocks || hasRefContent;
        })
        .map((msg) => {
          if (msg.id !== streamingMessageId || !msg.isStreaming) return msg;

          if (API_VERSION === "v1") {
            // نفس منطق onComplete: نثبت المحتوى الحالي من الـ ref في الـ blocks ثم نعرض Final_Answer فقط في النص الرئيسي
            let updatedEventBlocks = (msg.eventBlocks || []).map((block) => ({
              ...block,
              isStreaming: false,
            }));

            if (
              stoppedCurrentContent &&
              String(stoppedCurrentContent).trim() &&
              stoppedCurrentEventName
            ) {
              const trimmedContent = String(stoppedCurrentContent).trim();
              const existingIndex = updatedEventBlocks.findIndex(
                (b) => b.eventType === stoppedCurrentEventName
              );
              if (existingIndex >= 0) {
                updatedEventBlocks = [...updatedEventBlocks];

                updatedEventBlocks[existingIndex] = {
                  ...updatedEventBlocks[existingIndex],
                  content: trimmedContent,
                  isStreaming: false,
                };
              } else {
                updatedEventBlocks = [
                  ...updatedEventBlocks,
                  {
                    eventType: stoppedCurrentEventName,
                    content: trimmedContent,
                    isCollapsed: true,
                    isStreaming: false,
                  },
                ];
              }
            }

            const finalAnswerBlock = updatedEventBlocks.find(
              (b) => b.eventType === "Final_Answer"
            );
            let displayText = "";
            let displayEventBlocks = updatedEventBlocks;
            if (finalAnswerBlock) {
              displayText =
                (finalAnswerBlock.content &&
                  String(finalAnswerBlock.content).trim()) ||
                (stoppedCurrentEventName === "Final_Answer" && msg.text
                  ? String(msg.text).trim()
                  : "") ||
                "";
              displayEventBlocks = updatedEventBlocks.filter(
                (b) => b.eventType !== "Final_Answer"
              );
            } else if (
              stoppedCurrentEventName === "Final_Answer" &&
              msg.text &&
              String(msg.text).trim()
            ) {
              // المحتوى في msg.text فقط (الـ ref لم يُحدَّث بعد)
              displayText = String(msg.text).trim();
              displayEventBlocks = updatedEventBlocks;
            } else {
              displayText = "";
            }

            return {
              ...msg,
              text: displayText,
              eventBlocks: displayEventBlocks,
              isStreaming: false,
              isStopped: true,
            };
          }

          return {
            ...msg,
            text: msg.text || "",
            isStreaming: false,
            isStopped: true,
          };
        });
    });

    setIsStreaming(false);
    setStreamingMessageId(null);
    setInfoMessage(null);
    setStreamingProgress(0);
  };

  // const handleSuggestionClick = async (suggestion) => {
  //   if (isStreaming) return;
  //   await handleStreamingResponse(suggestion);
  // };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (isStreaming) {
        return;
      }
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Message actions
  const handleMessageAction = async (messageId, action, event) => {
    // Prevent any default behavior and stop propagation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Set user as scrolling to prevent auto-scroll
    setIsUserScrolling(true);
    setTimeout(() => setIsUserScrolling(false), 2000);

    // Handle like/dislike mutual exclusivity
    if (action === "like" || action === "dislike") {
      const message = messages.find((m) => m.id === messageId);
      if (!message) {
        console.error("Message not found for like/dislike action");
        return;
      }

      const previousState = messageActions[messageId];
      // Likes/dislikes can only be 0 or 1
      const previousLikes = Math.min(1, Math.max(0, message.likes || 0));
      const previousDislikes = Math.min(1, Math.max(0, message.dislikes || 0));

      // Check if user already liked/disliked based on count (1 = already liked/disliked)
      const wasLiked = previousLikes === 1 || previousState?.like || false;
      const wasDisliked =
        previousDislikes === 1 || previousState?.dislike || false;

      // Toggle: if already liked/disliked, next click calls API and on success becomes 0
      let newLikeState = false;
      let newDislikeState = false;
      let newLikes = 0;
      let newDislikes = 0;

      if (action === "like") {
        newLikeState = !wasLiked; // Toggle: 1 -> 0, 0 -> 1
        newLikes = newLikeState ? 1 : 0;
        newDislikeState = false;
        newDislikes = 0;
      } else if (action === "dislike") {
        newDislikeState = !wasDisliked; // Toggle: 1 -> 0, 0 -> 1
        newDislikes = newDislikeState ? 1 : 0;
        newLikeState = false;
        newLikes = 0;
      }

      // Get the actual messageId to send to API (use messageId from API response if available, otherwise use local id)
      const apiMessageId = message.messageId || message.messagedId || messageId; // Support both messageId and messagedId for backward compatibility

      // Submit feedback to API first, then update UI based on response
      // The API endpoint works as a toggle - sending "like" will add like if not exists, remove if exists
      try {
        const result = await submitFeedback(
          apiMessageId,
          action,
          token,
          userId
        );

        if (result.success) {
          // API call succeeded - update UI based on the toggle that happened
          setMessageActions((prev) => ({
            ...prev,
            [messageId]: {
              ...prev[messageId],
              like: newLikeState,
              dislike: newDislikeState,
            },
          }));

          // Update message likes/dislikes count (ensure values are 0 or 1 only)
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    likes: Math.min(1, Math.max(0, newLikes)), // Clamp to 0 or 1
                    dislikes: Math.min(1, Math.max(0, newDislikes)), // Clamp to 0 or 1
                  }
                : msg
            )
          );
        } else {
          // Revert UI state on failure
          setMessageActions((prev) => ({
            ...prev,
            [messageId]: {
              ...prev[messageId],
              like: wasLiked,
              dislike: wasDisliked,
            },
          }));

          // Revert likes/dislikes count (ensure values are 0 or 1 only)
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    likes: Math.min(1, Math.max(0, previousLikes)), // Clamp to 0 or 1
                    dislikes: Math.min(1, Math.max(0, previousDislikes)), // Clamp to 0 or 1
                  }
                : msg
            )
          );

          // Show error message from API response if available
          const errorMessage =
            result.error ||
            intl.formatMessage({ id: "chatbot.feedbackError" }) ||
            "Failed to submit feedback. Please try again.";
          toast.error(errorMessage, {
            position: getToastPosition(),
            autoClose: 5000,
          });
        }
      } catch (error) {
        console.error("Failed to submit feedback:", error);

        // Revert UI state on error
        setMessageActions((prev) => ({
          ...prev,
          [messageId]: {
            ...prev[messageId],
            like: wasLiked,
            dislike: wasDisliked,
          },
        }));

        // Revert likes/dislikes count (ensure values are 0 or 1 only)
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  likes: Math.min(1, Math.max(0, previousLikes)), // Clamp to 0 or 1
                  dislikes: Math.min(1, Math.max(0, previousDislikes)), // Clamp to 0 or 1
                }
              : msg
          )
        );

        // Show error message
        const errorMessage =
          error.message ||
          intl.formatMessage({ id: "chatbot.feedbackError" }) ||
          "Failed to submit feedback. Please try again.";
        toast.error(errorMessage, {
          position: getToastPosition(),
          autoClose: 5000,
        });
      }
      return;
    }

    // Handle export action separately (one-time action, triggers download)
    if (action === "export") {
      const message = messages.find((m) => m.id === messageId);
      if (!message) {
        console.error("Message not found for export action");
        return;
      }

      // Get the actual messageId to send to API (use messageId from API response if available, otherwise use local id)
      const apiMessageId = message.messageId || message.messagedId || messageId;

      try {
        const result = await exportMessage(apiMessageId, token, userId);

        if (result.success) {
          // Show success message
          toast.success(
            intl.formatMessage({ id: "chatbot.exportSuccess" }) ||
              "تم تصدير الرسالة بنجاح",
            {
              position: getToastPosition(),
              autoClose: 3000,
            }
          );
        } else {
          // Show error message from API response if available
          const errorMessage =
            result.error ||
            intl.formatMessage({ id: "chatbot.exportError" }) ||
            "فشل تصدير الرسالة. يرجى المحاولة مرة أخرى.";
          toast.error(errorMessage, {
            position: getToastPosition(),
            autoClose: 5000,
          });
        }
      } catch (error) {
        console.error("Failed to export message:", error);
        const errorMessage =
          error.message ||
          intl.formatMessage({ id: "chatbot.exportError" }) ||
          "فشل تصدير الرسالة. يرجى المحاولة مرة أخرى.";
        toast.error(errorMessage, {
          position: getToastPosition(),
          autoClose: 5000,
        });
      }
      return;
    }

    // Handle copy action separately (one-time action, not a toggle)
    if (action === "copy") {
      const message = messages.find((m) => m.id === messageId);
      if (message) {
        try {
          // Copy plain text without markdown formatting
          const plainText = stripMarkdown(message.text);
          await navigator.clipboard.writeText(plainText);
          // Show success state for 2 seconds
          setCopiedMessageId(messageId);
          setTimeout(() => {
            setCopiedMessageId(null);
          }, 2000);
        } catch (error) {
          console.error("Failed to copy text:", error);
        }
      }
      return;
    }

    // Handle speak action separately (stop/start, not a simple toggle)
    if (action === "speak") {
      // If already speaking the same message, stop it
      if (isSpeaking && speakingMessageId === messageId) {
        // Mark as user-stopped FIRST to prevent error messages
        isUserStoppedRef.current = true;

        // Abort any ongoing TTS request FIRST (before stopping audio)
        if (ttsAbortControllerRef.current) {
          ttsAbortControllerRef.current.abort();
          ttsAbortControllerRef.current = null;
        }
        currentSpeakingMessageIdRef.current = null;

        // Stop the audio
        if (audioRef.current) {
          try {
            audioRef.current.pause();
            audioRef.current.src = "";
            URL.revokeObjectURL(audioRef.current.src);
          } catch (e) {
            // Ignore errors during cleanup
          }
          audioRef.current = null;
        }

        // Update state
        setIsSpeaking(false);
        setSpeakingMessageId(null);
        setMessageActions((prev) => ({
          ...prev,
          [messageId]: {
            ...prev[messageId],
            speak: false,
          },
        }));

        // Reset the flag after a delay to ensure all error handlers check it
        setTimeout(() => {
          isUserStoppedRef.current = false;
        }, 1000);
        return;
      }

      // If speaking a different message, stop it first synchronously
      if (isSpeaking && speakingMessageId && speakingMessageId !== messageId) {
        // Mark as user-stopped FIRST to prevent error messages
        isUserStoppedRef.current = true;

        // Abort any ongoing TTS request FIRST (before stopping audio)
        if (ttsAbortControllerRef.current) {
          ttsAbortControllerRef.current.abort();
          ttsAbortControllerRef.current = null;
        }
        currentSpeakingMessageIdRef.current = null;

        // Stop the audio immediately
        if (audioRef.current) {
          try {
            audioRef.current.pause();
            audioRef.current.src = "";
            URL.revokeObjectURL(audioRef.current.src);
          } catch (e) {
            // Ignore errors during cleanup
          }
          audioRef.current = null;
        }

        // Update state for the previous message
        setIsSpeaking(false);
        setSpeakingMessageId(null);
        setMessageActions((prev) => ({
          ...prev,
          [speakingMessageId]: {
            ...prev[speakingMessageId],
            speak: false,
          },
        }));

        // Wait for cleanup so only one TTS runs at a time
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Reset the flag after delay to ensure all error handlers check it
        setTimeout(() => {
          isUserStoppedRef.current = false;
        }, 1000);
      }

      // Update action state immediately for better UX
      setMessageActions((prev) => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          speak: true,
        },
      }));

      // Then start speaking the new message
      handleSpeakMessageWrapper(messageId);
      return;
    }

    // Handle other actions (toggleable actions)
    setMessageActions((prev) => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        [action]: !prev[messageId]?.[action],
      },
    }));

    // Handle specific actions
    switch (action) {
      case "graph":
        try {
          await exportData(messageId, "graph");
        } catch (error) {
          console.error("Failed to generate graph:", error);
        }
        break;
      case "excel":
        try {
          await exportData(messageId, "excel");
        } catch (error) {
          console.error("Failed to export to Excel:", error);
        }
        break;
      case "pdf":
        try {
          await exportData(messageId, "pdf");
        } catch (error) {
          console.error("Failed to export to PDF:", error);
        }
        break;
      default:
        break;
    }
  };

  // Text-to-Speech function using API from mockApi.js
  const handleSpeakMessageWrapper = useCallback(
    async (messageId) => {
      const message = messages.find((m) => m.id === messageId);
      if (!message || message.isUser) return;

      // Get plain text from message
      let plainText = stripMarkdown(message.text);

      // Remove any remaining graph dictionary patterns (safety check)
      if (plainText.includes("{'graph':") || plainText.includes('{"graph":')) {
        const graphPatterns = [
          /\{'graph':\s*'[^']*'\}/g,
          /\{"graph":\s*"[^"]*"\}/g,
        ];
        graphPatterns.forEach((pattern) => {
          plainText = plainText.replace(pattern, "");
        });
        plainText = plainText.trim();
      }

      // If message has a graph with content, add a note for the user
      if (message.graph && String(message.graph).trim()) {
        const graphNote =
          locale === "ar"
            ? ". يوجد رسم بياني مرفق مع هذه الإجابة"
            : ". There is a graph attached to this response";
        plainText = plainText + graphNote;
      }

      if (!plainText || plainText.trim().length === 0) return;

      // Reset user-stopped flag when starting new audio
      isUserStoppedRef.current = false;
      // Mark this message as the current TTS (so aborted/old request won't show error)
      currentSpeakingMessageIdRef.current = messageId;

      // Call handleSpeakMessage from mockApi.js
      await handleSpeakMessage(plainText, {
        audioRef,
        ttsAbortControllerRef,
        isUserStoppedRef,
        setIsSpeaking,
        setSpeakingMessageId,
        setMessageActions,
        setIsAudioLoading,
        messageId,
        isSpeaking,
        speakingMessageId,
        shouldShowError: (id) => currentSpeakingMessageIdRef.current === id,
        onTtsEnded: () => {
          if (currentSpeakingMessageIdRef.current === messageId)
            currentSpeakingMessageIdRef.current = null;
        },
        onError: (error) => {
          if (isUserStoppedRef.current) return;
          if (currentSpeakingMessageIdRef.current !== messageId) return;
          toast.error(
            intl.formatMessage({
              id: "chatbot.speechError",
              defaultMessage: "حدث خطأ في قراءة النص",
            }),
            { position: getToastPosition() }
          );
        },
      });
    },
    [messages, isSpeaking, speakingMessageId, intl]
  );

  // Helper function to convert audio to WAV format
  const convertToWav = async (audioBlob) => {
    try {
      // Create audio context
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();

      // Read blob as array buffer
      const arrayBuffer = await audioBlob.arrayBuffer();

      // Decode audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // Convert to WAV
      const wav = audioBufferToWav(audioBuffer);

      // Create WAV blob
      return new Blob([wav], { type: "audio/wav" });
    } catch (error) {
      console.error("Error converting to WAV:", error);
      throw error;
    }
  };

  // Helper function to encode AudioBuffer to WAV format
  const audioBufferToWav = (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;

    let result;
    if (numChannels === 2) {
      result = interleave(buffer.getChannelData(0), buffer.getChannelData(1));
    } else {
      result = buffer.getChannelData(0);
    }

    return encodeWAV(result, format, sampleRate, numChannels, bitDepth);
  };

  const interleave = (leftChannel, rightChannel) => {
    const length = leftChannel.length + rightChannel.length;
    const result = new Float32Array(length);

    let inputIndex = 0;
    for (let index = 0; index < length; ) {
      result[index++] = leftChannel[inputIndex];
      result[index++] = rightChannel[inputIndex];
      inputIndex++;
    }
    return result;
  };

  const encodeWAV = (samples, format, sampleRate, numChannels, bitDepth) => {
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;

    const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
    const view = new DataView(buffer);

    /* RIFF identifier */
    writeString(view, 0, "RIFF");
    /* file length */
    view.setUint32(4, 36 + samples.length * bytesPerSample, true);
    /* RIFF type */
    writeString(view, 8, "WAVE");
    /* format chunk identifier */
    writeString(view, 12, "fmt ");
    /* format chunk length */
    view.setUint32(16, 16, true);
    /* sample format (raw) */
    view.setUint16(20, format, true);
    /* channel count */
    view.setUint16(22, numChannels, true);
    /* sample rate */
    view.setUint32(24, sampleRate, true);
    /* byte rate (sample rate * block align) */
    view.setUint32(28, sampleRate * blockAlign, true);
    /* block align (channel count * bytes per sample) */
    view.setUint16(32, blockAlign, true);
    /* bits per sample */
    view.setUint16(34, bitDepth, true);
    /* data chunk identifier */
    writeString(view, 36, "data");
    /* data chunk length */
    view.setUint32(40, samples.length * bytesPerSample, true);

    floatTo16BitPCM(view, 44, samples);

    return buffer;
  };

  const floatTo16BitPCM = (output, offset, input) => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  // Stop recording timer
  const stopRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingTime(0);
    pausedTimeRef.current = 0;
    recordingStartTimeRef.current = null;
  }, []);

  // Pause recording timer
  const pauseRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (recordingStartTimeRef.current) {
      pausedTimeRef.current =
        (Date.now() - recordingStartTimeRef.current) / 1000;
    }
  }, []);

  // Start recording timer
  const startRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }

    recordingStartTimeRef.current = Date.now() - pausedTimeRef.current * 1000;

    recordingTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - recordingStartTimeRef.current) / 1000;
      setRecordingTime(Math.floor(elapsed));

      // Auto-pause at 25 seconds — keep recording paused, user can stop (send) or delete
      if (elapsed >= MAX_RECORDING_TIME) {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === "recording"
        ) {
          mediaRecorderRef.current.pause();
          setIsRecordingPaused(true);
          pauseRecordingTimer();
          setReachedMaxTime(true);
        }
        clearInterval(recordingTimerRef.current);

        toast.warning(
          intl.formatMessage({
            id: "chatbot.maxRecordingTimeReached",
            defaultMessage: "تم الوصول إلى الحد الأقصى للتسجيل (25 ثانية)",
          }),
          {
            position: getToastPosition(),
          }
        );
      }
    }, 100);
  }, [intl, pauseRecordingTimer]);

  // Resume recording timer
  const resumeRecordingTimer = useCallback(() => {
    if (recordingStartTimeRef.current) {
      recordingStartTimeRef.current = Date.now() - pausedTimeRef.current * 1000;
    } else {
      recordingStartTimeRef.current = Date.now();
    }

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }

    recordingTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - recordingStartTimeRef.current) / 1000;
      setRecordingTime(Math.floor(elapsed));

      // Auto-pause at 25 seconds — keep recording paused, user can stop (send) or delete
      if (elapsed >= MAX_RECORDING_TIME) {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === "recording"
        ) {
          mediaRecorderRef.current.pause();
          setIsRecordingPaused(true);
          pauseRecordingTimer();
          setReachedMaxTime(true);
        }
        clearInterval(recordingTimerRef.current);

        toast.warning(
          intl.formatMessage({
            id: "chatbot.maxRecordingTimeReached",
            defaultMessage: "تم الوصول إلى الحد الأقصى للتسجيل (25 ثانية)",
          }),
          {
            position: getToastPosition(),
          }
        );
      }
    }, 100);
  }, [intl]);

  // Stop recording and process it (send to speech-to-text)
  const handleStopRecording = useCallback(async () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      shouldProcessRecordingRef.current = true; // Process the recording
      try {
        mediaRecorderRef.current.requestData(); // flush buffer
      } catch (_) {}
      mediaRecorderRef.current.stop();
    }
  }, []);

  // Delete recording without processing (don't send to speech-to-text)
  const handleDeleteRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      shouldProcessRecordingRef.current = false; // Don't process the recording
      mediaRecorderRef.current.stop();
      // Clear chunks manually since onstop won't process them
      audioChunksRef.current = [];
      // Reset states
      setIsListening(false);
      setIsRecordingPaused(false);
      setRecordingTime(0);
      setReachedMaxTime(false);
      stopRecordingTimer();
      // Stop media tracks
      if (recordingStreamRef.current) {
        recordingStreamRef.current.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
      }
    }
  }, [stopRecordingTimer]);

  // Speech-to-Text function using MediaRecorder and backend API
  const handleMicClick = useCallback(async () => {
    // If already recording, handle pause/resume or stop
    if (isListening && mediaRecorderRef.current) {
      // Don't allow resume if max time reached
      if (isRecordingPaused && reachedMaxTime) {
        return; // Can't resume after reaching max time
      }
      if (isRecordingPaused) {
        // Resume recording
        if (mediaRecorderRef.current.state === "paused") {
          mediaRecorderRef.current.resume();
          setIsRecordingPaused(false);
          resumeRecordingTimer();
        }
      } else {
        // Pause recording
        if (mediaRecorderRef.current.state === "recording") {
          mediaRecorderRef.current.pause();
          setIsRecordingPaused(true);
          pauseRecordingTimer();
        }
      }
      return;
    }

    // If paused, resume (only if not reached max time)
    if (isRecordingPaused && mediaRecorderRef.current && !reachedMaxTime) {
      if (mediaRecorderRef.current.state === "paused") {
        mediaRecorderRef.current.resume();
        setIsRecordingPaused(false);
        resumeRecordingTimer();
        return;
      }
    }

    // Check if browser supports MediaRecorder
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.warning(
        intl.formatMessage({
          id: "chatbot.speechNotSupported",
          defaultMessage: "المتصفح لا يدعم التسجيل الصوتي",
        }),
        {
          position: getToastPosition(),
        }
      );
      return;
    }

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream; // Store stream for cleanup

      // Reset audio chunks
      audioChunksRef.current = [];

      // Determine audio format based on browser support
      let mimeType = "audio/webm";
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        mimeType = "audio/webm;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
        mimeType = "audio/ogg;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/wav")) {
        mimeType = "audio/wav";
      }

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      // Collect audio chunks
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = async () => {
        // Stop timer
        stopRecordingTimer();

        // Stop all tracks
        if (recordingStreamRef.current) {
          recordingStreamRef.current
            .getTracks()
            .forEach((track) => track.stop());
          recordingStreamRef.current = null;
        }

        // Check if we should process the recording (user clicked Stop) or discard it (user clicked Delete)
        if (!shouldProcessRecordingRef.current) {
          // User deleted the recording, just reset states
          audioChunksRef.current = [];
          setIsListening(false);
          setIsRecordingPaused(false);
          setRecordingTime(0);
          setReachedMaxTime(false);
          return;
        }

        // Create blob from chunks
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

        // Clear chunks
        audioChunksRef.current = [];

        // Reset states
        setIsListening(false);
        setIsRecordingPaused(false);
        setRecordingTime(0);
        setReachedMaxTime(false);
        shouldProcessRecordingRef.current = true; // Reset flag for next recording

        // Check if we have audio data
        if (audioBlob.size === 0) {
          toast.warning(
            intl.formatMessage({
              id: "chatbot.noAudioRecorded",
              defaultMessage: "لم يتم تسجيل أي صوت",
            }),
            {
              position: getToastPosition(),
            }
          );
          return;
        }

        // Convert to WAV format before sending to backend
        setIsTranscribing(true);
        try {
          let wavBlob = audioBlob;

          // Only convert if not already WAV
          if (!mimeType.includes("wav")) {
            wavBlob = await convertToWav(audioBlob);
          }

          // Send to backend for transcription
          const language = locale === "ar" ? "ar-EG" : "en-US";
          const result = await speechToText(wavBlob, language);

          if (result.success && result.text) {
            // Add transcribed text to input
            const baseInput = getLatestInputValue();
            const nextInput =
              baseInput + (baseInput ? " " : "") + String(result.text);
            syncInputValue(nextInput);
          } else {
            toast.error(
              result.error ||
                intl.formatMessage({
                  id: "chatbot.speechError",
                  defaultMessage: "حدث خطأ في التعرف على الصوت",
                }),
              {
                position: getToastPosition(),
              }
            );
          }
        } catch (error) {
          console.error("Speech-to-text error:", error);
          toast.error(
            error.message ||
              intl.formatMessage({
                id: "chatbot.speechError",
                defaultMessage: "حدث خطأ في التعرف على الصوت",
              }),
            {
              position: getToastPosition(),
            }
          );
        } finally {
          setIsTranscribing(false);
          setIsListening(false);
          setIsRecordingPaused(false);
          setRecordingTime(0);
          setReachedMaxTime(false);
        }
      };

      // Handle errors
      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event.error);
        stopRecordingTimer();
        setIsListening(false);
        setIsRecordingPaused(false);
        setRecordingTime(0);
        stream.getTracks().forEach((track) => track.stop());

        toast.error(
          intl.formatMessage({
            id: "chatbot.recordingError",
            defaultMessage: "حدث خطأ أثناء التسجيل",
          }),
          {
            position: getToastPosition(),
          }
        );
      };

      // Start recording with 1s timeslice so chunks are collected regularly (avoids losing audio when auto-pausing at 25s)
      mediaRecorder.start(1000);
      setIsListening(true);
      setIsRecordingPaused(false);
      setRecordingTime(0);
      setReachedMaxTime(false);
      pausedTimeRef.current = 0;
      shouldProcessRecordingRef.current = true; // Reset flag for new recording
      startRecordingTimer();
    } catch (error) {
      console.error("Microphone access error:", error);
      setIsListening(false);

      toast.error(
        intl.formatMessage({
          id: "chatbot.microphoneAccessError",
          defaultMessage: "تعذر الوصول إلى الميكروفون",
        }),
        {
          position: getToastPosition(),
        }
      );
    }
  }, [
    isListening,
    isRecordingPaused,
    isTranscribing,
    reachedMaxTime,
    locale,
    intl,
    startRecordingTimer,
    stopRecordingTimer,
    pauseRecordingTimer,
    resumeRecordingTimer,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }

      // Stop recording if active
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          // Ignore errors
        }
      }

      // Stop all tracks
      if (mediaRecorderRef.current) {
        try {
          const stream = mediaRecorderRef.current.stream;
          if (stream) {
            stream.getTracks().forEach((track) => track.stop());
          }
        } catch (e) {
          // Ignore errors
        }
      }
      // Stop audio playback
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }

      // Cleanup MediaSource
      if (mediaSourceRef.current) {
        if (mediaSourceRef.current.readyState === "open") {
          try {
            mediaSourceRef.current.endOfStream();
          } catch (e) {
            // Ignore errors
          }
        }
        mediaSourceRef.current = null;
      }

      // Cleanup source buffer
      if (sourceBufferRef.current) {
        sourceBufferRef.current = null;
      }

      // Cancel stream reader
      if (streamReaderRef.current) {
        streamReaderRef.current.cancel().catch(() => {
          // Ignore cancel errors
        });
        streamReaderRef.current = null;
      }

      // Abort TTS request
      if (ttsAbortControllerRef.current) {
        ttsAbortControllerRef.current.abort();
        ttsAbortControllerRef.current = null;
      }

      // Stop audio recording
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }
    };
  }, []);

  // Close chat history when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showHistory &&
        chatHistoryPanelRef.current &&
        !chatHistoryPanelRef.current.contains(event.target)
      ) {
        // Check if click is not on the history toggle button
        const historyButton = event.target.closest(
          ".chat-history-toggle-btn, .MuiTooltip-root"
        );
        if (!historyButton && !isHistoryClosing) {
          setIsHistoryClosing(true);
          setTimeout(() => {
            setShowHistory(false);
            setIsHistoryClosing(false);
          }, 300); // Match animation duration
        }
      }
    };

    if (showHistory) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showHistory]);

  useEffect(() => {
    if (!showHistory && historySearchQuery) {
      setHistorySearchQuery("");
    }
  }, [showHistory, historySearchQuery]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    // Don't save chat when toggling fullscreen - chat is already saved after API responses
  };

  const handleOpen = async () => {
    setShowInfoTooltip(false);

    // Check if user is logged in
    const isAuthenticated =
      token !== "" && token !== undefined && token !== null;

    if (!isAuthenticated) {
      // Show login dialog instead of opening chat
      setShowLoginDialog(true);
      return;
    }

    // User is authenticated, open chat normally
    setIsOpen(true);
    // Chat history will be loaded automatically by useEffect when isOpen becomes true

    if (!currentChatId) {
      setCurrentChatId(generateChatId());
    }
    if (messages.length === 0) {
      // Show welcome only if still empty when timer fires (don't overwrite an ongoing conversation)
      setTimeout(() => {
        setMessages((prev) => {
          if (prev.length > 0) return prev; // Don't clear existing conversation
          const welcomeMessage = {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            text: intl.formatMessage({ id: "chatbot.welcomeMessage" }),
            suggestions: [
              intl.formatMessage({ id: "chatbot.suggestion1" }),
              intl.formatMessage({ id: "chatbot.suggestion2" }),
              intl.formatMessage({ id: "chatbot.suggestion3" }),
            ],
            isUser: false,
            timestamp: new Date(),
            textDirection: dir,
          };
          return [welcomeMessage];
        });
      }, 500);
    }
  };

  const handleLoginClick = () => {
    setShowLoginDialog(false);
    navigate(`${APP_PREFIX_PATH}/login`);
  };

  const handleCreateAccountClick = () => {
    setShowLoginDialog(false);
    navigate(`${APP_PREFIX_PATH}/register`);
  };

  const handleCloseLoginDialog = () => {
    setShowLoginDialog(false);
  };

  // Message Actions Component
  const MessageActions = ({
    messageId,
    isUser,
    isWelcome,
    isStreaming,
    streamingMessageId,
    hasText,
    likes = 0,
    dislikes = 0,
    infoMessage,
    canExport = false,
    isThinking = false,
  }) => {
    const actions = messageActions[messageId] || {};

    // Don't show actions for user messages
    if (isUser) {
      return null;
    }

    // Don't show actions for thinking/info messages (جاري تحليل السؤال، جاري البحث في البيانات)
    if (isThinking) {
      return null;
    }

    // Don't show actions for welcome message only
    // Welcome message is identified by having suggestions array
    if (isWelcome) {
      return null;
    }

    // Don't show actions while status info is shown (جاري تحليل السؤال، جاري البحث في البيانات)
    if (infoMessage) {
      return null;
    }

    // Don't show actions for messages that are currently streaming
    // Check streamingMessageId first (most reliable indicator)
    if (streamingMessageId === messageId) {
      return null;
    }

    // Also check isStreaming prop as fallback
    if (isStreaming === true) {
      return null;
    }

    // Don't show actions if message has no text (loading state)
    if (!hasText) {
      return null;
    }

    // Show actions for all other bot messages (after streaming completes)

    // Handle hover events to prevent scroll
    const handleMouseEnter = useCallback(() => {
      setIsUserScrolling(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setTimeout(() => setIsUserScrolling(false), 500);
    }, []);

    // Memoize the action handlers to prevent unnecessary re-renders
    const handleActionClick = useCallback(
      (action, event) => {
        handleMessageAction(messageId, action, event);
      },
      [messageId]
    );

    return (
      <Box
        className="message-actions"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Tooltip
          title={intl.formatMessage({ id: "chatbot.like" })}
          enterDelay={400}
          leaveDelay={100}
          arrow
          placement="top"
          disableInteractive={false}
          PopperProps={{
            style: { zIndex: 100000 },
          }}
        >
          <span>
            <IconButton
              size="small"
              onClick={(e) => handleActionClick("like", e)}
              className={`action-btn ${likes === 1 ? "active" : ""}`}
              sx={{
                position: "relative",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <ThumbUp fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip
          title={intl.formatMessage({ id: "chatbot.dislike" })}
          enterDelay={400}
          leaveDelay={100}
          arrow
          placement="top"
          disableInteractive={false}
          PopperProps={{
            style: { zIndex: 100000 },
          }}
        >
          <span>
            <IconButton
              size="small"
              onClick={(e) => handleActionClick("dislike", e)}
              className={`action-btn ${dislikes === 1 ? "active" : ""}`}
              sx={{
                position: "relative",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <ThumbDown fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {canExport === true && (
          <Tooltip
            title={intl.formatMessage({ id: "chatbot.export" }) || "تصدير"}
            enterDelay={400}
            leaveDelay={100}
            arrow
            placement="top"
            disableInteractive={false}
            PopperProps={{
              style: { zIndex: 100000 },
            }}
          >
            <span>
              <IconButton
                size="small"
                onClick={(e) => handleActionClick("export", e)}
                className="action-btn"
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <Download fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* <Tooltip title={intl.formatMessage({ id: "chatbot.warning" })}>
          <IconButton
            size="small"
            onClick={(e) => handleActionClick("warning", e)}
            className={`action-btn ${actions.warning ? "active" : ""}`}
          >
            <Warning fontSize="small" />
          </IconButton>
        </Tooltip> */}

        <Tooltip
          title={
            copiedMessageId === messageId
              ? intl.formatMessage({
                  id: "chatbot.copied",
                  defaultMessage: "تم النسخ بنجاح",
                })
              : intl.formatMessage({ id: "chatbot.copy" })
          }
          enterDelay={copiedMessageId === messageId ? 0 : 400}
          leaveDelay={copiedMessageId === messageId ? 2000 : 100}
          arrow
          placement="top"
          disableInteractive={false}
          open={copiedMessageId === messageId ? true : undefined}
          onClose={() => {
            // Prevent closing when manually controlled
            if (copiedMessageId === messageId) {
              return;
            }
          }}
          disableHoverListener={copiedMessageId === messageId}
          disableFocusListener={copiedMessageId === messageId}
          disableTouchListener={copiedMessageId === messageId}
          PopperProps={{
            style: { zIndex: 100000 },
          }}
        >
          <span>
            <IconButton
              size="small"
              onClick={(e) => handleActionClick("copy", e)}
              className={`action-btn ${
                copiedMessageId === messageId ? "copied" : ""
              }`}
              sx={{
                transition: "all 0.3s ease-in-out",
                "&.copied": {
                  color: "#4caf50",
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(76, 175, 80, 0.15)",
                  },
                },
                "&:hover:not(.copied)": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              {copiedMessageId === messageId ? (
                <CheckCircle
                  fontSize="small"
                  sx={{
                    animation: "scaleIn 0.2s ease-in-out",
                    "@keyframes scaleIn": {
                      "0%": {
                        transform: "scale(0)",
                        opacity: 0,
                      },
                      "100%": {
                        transform: "scale(1)",
                        opacity: 1,
                      },
                    },
                  }}
                />
              ) : (
                <ContentCopy
                  fontSize="small"
                  sx={{
                    transition: "transform 0.2s ease-in-out",
                  }}
                />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {/* <Tooltip title={intl.formatMessage({ id: "chatbot.graph" })}>
          <IconButton
            size="small"
            onClick={(e) => handleActionClick("graph", e)}
            className={`action-btn ${actions.graph ? "active" : ""}`}
          >
            <BarChart fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={intl.formatMessage({ id: "chatbot.pdf" })}>
          <IconButton
            size="small"
            onClick={(e) => handleActionClick("pdf", e)}
            className={`action-btn ${actions.pdf ? "active" : ""}`}
          >
            <PictureAsPdf fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={intl.formatMessage({ id: "chatbot.excel" })}>
          <IconButton
            size="small"
            onClick={(e) => handleActionClick("excel", e)}
            className={`action-btn ${actions.excel ? "active" : ""}`}
          >
            <TableChart fontSize="small" />
          </IconButton>
        </Tooltip> */}

        <Tooltip
          title={
            isAudioLoading && speakingMessageId === messageId
              ? intl.formatMessage({
                  id: "chatbot.audioLoading",
                  defaultMessage: "جاري تحميل الصوت...",
                })
              : actions.speak && isSpeaking && speakingMessageId === messageId
              ? intl.formatMessage({
                  id: "chatbot.stopSpeaking",
                  defaultMessage: "إيقاف القراءة",
                })
              : intl.formatMessage({
                  id: "chatbot.speak",
                  defaultMessage: "قراءة الإجابة",
                })
          }
          enterDelay={400}
          leaveDelay={100}
          arrow
          placement="top"
          disableInteractive={false}
          PopperProps={{
            style: { zIndex: 100000 },
          }}
        >
          <span>
            <IconButton
              size="small"
              onClick={(e) => handleActionClick("speak", e)}
              className={`action-btn speak-action ${
                (actions.speak &&
                  isSpeaking &&
                  speakingMessageId === messageId) ||
                (isAudioLoading && speakingMessageId === messageId)
                  ? "active"
                  : ""
              }`}
              sx={{
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                animation:
                  actions.speak &&
                  isSpeaking &&
                  !isAudioLoading &&
                  speakingMessageId === messageId
                    ? "pulse 1.5s ease-in-out infinite"
                    : "none",
                "&.active": {
                  color: "#1976d2",
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  transform: "scale(1.05)",
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.12)",
                    transform: "scale(1.1)",
                  },
                },
                "&:hover:not(.active)": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  transform: "scale(1.05)",
                },
                "@keyframes pulse": {
                  "0%, 100%": {
                    opacity: 1,
                    transform: "scale(1)",
                  },
                  "50%": {
                    opacity: 0.8,
                    transform: "scale(1.05)",
                  },
                },
              }}
            >
              {isAudioLoading && speakingMessageId === messageId ? (
                <CircularProgress
                  size={16}
                  thickness={5}
                  sx={{ color: "#1976d2" }}
                />
              ) : actions.speak &&
                isSpeaking &&
                speakingMessageId === messageId ? (
                <VolumeOff
                  fontSize="small"
                  sx={{
                    animation: "fadeIn 0.2s ease-in-out",
                    "@keyframes fadeIn": {
                      "0%": {
                        opacity: 0,
                        transform: "scale(0.8)",
                      },
                      "100%": {
                        opacity: 1,
                        transform: "scale(1)",
                      },
                    },
                  }}
                />
              ) : (
                <VolumeUp
                  fontSize="small"
                  sx={{
                    transition: "transform 0.2s ease-in-out",
                  }}
                />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      {/* ChatBot Button */}
      {!isFullscreen && (
        <Fade in={true} timeout={500}>
          <Box
            className="chatbot-button-wrapper"
            style={{ alignItems: dir === "rtl" ? "flex-start" : "flex-end" }}
            onMouseEnter={() => {
              if (supportsHover) {
                setShowInfoTooltip(true);
              }
            }}
            onMouseLeave={() => setShowInfoTooltip(false)}
          >
            {/* Info Tooltip */}
            {showInfoTooltip && !isOpen && !showLoginDialog && (
              <Box className="chatbot-info-tooltip">
                <Typography variant="h6" className="tooltip-title">
                  {intl.formatMessage({ id: "chatbot.infoTitle" })}
                </Typography>
                <Typography variant="body2" className="tooltip-description">
                  {intl.formatMessage({ id: "chatbot.infoDescription" })}
                </Typography>
              </Box>
            )}
            <Box
              className="chatbot-button"
              onClick={
                showLoginDialog
                  ? () => {
                      setShowInfoTooltip(false);
                      setShowLoginDialog(false);
                    }
                  : isOpen
                  ? () => {
                      setShowInfoTooltip(false);
                      setIsOpen(false);
                    }
                  : handleOpen
              }
            >
              <Box className="chatbot-icon">
                {isOpen || showLoginDialog ? (
                  <Close
                    sx={{
                      color: "white",
                      fontSize: "28px",
                      width: "28px",
                      height: "28px",
                    }}
                  />
                ) : (
                  <img
                    src="/Group.svg"
                    alt="Chatbot"
                    style={{ width: "35px", height: "20px" }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Fade>
      )}

      {/* ChatBot Dialog */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth={isFullscreen ? "lg" : "md"}
        fullWidth
        fullScreen={isFullscreen}
        className={`chatbot-dialog ${isFullscreen ? "fullscreen" : ""}`}
        PaperProps={{
          className: `chatbot-paper ${isFullscreen ? "fullscreen-paper" : ""}`,
        }}
      >
        <DialogTitle className="chatbot-header" dir={dir}>
          <Box className="chatbot-header-content">
            <Box
              className="chatbot-avatar"
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                backgroundColor: "#ddd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img src="/logo.png" alt="Chatbot" style={{ width: "40px" }} />
            </Box>
            <Box className="chatbot-info">
              <Typography variant="h6" className="chatbot-name">
                {intl.formatMessage({ id: "chatbot.name" })}
              </Typography>
              <Typography variant="caption" className="chatbot-status">
                {intl.formatMessage({ id: "chatbot.connected" })}
              </Typography>
            </Box>
          </Box>

          <Box className="chatbot-header-actions">
            <Tooltip
              title={
                isFullscreen
                  ? intl.formatMessage({ id: "chatbot.minimize" })
                  : intl.formatMessage({ id: "chatbot.maximize" })
              }
            >
              <IconButton
                onClick={toggleFullscreen}
                className="header-action-btn"
                size="small"
                sx={{
                  color: "white",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "chatbot.chatHistory" })}>
              <IconButton
                onClick={() => {
                  if (showHistory && !isHistoryClosing) {
                    setIsHistoryClosing(true);
                    setTimeout(() => {
                      setShowHistory(false);
                      setIsHistoryClosing(false);
                    }, 300); // Match animation duration
                  } else if (!showHistory) {
                    setShowHistory(true);
                  }
                }}
                className="header-action-btn chat-history-toggle-btn"
                size="small"
                sx={{
                  color: "white",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <History />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={() => setIsOpen(false)}
              className="header-action-btn"
              size="small"
              sx={{
                color: "white",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <Close />
            </IconButton>
            <Tooltip title={intl.formatMessage({ id: "chatbot.newChat" })}>
              <IconButton
                onClick={startNewChat}
                className="header-action-btn"
                size="small"
                sx={{
                  color: "white",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent
          className="chatbot-content"
          dir={dir === "rtl" ? "ltr" : "rtl"}
        >
          {showHistory && (
            <Box
              className={`chat-history-panel ${
                isFullscreen ? "fullscreen-mode" : "popup-mode"
              } ${isHistoryClosing ? "closing" : ""}`}
              dir={dir}
              ref={chatHistoryPanelRef}
            >
              {/* Header with Blue Bar */}
              {/* <Box className="history-header-bar" /> */}

              <Box className="history-title">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box className="history-icon">
                    <History />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      flex: 1,
                      fontFamily: "Tajawal, sans-serif",
                      fontSize: "15px",
                      fontWeight: 600,
                    }}
                  >
                    {intl.formatMessage({ id: "chatbot.chatHistory" })}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (!isHistoryClosing) {
                      setIsHistoryClosing(true);
                      setTimeout(() => {
                        setShowHistory(false);
                        setIsHistoryClosing(false);
                      }, 300); // Match animation duration
                    }
                  }}
                  sx={{
                    color: "var(--main-color)",
                    "&:hover": {
                      background: "rgba(9, 134, 237, 0.1)",
                    },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              {/* Search Input */}
              <Box className="history-search">
                <TextField
                  fullWidth
                  placeholder={intl.formatMessage({ id: "search" }) || "بحث"}
                  value={historySearchQuery}
                  onChange={(e) => setHistorySearchQuery(e.target.value)}
                  disabled={chatHistory.length === 0} // Disable if no chats
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor:
                        chatHistory.length === 0 ? "#f5f5f5" : "#f8fafc",
                      "& fieldset": {
                        borderColor: "rgba(9, 134, 237, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(9, 134, 237, 0.3)",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#f5f5f5",
                        cursor: "not-allowed",
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "8px 12px",
                      fontFamily: "Tajawal, sans-serif",
                      fontSize: "14px",
                      "&:disabled": {
                        cursor: "not-allowed",
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        sx={{ padding: "4px" }}
                        disabled={chatHistory.length === 0}
                      >
                        <Search fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
              </Box>

              {/* History List with Sections */}
              <Box
                className="history-list"
                ref={historyListScrollRef}
                onScroll={handleHistoryListScroll}
              >
                {isLoadingChatHistory && chatHistory.length === 0 ? (
                  <Box
                    className="history-list-initial-loading"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "48px 16px",
                    }}
                  >
                    <CircularProgress
                      size={32}
                      sx={{ color: "var(--main-color)" }}
                    />
                  </Box>
                ) : (
                  <>
                    {displayedHistorySections.map((section) =>
                      (section.chats || []).length === 0 ? null : (
                        <Box key={section.key} className="history-section">
                          <Box
                            className="history-section-header"
                            onClick={() => toggleHistorySection(section.key)}
                          >
                            <Typography
                              variant="body2"
                              className="history-section-title"
                              sx={{
                                fontFamily: "Tajawal, sans-serif",
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              {historySectionTitle(section.label, dir)}
                              {expandedHistorySections[section.key] !==
                              false ? (
                                <ExpandLess fontSize="small" />
                              ) : (
                                <ExpandMore fontSize="small" />
                              )}
                            </Typography>
                          </Box>
                          {expandedHistorySections[section.key] !== false && (
                            <Box className="history-section-items">
                              {section.chats.map((chat) => (
                                <Box
                                  key={chat.id}
                                  className={`history-item ${
                                    currentChatId === chat.id ? "active" : ""
                                  } ${
                                    deleteConfirmChat?.id === chat.id
                                      ? "pending-delete"
                                      : ""
                                  }`}
                                >
                                  <Box
                                    className="history-item-content"
                                    onClick={() => loadChatFromHistory(chat.id)}
                                  >
                                    <Typography
                                      variant="body2"
                                      className="history-item-title"
                                      style={{
                                        fontFamily: "Tajawal, sans-serif",
                                      }}
                                    >
                                      {chat.title ||
                                        intl.formatMessage({
                                          id: "chatbot.newChat",
                                        }) ||
                                        "New Chat"}
                                    </Typography>
                                    <Tooltip
                                      title={intl.formatMessage({
                                        id: "chatbot.deleteChat",
                                        defaultMessage:
                                          dir === "rtl"
                                            ? "حذف المحادثة"
                                            : "Delete conversation",
                                      })}
                                      placement="top"
                                      arrow
                                      enterDelay={200}
                                      leaveDelay={0}
                                      PopperProps={{
                                        style: { zIndex: 100050 },
                                        disablePortal: false,
                                      }}
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            fontFamily: "Tajawal, sans-serif",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            bgcolor: "rgba(15, 23, 42, 0.92)",
                                            borderRadius: "8px",
                                            px: 1.25,
                                            py: 0.75,
                                          },
                                        },
                                        arrow: {
                                          sx: {
                                            color: "rgba(15, 23, 42, 0.92)",
                                          },
                                        },
                                      }}
                                    >
                                      <span
                                        className="history-delete-btn-wrap"
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                      >
                                        <IconButton
                                          className="history-delete-btn"
                                          size="small"
                                          aria-label={intl.formatMessage({
                                            id: "chatbot.deleteChat",
                                            defaultMessage:
                                              "Delete conversation",
                                          })}
                                          disabled={
                                            isDeletingChat &&
                                            deleteConfirmChat?.id === chat.id
                                          }
                                          onClick={(e) =>
                                            openDeleteChatConfirm(chat, e)
                                          }
                                        >
                                          {isDeletingChat &&
                                          deleteConfirmChat?.id === chat.id ? (
                                            <CircularProgress
                                              size={14}
                                              sx={{ color: "#ef4444" }}
                                            />
                                          ) : (
                                            <Delete fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )
                    )}
                    {historyPagination.hasMore &&
                      !historySearchQuery.trim() &&
                      chatHistory.length > 0 && (
                        <Box
                          className="history-list-load-more"
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "16px 8px 24px",
                            minHeight: 56,
                          }}
                        >
                          {isLoadingMoreChatHistory ? (
                            <CircularProgress
                              size={22}
                              sx={{ color: "var(--main-color)" }}
                            />
                          ) : null}
                        </Box>
                      )}
                  </>
                )}

                {/* No History Message */}
                {!isLoadingChatHistory && chatHistory.length === 0 && (
                  <Box
                    className="no-history"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "60px 20px",
                      textAlign: "center",
                      minHeight: "300px",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(9, 134, 237, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "24px",
                      }}
                    >
                      <History
                        sx={{
                          fontSize: "40px",
                          color: "var(--main-color)",
                          opacity: 0.6,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "Tajawal, sans-serif",
                        color: "#374151",
                        fontSize: "18px",
                        marginBottom: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {intl.formatMessage({ id: "chatbot.noChatHistory" })}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Tajawal, sans-serif",
                        color: "#6b7280",
                        fontSize: "14px",
                        maxWidth: "300px",
                        lineHeight: 1.6,
                      }}
                    >
                      {intl.formatMessage({
                        id: "chatbot.noChatHistoryDesc",
                        defaultMessage:
                          "ابدأ محادثة جديدة لتبدأ المحادثات في الظهور هنا",
                      })}
                    </Typography>
                  </Box>
                )}

                {/* No Search Results Message */}
                {showNoHistorySearchResults && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "40px 20px",
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Tajawal, sans-serif",
                        color: "#374151",
                        fontWeight: 600,
                        marginBottom: "8px",
                      }}
                    >
                      {intl.formatMessage({
                        id: "chatbot.noResults",
                        defaultMessage: "No Results",
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Tajawal, sans-serif",
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      {intl.formatMessage({
                        id: "chatbot.noResultsDesc",
                        defaultMessage: "Try another search keyword",
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* User Profile Section */}
              <Box className="history-user-profile">
                <Box className="user-avatar">
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Tajawal, sans-serif",
                      fontWeight: 600,
                      color: "white",
                    }}
                  >
                    {getUserInitials()}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  className="user-name"
                  style={{ fontFamily: "Tajawal, sans-serif" }}
                >
                  {getUserName()}
                </Typography>
              </Box>
            </Box>
          )}

          <Box
            ref={messagesContainerRef}
            className={`chatbot-messages ${
              isFullscreen ? "fullscreen-messages" : ""
            }`}
          >
            {/* Infinite scroll: load next page when user scrolls up toward older messages (sentinel at top) */}
            {pagination.hasMore && currentChatId && !isLoadingMoreMessages && (
              <Box
                className="messages-load-more-hint"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  padding: "6px 16px 10px",
                  marginBottom: "4px",
                }}
              >
                <KeyboardDoubleArrowUp
                  className="messages-load-more-hint-icon"
                  sx={{
                    color: "var(--main-color)",
                    fontSize: 22,
                    opacity: 0.85,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontFamily: "Tajawal, sans-serif",
                    fontSize: "12px",
                    fontWeight: 500,
                    textAlign: "center",
                    lineHeight: 1.35,
                    maxWidth: 280,
                  }}
                >
                  {intl.formatMessage(
                    {
                      id: "chatbot.scrollForMore",
                      defaultMessage:
                        "مرّر لأعلى لعرض المزيد من الرسائل ({current} من {total})",
                    },
                    {
                      current: pagination.maxLoadedPage ?? pagination.page,
                      total: pagination.totalPages,
                    }
                  )}
                </Typography>
              </Box>
            )}

            {currentChatId && pagination.hasMore && (
              <Box
                ref={messagesLoadMoreSentinelRef}
                className="messages-infinite-sentinel"
                aria-hidden
                sx={{ height: 8, width: "100%", flexShrink: 0 }}
              />
            )}

            {messages.map((message) => {
              const messageTextRaw =
                typeof message.text === "string"
                  ? message.text
                  : message.text != null
                  ? String(message.text)
                  : "";

              const hasBottomGraph =
                !message.isUser &&
                ((message.graph && String(message.graph).trim()) ||
                  (!message.graph &&
                    extractGraphUrlsFromText(messageTextRaw).length > 0));

              const hasBottomExcel =
                API_VERSION === "v1" &&
                !message.isUser &&
                ((message.excelCode && String(message.excelCode).trim()) ||
                  extractExcelUrlsFromText(messageTextRaw).length > 0);

              const hasDeferredMedia = hasBottomGraph || hasBottomExcel;

              const sourceSplit = hasDeferredMedia
                ? splitTrailingSourceSection(messageTextRaw)
                : { main: messageTextRaw, sourceBlock: "" };

              const markdownBodyForRender = sourceSplit.main;
              const inlineMediaSegments = !message.isUser
                ? splitIntoInlineMediaSegments(markdownBodyForRender)
                : null;
              const hasInlineMediaOrder =
                !!inlineMediaSegments &&
                inlineMediaSegments.some((s) => s.type !== "markdown");
              const suppressBottomMarkdownMedia = hasInlineMediaOrder;

              const markdownSourceRaw = sourceSplit.sourceBlock;
              const markdownTextForSourceFooter = markdownSourceRaw
                ? preprocessMarkdown(
                    stripExcelLinksFromText(
                      stripGraphLinksFromText(markdownSourceRaw)
                    )
                  )
                : "";

              const msgDirForMarkdown =
                message.textDirection ||
                (message.isUser
                  ? message.questionLanguage === "en"
                    ? "ltr"
                    : "rtl"
                  : "rtl");
              const markdownComponentsCacheKey = `md-${message.id}-${msgDirForMarkdown}-${isDarkMode}-${isFullscreen}-${dir}`;

              return (
                <Slide
                  key={message.id}
                  direction={message.isUser ? "left" : "right"}
                  in={true}
                  timeout={300}
                >
                  <Box
                    data-message-id={message.id}
                    className={`message ${
                      message.isUser
                        ? "user-message"
                        : message.isThinking
                        ? "thinking-message"
                        : `bot-message ${!isFullscreen ? "popup-message" : ""}`
                    }${message.graph && !message.isUser ? " has-graph" : ""}`}
                  >
                    <div
                      style={{
                        display: "flex",
                        // flexDirection: "row-reverse",
                        gap: "8px",
                      }}
                    >
                      {/* Bot Avatar - Only for bot messages */}
                      {!message.isUser && !message.isThinking && (
                        <Box
                          className="message-avatar"
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "#eee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src="/logo.png"
                            alt="Chatbot"
                            style={{ width: "30px" }}
                          />
                        </Box>
                      )}
                      <Box
                        className="message-content"
                        sx={{
                          flex: "1 1 0%",
                          minWidth: 0,
                          maxWidth: "100%",
                          width: "100%",
                        }}
                      >
                        {/* Show loading indicator when message is loading (initial load) */}
                        {message.isLoading ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 2,
                              padding: "24px 32px",
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                              borderRadius: "20px",
                              minWidth: "200px",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                              backdropFilter: "blur(10px)",
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: "48px",
                                height: "48px",
                              }}
                            >
                              <CircularProgress
                                size={48}
                                thickness={4}
                                sx={{
                                  color: "#1976d2",
                                  animationDuration: "1.2s",
                                }}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "50%",
                                  backgroundColor: "#1976d2",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  animation: "pulse 1.5s ease-in-out infinite",
                                  "@keyframes pulse": {
                                    "0%, 100%": {
                                      transform:
                                        "translate(-50%, -50%) scale(1)",
                                      opacity: 1,
                                    },
                                    "50%": {
                                      transform:
                                        "translate(-50%, -50%) scale(0.9)",
                                      opacity: 0.8,
                                    },
                                  },
                                }}
                              >
                                <SmartToy
                                  sx={{
                                    color: "white",
                                    fontSize: "18px",
                                  }}
                                />
                              </Box>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#1976d2",
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                fontFamily: "Tajawal, sans-serif",
                                textAlign: "center",
                              }}
                            >
                              {message.text ||
                                intl.formatMessage({
                                  id: "chatbot.loading",
                                  defaultMessage: "جاري التحميل...",
                                })}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                alignItems: "center",
                              }}
                            >
                              {[0, 1, 2].map((i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: "6px",
                                    height: "6px",
                                    borderRadius: "50%",
                                    backgroundColor: "#1976d2",
                                    animation: `bounce 1.4s ease-in-out ${
                                      i * 0.2
                                    }s infinite`,
                                    "@keyframes bounce": {
                                      "0%, 80%, 100%": {
                                        transform: "scale(0.8)",
                                        opacity: 0.5,
                                      },
                                      "40%": {
                                        transform: "scale(1.2)",
                                        opacity: 1,
                                      },
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        ) : message.isThinking && API_VERSION !== "v1" ? (
                          /* جاري تحليل السؤال / جاري البحث في البيانات - contained UI, no overlap */
                          <Box
                            className="thinking-message-content"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "12px 18px",
                              minWidth: "180px",
                              maxWidth: "100%",
                              borderRadius: "20px",
                              boxSizing: "border-box",
                              background:
                                "linear-gradient(135deg, rgba(9, 134, 237, 0.08) 0%, rgba(9, 134, 237, 0.04) 100%)",
                              border: "1px solid rgba(9, 134, 237, 0.2)",
                              boxShadow:
                                "0 2px 10px rgba(9, 134, 237, 0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
                              flexShrink: 0,
                              overflow: "visible",
                            }}
                          >
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                flexShrink: 0,
                                borderRadius: "50%",
                                border: "2px solid var(--main-color)",
                                borderTopColor: "transparent",
                                animation: "spin 0.9s linear infinite",
                                "@keyframes spin": {
                                  to: { transform: "rotate(360deg)" },
                                },
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "Tajawal, sans-serif",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "var(--main-color)",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                // textAlign: "start !important",
                                flex: "1 1 auto",
                                minWidth: 0,
                                lineHeight: 1.4,
                              }}
                            >
                              {typeof message.text === "string"
                                ? message.text
                                : message.text != null
                                ? String(message.text)
                                : ""}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "3px",
                                flexShrink: 0,
                                "& span": {
                                  width: "5px",
                                  height: "5px",
                                  borderRadius: "50%",
                                  backgroundColor: "var(--main-color)",
                                  animation:
                                    "dotPulse 1.2s ease-in-out infinite",
                                  "&:nth-of-type(2)": {
                                    animationDelay: "0.15s",
                                  },
                                  "&:nth-of-type(3)": {
                                    animationDelay: "0.3s",
                                  },
                                },
                              }}
                            >
                              <Box component="span" />
                              <Box component="span" />
                              <Box component="span" />
                            </Box>
                          </Box>
                        ) : (message.isStreaming ||
                            streamingMessageId === message.id) &&
                          !message.text?.trim() &&
                          (!message.eventBlocks ||
                            message.eventBlocks.length === 0) ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.75,
                              padding: "14px 20px",
                              backgroundColor: "rgba(0, 0, 0, 0.05)",
                              borderRadius: "20px",
                              minWidth: "72px",
                              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                            }}
                          >
                            {[0, 1, 2].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: "50%",
                                  backgroundColor: "#1976d2",
                                  animation: `typingBounce 1.4s ease-in-out ${
                                    i * 0.16
                                  }s infinite`,
                                  "@keyframes typingBounce": {
                                    "0%, 60%, 100%": {
                                      transform: "translateY(0)",
                                    },
                                    "30%": {
                                      transform: "translateY(-6px)",
                                    },
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              position: "relative",
                            }}
                          >
                            <Box
                              className={`message-text ${
                                message.isStreaming ? "streaming" : ""
                              }`}
                              dir={
                                message.textDirection ||
                                (message.isUser
                                  ? message.questionLanguage === "en"
                                    ? "ltr"
                                    : "rtl"
                                  : "rtl")
                              }
                            >
                              {(() => {
                                // Get text direction for this message
                                const msgDir =
                                  message.textDirection ||
                                  (message.isUser
                                    ? message.questionLanguage === "en"
                                      ? "ltr"
                                      : "rtl"
                                    : "rtl");

                                // Normalize markdown (strip [GRAPH]/[EXCEL] only when not using inline order).
                                const markdownTextFallback = preprocessMarkdown(
                                  stripExcelLinksFromText(
                                    stripGraphLinksFromText(
                                      markdownBodyForRender
                                    )
                                  )
                                );

                                const markdownComponents =
                                  getCachedMarkdownComponents(
                                    markdownComponentsCacheKey,
                                    () => ({
                                      br: () => (
                                        <br
                                          style={{
                                            display: "block",
                                            margin: "4px 0",
                                            content: "",
                                          }}
                                        />
                                      ),
                                      // Render blockquote as plain block so no ">" appears during stream
                                      blockquote: ({ children }) => (
                                        <Box
                                          component="span"
                                          sx={{ display: "block" }}
                                        >
                                          {children}
                                        </Box>
                                      ),
                                      h1: ({ children }) => (
                                        <Typography
                                          variant="h5"
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            fontWeight: "bold",
                                            marginTop: 0,
                                            marginBottom: "6px",
                                            color: "#000",
                                            fontSize: "1.2em",
                                            lineHeight: 1.3,
                                            display: "block",
                                            clear: "both",
                                            textAlign: "start !important",
                                          }}
                                        >
                                          {children}
                                        </Typography>
                                      ),
                                      h2: ({ children }) => (
                                        <Typography
                                          variant="h6"
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            fontWeight: "bold",
                                            marginTop: 0,
                                            marginBottom: "5px",
                                            color: "#000",
                                            fontSize: isFullscreen
                                              ? "1.1em"
                                              : "1.08em",
                                            lineHeight: 1.3,
                                            display: "block",
                                            clear: "both",
                                            textAlign: "start !important",
                                          }}
                                        >
                                          {children}
                                        </Typography>
                                      ),
                                      h3: ({ children }) => (
                                        <Typography
                                          variant="subtitle1"
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            fontWeight: "bold",
                                            marginTop: 0,
                                            marginBottom: "4px",
                                            color: "#000",
                                            fontSize: isFullscreen
                                              ? "1.05em"
                                              : "1.02em",
                                            lineHeight: 1.3,
                                            display: "block",
                                            clear: "both",
                                            textAlign: "start !important",
                                          }}
                                        >
                                          {children}
                                        </Typography>
                                      ),
                                      h4: ({ children }) => (
                                        <Typography
                                          variant="subtitle2"
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            fontWeight: "bold",
                                            marginTop: 0,
                                            marginBottom: "4px",
                                            color: "#000",
                                            fontSize: "1em",
                                            lineHeight: 1.3,
                                            display: "block",
                                            textAlign: "start !important",
                                          }}
                                        >
                                          {children}
                                        </Typography>
                                      ),
                                      h5: ({ children }) => (
                                        <Typography
                                          variant="body1"
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            fontWeight: "bold",
                                            marginTop: 0,
                                            marginBottom: "3px",
                                            color: "#000",
                                            fontSize: "0.95em",
                                            lineHeight: 1.3,
                                            display: "block",
                                            textAlign: "start !important",
                                          }}
                                        >
                                          {children}
                                        </Typography>
                                      ),
                                      h6: ({ children }) => (
                                        <Typography
                                          variant="body2"
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            fontWeight: "bold",
                                            marginTop: 0,
                                            marginBottom: "3px",
                                            color: "#000",
                                            fontSize: "0.9em",
                                            lineHeight: 1.3,
                                            display: "block",
                                            textAlign: "start !important",
                                          }}
                                        >
                                          {children}
                                        </Typography>
                                      ),
                                      p: ({ children }) => {
                                        // Recursively detect blank content:
                                        // handles null, empty/whitespace strings,
                                        // and arrays that are entirely whitespace
                                        // (react-markdown often passes ["\n"] for
                                        // blank lines, which is not a plain string)
                                        const isBlank = (c) => {
                                          if (
                                            c === null ||
                                            c === undefined ||
                                            c === false
                                          )
                                            return true;
                                          if (typeof c === "string")
                                            return !c.trim();
                                          if (Array.isArray(c))
                                            return c.every(isBlank);
                                          return false;
                                        };
                                        if (isBlank(children)) {
                                          // Return null — no DOM node at all.
                                          // A <br> with line-height:2 still creates
                                          // ~28px of phantom space above tables.
                                          return null;
                                        }
                                        return (
                                          <Typography
                                            variant="body2"
                                            dir={msgDir}
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              marginTop: 0,
                                              marginBottom: "6px",
                                              lineHeight: 1.6,
                                              fontSize: isFullscreen
                                                ? "14px"
                                                : "13px",
                                              whiteSpace: "pre-wrap",
                                              wordBreak: "break-word",
                                              display: "block",
                                              textAlign: "start !important",
                                            }}
                                          >
                                            {children}
                                          </Typography>
                                        );
                                      },
                                      ul: ({ children }) => (
                                        <Box
                                          component="ul"
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            paddingInline: "20px",
                                            fontSize: isFullscreen
                                              ? "14px"
                                              : "13px",
                                            listStylePosition: "outside",
                                            listStyleType: "disc",
                                            textAlign: "start !important",
                                            display: "block",
                                            margin: "0",
                                          }}
                                        >
                                          {children}
                                        </Box>
                                      ),
                                      ol: ({ children, start }) => (
                                        <Box
                                          component="ol"
                                          dir={msgDir}
                                          start={start}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            margin: "0",
                                            paddingInline: "20px",
                                            fontSize: isFullscreen
                                              ? "14px"
                                              : "13px",
                                            listStylePosition: "outside",
                                            listStyleType: "decimal",
                                            textAlign: "start  !important",
                                          }}
                                        >
                                          {children}
                                        </Box>
                                      ),
                                      li: ({ children }) => (
                                        <Typography
                                          component="li"
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            lineHeight: 1.5,
                                            fontSize: isFullscreen
                                              ? "14px"
                                              : "13px",
                                            textAlign: "start !important",
                                            display: "list-item",
                                            listStyleType: "inherit",
                                          }}
                                        >
                                          {children}
                                        </Typography>
                                      ),
                                      // blockquote: ({ children }) => (
                                      //   <Box
                                      //     component="blockquote"
                                      //     dir={msgDir}
                                      //     style={{
                                      //       fontFamily: "Tajawal, sans-serif",
                                      //       borderRight:
                                      //         msgDir === "rtl"
                                      //           ? "4px solid #000"
                                      //           : "none",
                                      //       borderLeft:
                                      //         msgDir === "ltr"
                                      //           ? "4px solid #000"
                                      //           : "none",
                                      //       paddingRight:
                                      //         msgDir === "rtl" ? "16px" : "12px",
                                      //       paddingLeft:
                                      //         msgDir === "ltr" ? "16px" : "12px",
                                      //       // margin: "16px 0",
                                      //       fontStyle: "italic",
                                      //       backgroundColor: "#f0f7ff",
                                      //       padding: isFullscreen
                                      //         ? "12px 16px"
                                      //         : "10px 14px",
                                      //       borderRadius: "8px",
                                      //       fontSize: isFullscreen
                                      //         ? "14px"
                                      //         : "13px",
                                      //       lineHeight: 1.8,
                                      //       textAlign: "start  !important",
                                      //     }}
                                      //   >
                                      //     {children}
                                      //   </Box>
                                      // ),
                                      code: ({ children }) => (
                                        <Box
                                          component="code"
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "monospace",
                                            backgroundColor: "#f5f5f5",
                                            padding: "1px 3px",
                                            borderRadius: "3px",
                                            fontSize: "12px",
                                            textAlign: "start  !important",
                                          }}
                                        >
                                          {children}
                                        </Box>
                                      ),
                                      pre: ({ children }) => (
                                        <Box
                                          component="pre"
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "monospace",
                                            backgroundColor: "#282c34",
                                            color: "#abb2bf",
                                            padding: isFullscreen
                                              ? "16px"
                                              : "12px",
                                            borderRadius: "8px",
                                            overflow: "auto",
                                            // margin: "12px 0",
                                            fontSize: "13px",
                                            lineHeight: 1.6,
                                            border: "1px solid #3e4451",
                                            textAlign: "start  !important",
                                          }}
                                        >
                                          {children}
                                        </Box>
                                      ),
                                      table: ({ children, node }) => {
                                        const tableNodeProps =
                                          getTableElementProps(node);
                                        const {
                                          style: tableNodeStyle,
                                          ...restTableProps
                                        } = tableNodeProps;
                                        const baseTableStyle = {
                                          fontFamily: "Tajawal, sans-serif",
                                          width: "100%",
                                          borderCollapse: "collapse",
                                          tableLayout: "auto",
                                          fontSize: isFullscreen
                                            ? "14px"
                                            : "13px",
                                          borderRadius: "8px",
                                          boxShadow:
                                            "0 2px 8px rgba(0,0,0,0.1)",
                                          textAlign: "start !important",
                                        };
                                        return (
                                          <Box
                                            component="div"
                                            dir={msgDir}
                                            sx={{
                                              width: "100%",
                                              overflowX: "auto",
                                              overflowY: "visible",
                                              margin: "4px 0 8px",
                                              "& table": {
                                                fontFamily:
                                                  "Tajawal, sans-serif",
                                                width: "100%",
                                                borderCollapse: "collapse",
                                                tableLayout: "auto",
                                                fontSize: isFullscreen
                                                  ? "14px"
                                                  : "13px",
                                                borderRadius: "8px",
                                                boxShadow:
                                                  "0 2px 8px rgba(0,0,0,0.1)",
                                                textAlign: "start  !important",
                                                minWidth: "100%",
                                              },
                                            }}
                                          >
                                            <Box
                                              component="table"
                                              dir={msgDir}
                                              {...restTableProps}
                                              style={{
                                                ...baseTableStyle,
                                                ...(tableNodeStyle || {}),
                                              }}
                                            >
                                              {children}
                                            </Box>
                                          </Box>
                                        );
                                      },
                                      th: ({ children, node }) => {
                                        const nodeProps =
                                          getTableElementProps(node);
                                        const {
                                          style: nodeStyle,
                                          ...restNodeProps
                                        } = nodeProps;
                                        // Helper function to fix negative number format (same as td)
                                        const fixNegativeNumberFormat = (
                                          text
                                        ) => {
                                          if (typeof text !== "string")
                                            return text;
                                          return text.replace(
                                            /(\d+(?:\.\d+)?)\s*-\s*($|[\s.,;:()\[\]])/g,
                                            (match, number, after, offset) => {
                                              const beforeMatch =
                                                text.substring(0, offset);
                                              const trimmedBefore =
                                                beforeMatch.trim();
                                              const lastCharBefore =
                                                trimmedBefore.slice(-1);

                                              if (/\d/.test(lastCharBefore)) {
                                                if (
                                                  after === "" ||
                                                  /^[\s.,;:()\[\]]/.test(after)
                                                ) {
                                                  return `-${number}${after}`;
                                                }
                                                return match;
                                              }

                                              if (
                                                trimmedBefore === "" ||
                                                /[\s.,;:()\[\]]/.test(
                                                  lastCharBefore
                                                )
                                              ) {
                                                return `-${number}${after}`;
                                              }

                                              return `-${number}${after}`;
                                            }
                                          );
                                        };

                                        // Process children to fix negative numbers
                                        const processChildren = (children) => {
                                          if (typeof children === "string") {
                                            return fixNegativeNumberFormat(
                                              children
                                            );
                                          }
                                          if (Array.isArray(children)) {
                                            return children.map((child) =>
                                              typeof child === "string"
                                                ? fixNegativeNumberFormat(child)
                                                : child
                                            );
                                          }
                                          return children;
                                        };

                                        const processedChildren =
                                          processChildren(children);
                                        const baseThStyle = {
                                          fontFamily: "Tajawal, sans-serif",
                                          border: "1px solid #e0e0e0",
                                          padding: "10px 12px",
                                          backgroundColor: "#000",
                                          color: "white",
                                          fontWeight: "bold",
                                          fontSize: isFullscreen
                                            ? "14px"
                                            : "13px",
                                          textAlign: "start !important",
                                          whiteSpace: "normal",
                                          wordBreak: "normal",
                                          overflowWrap: "break-word",
                                          lineHeight: 1.4,
                                          minWidth: "100px",
                                          verticalAlign: "middle",
                                        };

                                        return (
                                          <Box
                                            component="th"
                                            dir={msgDir}
                                            {...restNodeProps}
                                            style={{
                                              ...baseThStyle,
                                              ...(nodeStyle || {}),
                                            }}
                                          >
                                            {processedChildren}
                                          </Box>
                                        );
                                      },
                                      td: ({ children, node }) => {
                                        const nodeProps =
                                          getTableElementProps(node);
                                        const {
                                          style: nodeStyle,
                                          ...restNodeProps
                                        } = nodeProps;
                                        // Helper function to fix negative number format (move - from end to beginning)
                                        const fixNegativeNumberFormat = (
                                          text
                                        ) => {
                                          if (typeof text !== "string")
                                            return text;

                                          // Match patterns like "24.6-", "22.1-", "43.6-", etc.
                                          // Pattern: number (with optional decimal) followed by a minus sign
                                          // This handles: "24.6-", "22.1- ", "43.6-", "49.7-"
                                          return text.replace(
                                            /(\d+(?:\.\d+)?)\s*-\s*($|[\s.,;:()\[\]])/g,
                                            (match, number, after, offset) => {
                                              // Get context before the match
                                              const beforeMatch =
                                                text.substring(0, offset);
                                              const trimmedBefore =
                                                beforeMatch.trim();
                                              const lastCharBefore =
                                                trimmedBefore.slice(-1);

                                              // Check if there's a digit immediately before (could be a range like "10-20")
                                              if (/\d/.test(lastCharBefore)) {
                                                // If followed by end of string or space/punctuation, it's likely negative
                                                if (
                                                  after === "" ||
                                                  /^[\s.,;:()\[\]]/.test(after)
                                                ) {
                                                  return `-${number}${after}`;
                                                }
                                                // Otherwise might be a range, keep original
                                                return match;
                                              }

                                              // If at start or after space/punctuation, it's a negative number
                                              if (
                                                trimmedBefore === "" ||
                                                /[\s.,;:()\[\]]/.test(
                                                  lastCharBefore
                                                )
                                              ) {
                                                return `-${number}${after}`;
                                              }

                                              // Default: treat as negative
                                              return `-${number}${after}`;
                                            }
                                          );
                                        };

                                        // Process children to fix negative numbers
                                        const processChildren = (children) => {
                                          if (typeof children === "string") {
                                            return fixNegativeNumberFormat(
                                              children
                                            );
                                          }
                                          if (Array.isArray(children)) {
                                            return children.map((child) =>
                                              typeof child === "string"
                                                ? fixNegativeNumberFormat(child)
                                                : child
                                            );
                                          }
                                          if (
                                            React.isValidElement(children) &&
                                            children.props?.children
                                          ) {
                                            return React.cloneElement(
                                              children,
                                              {
                                                children: processChildren(
                                                  children.props.children
                                                ),
                                              }
                                            );
                                          }
                                          return children;
                                        };

                                        const processedChildren =
                                          processChildren(children);

                                        // Check if content is numeric (for proper alignment)
                                        const content =
                                          typeof processedChildren === "string"
                                            ? processedChildren
                                            : Array.isArray(processedChildren)
                                            ? processedChildren.join("")
                                            : String(processedChildren);
                                        const isNumeric = /^[\d.,\s-]+$/.test(
                                          content.trim()
                                        );
                                        const baseTdStyle = {
                                          fontFamily: "Tajawal, sans-serif",
                                          border: "1px solid #e0e0e0",
                                          padding: "10px 12px",
                                          fontSize: isFullscreen
                                            ? "14px"
                                            : "13px",
                                          backgroundColor: "white",
                                          textAlign: "start !important",
                                          whiteSpace: isNumeric
                                            ? "nowrap"
                                            : "normal",
                                          wordBreak: isNumeric
                                            ? "normal"
                                            : "normal",
                                          overflowWrap: isNumeric
                                            ? "normal"
                                            : "break-word",
                                          lineHeight: 1.5,
                                          verticalAlign: "top",
                                          minWidth: isNumeric
                                            ? "70px"
                                            : "120px",
                                        };

                                        return (
                                          <Box
                                            component="td"
                                            dir={msgDir}
                                            {...restNodeProps}
                                            style={{
                                              ...baseTdStyle,
                                              ...(nodeStyle || {}),
                                            }}
                                          >
                                            {processedChildren}
                                          </Box>
                                        );
                                      },
                                      a: ({ href, children }) => {
                                        // Check if this is a GRAPH link
                                        // Handle different children formats: string, array of strings, or React elements
                                        let linkText = "";
                                        if (typeof children === "string") {
                                          linkText = children.trim();
                                        } else if (Array.isArray(children)) {
                                          // Extract text from array (could be strings or React elements)
                                          linkText = children
                                            .map((child) => {
                                              if (typeof child === "string")
                                                return child;
                                              if (
                                                child &&
                                                typeof child === "object" &&
                                                child.props &&
                                                child.props.children
                                              ) {
                                                return String(
                                                  child.props.children
                                                );
                                              }
                                              return "";
                                            })
                                            .join("")
                                            .trim();
                                        } else if (
                                          children &&
                                          typeof children === "object" &&
                                          children.props &&
                                          children.props.children
                                        ) {
                                          linkText = String(
                                            children.props.children
                                          ).trim();
                                        }

                                        const isGraphLink =
                                          linkText.toUpperCase() === "GRAPH";

                                        if (isGraphLink && href) {
                                          // Construct the graph URL: baseUrl + /graphs/ + filename
                                          // Extract filename from href (in case it's a full URL or just filename)
                                          let filename = href;
                                          if (
                                            href.startsWith("http://") ||
                                            href.startsWith("https://")
                                          ) {
                                            // Extract filename from URL (last part after the last /)
                                            const urlParts = href.split("/");
                                            filename =
                                              urlParts[urlParts.length - 1];
                                          }
                                          // Remove any leading slashes from filename
                                          filename = filename.replace(
                                            /^\/+/,
                                            ""
                                          );

                                          const graphUrl = `${END_POINTS.CHATBOT_API_BASE_URL_GRAPH}/${filename}`;

                                          return (
                                            <GraphIframeCell
                                              key={graphUrl}
                                              graphUrl={graphUrl}
                                              isDarkMode={isDarkMode}
                                            />
                                          );
                                        }

                                        // Excel link: always use EXCEL_BASE_URL/file/filename (fix relative or malformed https://filename.xlsx)
                                        let resolvedHref = href;
                                        if (href) {
                                          const isExcelLink =
                                            linkText.toUpperCase() ===
                                              "EXCEL" || /\.xlsx$/i.test(href);
                                          if (isExcelLink) {
                                            // ExcelIframe renders the viewer separately below the text; suppress the inline link
                                            return null;
                                          }
                                        }

                                        // Regular link
                                        return (
                                          <a
                                            href={resolvedHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            dir={msgDir}
                                            style={{
                                              color: "var(--main-color)",
                                              textDecoration: "none",
                                              fontFamily: "Tajawal, sans-serif",
                                              fontSize: isFullscreen
                                                ? "14px"
                                                : "13px",
                                              fontWeight: "bold",
                                              textAlign: "start !important",
                                            }}
                                          >
                                            {children}
                                          </a>
                                        );
                                      },
                                      strong: ({ children }) => (
                                        <strong
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            fontWeight: "bold",
                                            fontSize: isFullscreen
                                              ? "14px"
                                              : "13px",
                                            textAlign: "start !important",
                                          }}
                                        >
                                          {children}
                                        </strong>
                                      ),
                                      em: ({ children }) => (
                                        <em
                                          dir={msgDir}
                                          style={{
                                            fontFamily: "Tajawal, sans-serif",
                                            fontStyle: "italic",
                                            fontSize: isFullscreen
                                              ? "14px"
                                              : "13px",
                                            textAlign: "start !important",
                                          }}
                                        >
                                          {children}
                                        </em>
                                      ),
                                      hr: () => (
                                        <Box
                                          component="hr"
                                          sx={{
                                            border: "none",
                                            borderTop: "1px solid",
                                            borderColor: isDarkMode
                                              ? "rgba(255, 255, 255, 0.2)"
                                              : "rgba(0, 0, 0, 0.2)",
                                            margin: "16px 0",
                                            width: "100%",
                                          }}
                                        />
                                      ),
                                    })
                                  );

                                if (
                                  hasInlineMediaOrder &&
                                  inlineMediaSegments
                                ) {
                                  const excelInlineSx = {
                                    marginTop: 2,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "8px",
                                    overflow: "auto",
                                    backgroundColor: "#fff",
                                    width: "100%",
                                    maxWidth: "100%",
                                    position: "relative",
                                    WebkitOverflowScrolling: "touch",
                                  };
                                  return (
                                    <>
                                      {inlineMediaSegments.map((seg, i) => {
                                        if (seg.type === "markdown") {
                                          const md = preprocessMarkdown(
                                            seg.content || ""
                                          );
                                          if (!String(md).trim()) return null;
                                          return (
                                            <ReactMarkdown
                                              key={`md-${message.id}-${i}-${messageTextRaw.length}`}
                                              remarkPlugins={[
                                                remarkGfm,
                                                remarkBreaks,
                                              ]}
                                              rehypePlugins={[
                                                rehypeRaw,
                                                rehypeHighlight,
                                              ]}
                                              children={md}
                                              components={markdownComponents}
                                            />
                                          );
                                        }
                                        if (seg.type === "graph") {
                                          const graphUrl =
                                            normalizeGraphHrefToUrl(seg.href);
                                          if (!graphUrl) return null;
                                          return (
                                            <Box
                                              key={`g-${message.id}-${i}`}
                                              sx={{
                                                width: "100%",
                                                marginTop: "12px",
                                                marginBottom: "8px",
                                              }}
                                            >
                                              <GraphIframeCell
                                                graphUrl={graphUrl}
                                                isDarkMode={isDarkMode}
                                              />
                                            </Box>
                                          );
                                        }
                                        if (seg.type === "excel") {
                                          const fileUrl =
                                            normalizeExcelHrefToUrl(seg.href);
                                          if (!fileUrl) return null;
                                          return (
                                            <Box
                                              key={`e-${message.id}-${i}`}
                                              className="message-graph message-excel"
                                              sx={excelInlineSx}
                                            >
                                              <ExcelIframe
                                                excelUrls={[fileUrl]}
                                                isDarkMode={isDarkMode}
                                              />
                                            </Box>
                                          );
                                        }
                                        return null;
                                      })}
                                    </>
                                  );
                                }

                                return (
                                  <ReactMarkdown
                                    key={`md-${message.id}-${
                                      messageTextRaw.split("\n").length
                                    }-${messageTextRaw.length}`}
                                    remarkPlugins={[remarkGfm, remarkBreaks]}
                                    rehypePlugins={[rehypeRaw, rehypeHighlight]}
                                    children={markdownTextFallback}
                                    components={markdownComponents}
                                  />
                                );
                              })()}
                            </Box>

                            {/* Render event blocks as collapsible boxes */}
                            {message.eventBlocks &&
                              message.eventBlocks.length > 0 && (
                                <Box
                                  sx={{
                                    mb: 2,
                                    width: "100%",
                                    maxWidth: "100% !important",
                                    position: "relative",
                                  }}
                                >
                                  {/* Collapse/Expand All Button */}
                                  {(() => {
                                    const collapsibleBlocks =
                                      message.eventBlocks.filter(
                                        (block) =>
                                          !isGraphExcelHeaderOnlyEventBlock(
                                            block.eventType
                                          )
                                      );
                                    const allCollapsed =
                                      collapsibleBlocks.length > 0 &&
                                      collapsibleBlocks.every((block) => {
                                        const key = `${message.id}_${block.eventType}`;
                                        return (
                                          collapsedEventBlocks[key] !== false
                                        );
                                      });

                                    if (collapsibleBlocks.length === 0)
                                      return null;

                                    return (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent:
                                            dir === "rtl"
                                              ? "flex-start"
                                              : "flex-end",
                                          mb: 1,
                                          width: "100%",
                                        }}
                                        dir={dir}
                                      >
                                        <Button
                                          size="small"
                                          onClick={() =>
                                            toggleAllEventBlocks(
                                              message.id,
                                              message.eventBlocks,
                                              !allCollapsed
                                            )
                                          }
                                          startIcon={
                                            allCollapsed ? (
                                              <ExpandMore />
                                            ) : (
                                              <ExpandLess />
                                            )
                                          }
                                          sx={{
                                            fontSize: "11px",
                                            fontFamily: "Tajawal, sans-serif",
                                            textTransform: "none",
                                            color: isDarkMode
                                              ? "rgba(255, 255, 255, 0.7)"
                                              : "rgba(0, 0, 0, 0.6)",
                                            "&:hover": {
                                              backgroundColor: isDarkMode
                                                ? "rgba(255, 255, 255, 0.08)"
                                                : "rgba(0, 0, 0, 0.05)",
                                            },
                                          }}
                                        >
                                          {allCollapsed
                                            ? intl.formatMessage({
                                                id: "chatbot.expandAll",
                                              })
                                            : intl.formatMessage({
                                                id: "chatbot.collapseAll",
                                              })}
                                        </Button>
                                      </Box>
                                    );
                                  })()}

                                  {message.eventBlocks.map(
                                    (eventBlock, index) => {
                                      const blockKey = `${message.id}_${eventBlock.eventType}`;

                                      if (
                                        isGraphExcelHeaderOnlyEventBlock(
                                          eventBlock.eventType
                                        )
                                      ) {
                                        return (
                                          <Box
                                            key={blockKey}
                                            sx={{
                                              mb: 1,
                                              width: "100%",
                                              minWidth: "100%",
                                              maxWidth: "100%",
                                              border: "1px solid",
                                              borderColor: isDarkMode
                                                ? "rgba(255, 255, 255, 0.12)"
                                                : "rgba(0, 0, 0, 0.12)",
                                              borderRadius: "8px",
                                              overflow: "hidden",
                                              opacity: 0.7,
                                              transition: "opacity 0.3s ease",
                                              boxSizing: "border-box",
                                              "&:hover": {
                                                opacity: 0.85,
                                              },
                                            }}
                                            dir={dir}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-start",
                                                padding: "8px 12px",
                                                backgroundColor: isDarkMode
                                                  ? "rgba(255, 255, 255, 0.05)"
                                                  : "rgba(0, 0, 0, 0.03)",
                                                cursor: "default",
                                                userSelect: "none",
                                                width: "100%",
                                                boxSizing: "border-box",
                                              }}
                                              dir={dir}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "8px",
                                                }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontFamily:
                                                      "Tajawal, sans-serif",
                                                    fontSize: "12px",
                                                    fontWeight: 600,
                                                    color: isDarkMode
                                                      ? "rgba(255, 255, 255, 0.7)"
                                                      : "rgba(0, 0, 0, 0.6)",
                                                  }}
                                                >
                                                  {getEventTypeName(
                                                    eventBlock.eventType
                                                  )}
                                                </Typography>
                                                {eventBlock.isStreaming && (
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "3px",
                                                      "& span": {
                                                        width: "4px",
                                                        height: "4px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                          "var(--main-color)",
                                                        animation:
                                                          "dotPulse 1.2s ease-in-out infinite",
                                                        "&:nth-of-type(2)": {
                                                          animationDelay:
                                                            "0.15s",
                                                        },
                                                        "&:nth-of-type(3)": {
                                                          animationDelay:
                                                            "0.3s",
                                                        },
                                                      },
                                                      "@keyframes dotPulse": {
                                                        "0%, 80%, 100%": {
                                                          opacity: 0.3,
                                                        },
                                                        "40%": { opacity: 1 },
                                                      },
                                                    }}
                                                  >
                                                    <span></span>
                                                    <span></span>
                                                    <span></span>
                                                  </Box>
                                                )}
                                              </Box>
                                            </Box>
                                          </Box>
                                        );
                                      }

                                      const isCollapsed =
                                        collapsedEventBlocks[blockKey] !==
                                        false;

                                      const blockFontSize = isFullscreen
                                        ? "14px"
                                        : "13px";
                                      const blockComponents = {
                                        br: () => (
                                          <br
                                            style={{
                                              display: "block",
                                              margin: "4px 0",
                                              content: "",
                                            }}
                                          />
                                        ),
                                        blockquote: ({ children }) => (
                                          <Box
                                            component="span"
                                            sx={{ display: "block" }}
                                          >
                                            {children}
                                          </Box>
                                        ),
                                        h1: ({ children }) => (
                                          <Typography
                                            variant="h5"
                                            dir={dir}
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              fontWeight: "bold",
                                              fontSize: "1.2em",
                                              display: "block",
                                            }}
                                          >
                                            {children}
                                          </Typography>
                                        ),
                                        h2: ({ children }) => (
                                          <Typography
                                            variant="h6"
                                            dir={dir}
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              fontWeight: "bold",
                                              fontSize: "1.1em",
                                              display: "block",
                                            }}
                                          >
                                            {children}
                                          </Typography>
                                        ),
                                        h3: ({ children }) => (
                                          <Typography
                                            variant="subtitle1"
                                            dir={dir}
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              fontWeight: "bold",
                                              fontSize: "1em",
                                              display: "block",
                                            }}
                                          >
                                            {children}
                                          </Typography>
                                        ),
                                        h4: ({ children }) => (
                                          <Typography
                                            variant="subtitle2"
                                            dir={dir}
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              fontWeight: "bold",
                                              fontSize: blockFontSize,
                                              display: "block",
                                            }}
                                          >
                                            {children}
                                          </Typography>
                                        ),
                                        h5: ({ children }) => (
                                          <Typography
                                            variant="body1"
                                            dir={dir}
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              fontWeight: "bold",
                                              fontSize: blockFontSize,
                                              display: "block",
                                            }}
                                          >
                                            {children}
                                          </Typography>
                                        ),
                                        h6: ({ children }) => (
                                          <Typography
                                            variant="body2"
                                            dir={dir}
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              fontWeight: "bold",
                                              fontSize: blockFontSize,
                                              display: "block",
                                            }}
                                          >
                                            {children}
                                          </Typography>
                                        ),
                                        p: ({ children }) => {
                                          const _ib = (c) =>
                                            !c ||
                                            c === false ||
                                            (typeof c === "string"
                                              ? !c.trim()
                                              : Array.isArray(c) &&
                                                c.every(_ib));
                                          return _ib(children) ? null : (
                                            <Typography
                                              variant="body2"
                                              dir={dir}
                                              style={{
                                                fontFamily:
                                                  "Tajawal, sans-serif",
                                                lineHeight: 1.6,
                                                fontSize: blockFontSize,
                                                display: "block",
                                                marginBottom: "4px",
                                              }}
                                            >
                                              {children}
                                            </Typography>
                                          );
                                        },
                                        ul: ({ children }) => (
                                          <Box
                                            component="ul"
                                            dir={dir}
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              paddingInline: "20px",
                                              listStyleType: "disc",
                                              listStylePosition: "outside",
                                              fontSize: blockFontSize,
                                            }}
                                          >
                                            {children}
                                          </Box>
                                        ),
                                        ol: ({ children, start }) => (
                                          <Box
                                            component="ol"
                                            dir={dir}
                                            start={start}
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              paddingInline: "20px",
                                              listStyleType: "decimal",
                                              listStylePosition: "outside",
                                              fontSize: blockFontSize,
                                            }}
                                          >
                                            {children}
                                          </Box>
                                        ),
                                        li: ({ children }) => (
                                          <Typography
                                            component="li"
                                            dir={dir}
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              lineHeight: 1.5,
                                              fontSize: blockFontSize,
                                              display: "list-item",
                                              listStyleType: "inherit",
                                            }}
                                          >
                                            {children}
                                          </Typography>
                                        ),
                                        a: ({ href, children }) => (
                                          <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            dir={dir}
                                            style={{
                                              color: "var(--main-color)",
                                              fontFamily: "Tajawal, sans-serif",
                                              fontSize: blockFontSize,
                                              fontWeight: "bold",
                                              textDecoration: "none",
                                            }}
                                          >
                                            {children}
                                          </a>
                                        ),
                                        strong: ({ children }) => (
                                          <strong
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              fontWeight: "bold",
                                              fontSize: blockFontSize,
                                            }}
                                          >
                                            {children}
                                          </strong>
                                        ),
                                        em: ({ children }) => (
                                          <em
                                            style={{
                                              fontFamily: "Tajawal, sans-serif",
                                              fontSize: blockFontSize,
                                            }}
                                          >
                                            {children}
                                          </em>
                                        ),
                                        table: ({ children, node }) => {
                                          const tableNodeProps =
                                            getTableElementProps(node);
                                          const {
                                            style: tableNodeStyle,
                                            ...restTableProps
                                          } = tableNodeProps;
                                          const baseTableStyle = {
                                            width: "100%",
                                            borderCollapse: "collapse",
                                            fontFamily: "Tajawal, sans-serif",
                                            fontSize: blockFontSize,
                                          };
                                          return (
                                            <Box
                                              component="div"
                                              dir={dir}
                                              sx={{
                                                overflowX: "auto",
                                                margin: "8px 0",
                                              }}
                                            >
                                              <Box
                                                component="table"
                                                dir={dir}
                                                {...restTableProps}
                                                style={{
                                                  ...baseTableStyle,
                                                  ...(tableNodeStyle || {}),
                                                }}
                                              >
                                                {children}
                                              </Box>
                                            </Box>
                                          );
                                        },
                                        th: ({ children, node }) => {
                                          const nodeProps =
                                            getTableElementProps(node);
                                          const {
                                            style: nodeStyle,
                                            ...restNodeProps
                                          } = nodeProps;
                                          const baseThStyle = {
                                            border: "1px solid #e0e0e0",
                                            padding: "8px 10px",
                                            fontWeight: "bold",
                                            backgroundColor: isDarkMode
                                              ? "#333"
                                              : "#f5f5f5",
                                            fontFamily: "Tajawal, sans-serif",
                                            fontSize: blockFontSize,
                                          };
                                          return (
                                            <Box
                                              component="th"
                                              dir={dir}
                                              {...restNodeProps}
                                              style={{
                                                ...baseThStyle,
                                                ...(nodeStyle || {}),
                                              }}
                                            >
                                              {children}
                                            </Box>
                                          );
                                        },
                                        td: ({ children, node }) => {
                                          const nodeProps =
                                            getTableElementProps(node);
                                          const {
                                            style: nodeStyle,
                                            ...restNodeProps
                                          } = nodeProps;
                                          const baseTdStyle = {
                                            border: "1px solid #e0e0e0",
                                            padding: "8px 10px",
                                            fontFamily: "Tajawal, sans-serif",
                                            fontSize: blockFontSize,
                                          };
                                          return (
                                            <Box
                                              component="td"
                                              dir={dir}
                                              {...restNodeProps}
                                              style={{
                                                ...baseTdStyle,
                                                ...(nodeStyle || {}),
                                              }}
                                            >
                                              {children}
                                            </Box>
                                          );
                                        },
                                        code: ({ children }) => (
                                          <Box
                                            component="code"
                                            style={{
                                              fontFamily: "monospace",
                                              backgroundColor: isDarkMode
                                                ? "#333"
                                                : "#f5f5f5",
                                              padding: "1px 3px",
                                              borderRadius: "3px",
                                              fontSize: "12px",
                                            }}
                                          >
                                            {children}
                                          </Box>
                                        ),
                                        pre: ({ children }) => (
                                          <Box
                                            component="pre"
                                            style={{
                                              fontFamily: "monospace",
                                              backgroundColor: isDarkMode
                                                ? "#1e1e1e"
                                                : "#282c34",
                                              color: "#abb2bf",
                                              padding: "12px",
                                              borderRadius: "8px",
                                              overflow: "auto",
                                              fontSize: "13px",
                                            }}
                                          >
                                            {children}
                                          </Box>
                                        ),
                                        // blockquote: ({ children }) => (
                                        //   <Box
                                        //     component="blockquote"
                                        //     dir={dir}
                                        //     style={{
                                        //       borderLeft:
                                        //         "4px solid var(--main-color)",
                                        //       paddingLeft: "12px",
                                        //       fontStyle: "italic",
                                        //       margin: "8px 0",
                                        //       fontFamily: "Tajawal, sans-serif",
                                        //       fontSize: blockFontSize,
                                        //     }}
                                        //   >
                                        //     {children}
                                        //   </Box>
                                        // ),
                                      };

                                      return (
                                        <Box
                                          key={blockKey}
                                          sx={{
                                            mb: 1,
                                            width: "100%",
                                            minWidth: "100%",
                                            maxWidth: "100%",
                                            border: "1px solid",
                                            borderColor: isDarkMode
                                              ? "rgba(255, 255, 255, 0.12)"
                                              : "rgba(0, 0, 0, 0.12)",
                                            borderRadius: "8px",
                                            overflow: "hidden",
                                            opacity: 0.7,
                                            transition: "opacity 0.3s ease",
                                            boxSizing: "border-box",
                                            "&:hover": {
                                              opacity: 0.85,
                                            },
                                          }}
                                          dir={dir}
                                        >
                                          {/* Event type header (clickable) */}
                                          <Box
                                            onClick={() =>
                                              toggleEventBlock(
                                                message.id,
                                                eventBlock.eventType
                                              )
                                            }
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              padding: "8px 12px",
                                              backgroundColor: isDarkMode
                                                ? "rgba(255, 255, 255, 0.05)"
                                                : "rgba(0, 0, 0, 0.03)",
                                              cursor: "pointer",
                                              userSelect: "none",
                                              width: "100%",
                                              boxSizing: "border-box",
                                              "&:hover": {
                                                backgroundColor: isDarkMode
                                                  ? "rgba(255, 255, 255, 0.08)"
                                                  : "rgba(0, 0, 0, 0.05)",
                                              },
                                            }}
                                            dir={dir}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontFamily:
                                                    "Tajawal, sans-serif",
                                                  fontSize: "12px",
                                                  fontWeight: 600,
                                                  color: isDarkMode
                                                    ? "rgba(255, 255, 255, 0.7)"
                                                    : "rgba(0, 0, 0, 0.6)",
                                                }}
                                              >
                                                {getEventTypeName(
                                                  eventBlock.eventType
                                                )}
                                              </Typography>
                                              {eventBlock.isStreaming && (
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "3px",
                                                    "& span": {
                                                      width: "4px",
                                                      height: "4px",
                                                      borderRadius: "50%",
                                                      backgroundColor:
                                                        "var(--main-color)",
                                                      animation:
                                                        "dotPulse 1.2s ease-in-out infinite",
                                                      "&:nth-of-type(2)": {
                                                        animationDelay: "0.15s",
                                                      },
                                                      "&:nth-of-type(3)": {
                                                        animationDelay: "0.3s",
                                                      },
                                                    },
                                                    "@keyframes dotPulse": {
                                                      "0%, 80%, 100%": {
                                                        opacity: 0.3,
                                                      },
                                                      "40%": { opacity: 1 },
                                                    },
                                                  }}
                                                >
                                                  <span></span>
                                                  <span></span>
                                                  <span></span>
                                                </Box>
                                              )}
                                            </Box>
                                            <IconButton
                                              size="small"
                                              sx={{
                                                padding: 0,
                                                color: isDarkMode
                                                  ? "rgba(255, 255, 255, 0.7)"
                                                  : "rgba(0, 0, 0, 0.6)",
                                              }}
                                            >
                                              {isCollapsed ? (
                                                <ExpandMore fontSize="small" />
                                              ) : (
                                                <ExpandLess fontSize="small" />
                                              )}
                                            </IconButton>
                                          </Box>

                                          {/* Event content (collapsible) */}
                                          <Collapse in={!isCollapsed}>
                                            <Box
                                              sx={{
                                                padding: "12px",
                                                fontSize: isFullscreen
                                                  ? "14px"
                                                  : "13px",
                                                width: "100%",
                                                boxSizing: "border-box",
                                              }}
                                              dir={dir}
                                            >
                                              <ReactMarkdown
                                                remarkPlugins={[
                                                  remarkGfm,
                                                  remarkBreaks,
                                                ]}
                                                rehypePlugins={[
                                                  rehypeRaw,
                                                  rehypeHighlight,
                                                ]}
                                                components={blockComponents}
                                                children={preprocessMarkdown(
                                                  cleanEventBlockContent(
                                                    eventBlock.content
                                                  )
                                                )}
                                              />
                                            </Box>
                                          </Collapse>
                                        </Box>
                                      );
                                    }
                                  )}
                                </Box>
                              )}
                          </Box>
                        )}

                        {message.details && (
                          <Box
                            className="message-details"
                            dir={
                              message.textDirection ||
                              (message.isUser
                                ? message.questionLanguage === "en"
                                  ? "ltr"
                                  : "rtl"
                                : "rtl")
                            }
                          >
                            {message.details.map((detail, index) => (
                              <Typography
                                key={index}
                                variant="body2"
                                className="detail-item"
                                dir={msgDir}
                                style={{
                                  textAlign: "start !important",
                                }}
                              >
                                {detail}
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {/* Link Cards - Display publication cover images */}
                        {message.links &&
                          message.links_cover &&
                          !message.isUser && (
                            <Box
                              className="message-links"
                              sx={{
                                marginTop: 1,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                                flexDirection: "row",
                                justifyContent:
                                  locale === "ar" ? "flex-end" : "flex-start",
                              }}
                              dir={locale === "ar" ? "rtl" : "ltr"}
                            >
                              {(() => {
                                return Object.keys(message.links).map((key) => {
                                  const link = message.links[key];
                                  const coverImage = message.links_cover[key];

                                  if (!link || !coverImage) {
                                    return null;
                                  }

                                  return (
                                    <Box
                                      key={key}
                                      className="link-card"
                                      onClick={() => {
                                        window.open(
                                          link,
                                          "_blank",
                                          "noopener,noreferrer"
                                        );
                                      }}
                                      sx={{
                                        position: "relative",
                                        width: "100px",
                                        height: "140px",
                                        borderRadius: "4px",
                                        overflow: "hidden",
                                        cursor: "pointer",
                                        transition: "all 0.3s ease",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        "&:hover": {
                                          transform: "translateY(-2px)",
                                          opacity: 0.9,
                                        },
                                      }}
                                    >
                                      {(() => {
                                        return (
                                          <img
                                            src={coverImage}
                                            alt={`Publication ${key}`}
                                            crossOrigin="anonymous"
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              display: "block",
                                            }}
                                            onLoad={(e) => {
                                              // console.log('✅ Image loaded successfully:', coverImage);
                                            }}
                                            onError={(e) => {
                                              console.error(
                                                "❌ Failed to load image:",
                                                coverImage
                                              );
                                              console.error(
                                                "Error details:",
                                                e
                                              );
                                              e.target.style.display = "none";
                                              const fallbackDiv =
                                                document.createElement("div");
                                              fallbackDiv.style.cssText = `
                                          width: 100%;
                                          height: 100%;
                                          display: flex;
                                          align-items: center;
                                          justify-content: center;
                                          background: linear-gradient(135deg, #C89637 0%, #E0B558 100%);
                                          color: white;
                                          font-family: 'Cairo', 'Tajawal', Arial, sans-serif;
                                          font-size: 14px;
                                          text-align: center;
                                          padding: 16px;
                                          cursor: pointer;
                                        `;
                                              fallbackDiv.innerHTML = `
                                          <div>
                                            <div style="font-size: 32px; margin-bottom: 8px;">📄</div>
                                            <div>منشور رقم ${key}</div>
                                          </div>
                                        `;
                                              fallbackDiv.onclick = () =>
                                                window.open(
                                                  link,
                                                  "_blank",
                                                  "noopener,noreferrer"
                                                );
                                              e.target.parentElement.appendChild(
                                                fallbackDiv
                                              );
                                            }}
                                          />
                                        );
                                      })()}
                                    </Box>
                                  );
                                });
                              })()}
                            </Box>
                          )}

                        {/* Graph rendering (v1) — مكوّن memo لتفادي إعادة رسم الرسم عند scroll/typing/sidebar */}
                        {message.graph &&
                          !message.isUser &&
                          (() => {
                            const cacheKey = message.id;
                            if (
                              !graphHTMLCacheRef.current[cacheKey] &&
                              message.graph
                            ) {
                              graphHTMLCacheRef.current[cacheKey] =
                                processGraphHTML(message.graph);
                            }
                            const processedHtml =
                              graphHTMLCacheRef.current[cacheKey];
                            return (
                              <V1GraphIframeMemo
                                messageId={message.id}
                                processedHtml={processedHtml}
                                isFullscreen={isFullscreen}
                                graphHeight={graphHeights[message.id]}
                                onHeightLoad={handleGraphHeightLoad}
                                fixedWidth={GRAPH_FIXED_WIDTH}
                                minHeight={GRAPH_MIN_HEIGHT}
                                maxHeight={GRAPH_MAX_HEIGHT}
                              />
                            );
                          })()}
                        {/* Fallback graph rendering when links were not placed inline */}
                        {!suppressBottomMarkdownMedia &&
                          !message.graph &&
                          !message.isUser &&
                          (() => {
                            const graphUrls = extractGraphUrlsFromText(
                              message.text || ""
                            );
                            if (!graphUrls.length) return null;
                            return (
                              <>
                                {graphUrls.map((graphUrl, index) => (
                                  <GraphIframeCell
                                    key={`graph-link-${message.id}-${index}`}
                                    graphUrl={graphUrl}
                                    isDarkMode={isDarkMode}
                                  />
                                ))}
                              </>
                            );
                          })()}

                        {/* Excel (v1): code-only at bottom; file links inline when ordered in text */}
                        {API_VERSION === "v1" &&
                          !message.isUser &&
                          (() => {
                            const excelUrls = extractExcelUrlsFromText(
                              message.text || ""
                            );
                            const hasExcelCode =
                              message.excelCode &&
                              String(message.excelCode).trim();
                            const excelBlockSx = {
                              marginTop: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px",
                              overflow: "auto",
                              backgroundColor: "#fff",
                              width: "100%",
                              maxWidth: "100%",
                              position: "relative",
                              WebkitOverflowScrolling: "touch",
                            };
                            const showCodeBlock =
                              hasExcelCode && excelUrls.length === 0;
                            const showFileBlocks =
                              !suppressBottomMarkdownMedia &&
                              excelUrls.length > 0;
                            if (!showCodeBlock && !showFileBlocks) return null;
                            return (
                              <>
                                {showCodeBlock && (
                                  <Box
                                    key={`excel-code-${message.id}`}
                                    className="message-graph message-excel"
                                    sx={excelBlockSx}
                                  >
                                    <iframe
                                      srcDoc={processExcelHTML(
                                        message.excelCode
                                      )}
                                      title="Excel Code"
                                      sandbox="allow-same-origin"
                                      style={{
                                        width: "100%",
                                        minHeight: 300,
                                        border: "none",
                                        display: "block",
                                        backgroundColor: "#fff",
                                      }}
                                    />
                                  </Box>
                                )}
                                {showFileBlocks &&
                                  excelUrls.map((url, index) => (
                                    <Box
                                      key={`excel-${message.id}-${index}`}
                                      className="message-graph message-excel"
                                      sx={excelBlockSx}
                                    >
                                      <ExcelIframe
                                        excelUrls={[url]}
                                        isDarkMode={isDarkMode}
                                      />
                                    </Box>
                                  ))}
                              </>
                            );
                          })()}

                        {/* Source / Reference block after chart & Excel so citations sit below embeds */}
                        {markdownTextForSourceFooter &&
                          String(markdownTextForSourceFooter).trim() &&
                          !message.isUser && (
                            <Box
                              className="message-text message-source-after-media"
                              sx={{ marginTop: 2, width: "100%" }}
                              dir={
                                message.textDirection ||
                                (message.isUser
                                  ? message.questionLanguage === "en"
                                    ? "ltr"
                                    : "rtl"
                                  : "rtl")
                              }
                            >
                              <ReactMarkdown
                                key={`md-src-${message.id}-${messageTextRaw.length}-${markdownTextForSourceFooter.length}`}
                                remarkPlugins={[remarkGfm, remarkBreaks]}
                                rehypePlugins={[rehypeRaw, rehypeHighlight]}
                                children={markdownTextForSourceFooter}
                                components={getCachedMarkdownComponents(
                                  markdownComponentsCacheKey,
                                  () => ({})
                                )}
                              />
                            </Box>
                          )}

                        {/* Retry button for stopped messages */}
                        {message.isStopped && lastFailedMessage && (
                          <Box
                            className="stopped-message-actions"
                            dir={
                              message.textDirection ||
                              (message.isUser
                                ? message.questionLanguage === "en"
                                  ? "ltr"
                                  : "rtl"
                                : "rtl")
                            }
                          >
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Refresh />}
                              onClick={handleRetry}
                              className="retry-button-inline"
                              disabled={isStreaming}
                            >
                              {intl.formatMessage({ id: "retry" })}
                            </Button>
                          </Box>
                        )}

                        {/* Regenerate button for completed bot messages (excluding welcome and info) */}
                        {!message.isUser &&
                          !message.isStreaming &&
                          !message.isError &&
                          !message.isStopped &&
                          message.text &&
                          typeof message.text === "string" &&
                          message.text.trim() &&
                          messages.indexOf(message) > 0 && // Not the first message (welcome)
                          !message.text.includes("🔍") && // Not an info/thinking message
                          !message.text.includes("👋") && // Not a welcome message
                          (() => {
                            // Only show if there's a user message before this bot message
                            const messageIndex = messages.indexOf(message);
                            for (let i = messageIndex - 1; i >= 0; i--) {
                              if (messages[i].isUser) return true;
                            }
                            return false;
                          })() && (
                            <Box
                              className="regenerate-message-actions"
                              dir={
                                message.textDirection ||
                                (message.isUser
                                  ? message.questionLanguage === "en"
                                    ? "rtl"
                                    : "ltr"
                                  : "ltr")
                              }
                            >
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<Refresh />}
                                onClick={() => handleRegenerate(message.id)}
                                className="regenerate-button"
                                disabled={isStreaming}
                              >
                                {intl.formatMessage({ id: "regenerate" })}
                              </Button>
                            </Box>
                          )}
                      </Box>
                    </div>

                    {/* {message.suggestions && (
                    <Box className="message-suggestions">
                      {message.suggestions.map((suggestion, index) => (
                        <Chip
                          key={index}
                          label={suggestion}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="suggestion-chip"
                          size="small"
                        />
                      ))}
                    </Box>
                  )} */}

                    <MessageActions
                      key={`${message.id}-${
                        streamingMessageId === message.id ? "streaming" : "done"
                      }-${message.isStreaming}`}
                      messageId={message.id}
                      isUser={message.isUser}
                      isWelcome={
                        messages.indexOf(message) === 0 &&
                        !!message.suggestions &&
                        !message.isUser
                      }
                      isStreaming={message.isStreaming}
                      streamingMessageId={streamingMessageId}
                      hasText={
                        !!message.text &&
                        typeof message.text === "string" &&
                        message.text.trim().length > 0
                      }
                      likes={Math.min(1, Math.max(0, message.likes || 0))}
                      dislikes={Math.min(1, Math.max(0, message.dislikes || 0))}
                      infoMessage={infoMessage}
                      canExport={message.canExport === true}
                      isThinking={message.isThinking === true}
                    />
                  </Box>
                </Slide>
              );
            })}

            {/* {isStreaming && (
              <Box className="streaming-indicator">
                <Box className="streaming-content">
                  <CircularProgress size={16} className="streaming-spinner" />
                  <Typography variant="caption" className="streaming-text">
                    {infoMessage ||
                      intl.formatMessage({ id: "chatbot.writing" })}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={streamingProgress}
                  className="streaming-progress"
                />
              </Box>
            )} */}

            {apiError && (
              <Box className="error-indicator">
                <Box className="error-content">
                  <Error fontSize="small" className="error-icon" />
                  <Typography variant="caption" className="error-text">
                    {apiError}
                  </Typography>
                </Box>
                {lastFailedMessage && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Refresh />}
                    onClick={handleRetry}
                    className="retry-button"
                    disabled={isStreaming}
                  >
                    {intl.formatMessage({ id: "retry" })}
                  </Button>
                )}
              </Box>
            )}

            {/* Skeleton loading for pagination (at bottom) */}
            {isLoadingMoreMessages && (
              <Box
                sx={{
                  padding: "12px 16px 16px",
                  marginTop: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    [dir === "rtl" ? "marginRight" : "marginLeft"]: 0,
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width="70%"
                    height={60}
                    sx={{ maxWidth: 300, borderRadius: 2 }}
                    animation="wave"
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    [dir === "rtl" ? "marginLeft" : "marginRight"]: 0,
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width="75%"
                    height={65}
                    sx={{ maxWidth: 320, borderRadius: 2 }}
                    animation="wave"
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    [dir === "rtl" ? "marginRight" : "marginLeft"]: 0,
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width="65%"
                    height={50}
                    sx={{ maxWidth: 280, borderRadius: 2 }}
                    animation="wave"
                  />
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Box className="chatbot-input" dir={dir}>
            {/* Voice control bar (matches waveform style) */}
            {(isTranscribing || isListening) && (
              <Box
                className={
                  isTranscribing
                    ? "chatbot-transcribing-indicator"
                    : "chatbot-recording-indicator"
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: { xs: 0.5, sm: 1 },
                  width: "100%",
                  minHeight: { xs: 46, sm: 56 },
                  padding: {
                    xs: isTinyMobile ? "4px 8px" : "6px 10px",
                    sm: "8px 14px",
                  },
                  marginBottom: 0,
                  borderRadius: "999px",
                  background: isDarkMode ? "#0f172a" : "#ffffff",
                  border: "1px solid rgba(9, 134, 237, 0.2)",
                  boxShadow: isDarkMode
                    ? "0 2px 12px rgba(0,0,0,0.35)"
                    : "0 2px 10px rgba(15, 23, 42, 0.08)",
                  overflow: "hidden",
                  "@keyframes voiceWavePulse": {
                    "0%, 100%": { opacity: 0.4, transform: "scaleY(0.5)" },
                    "50%": { opacity: 1, transform: "scaleY(1)" },
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    minWidth: {
                      xs: isTinyMobile ? "38px" : "46px",
                      sm: "68px",
                    },
                    fontFamily: "Tajawal, sans-serif",
                    fontSize: isTranscribing
                      ? { xs: isTinyMobile ? "10px" : "11px", sm: "13px" }
                      : { xs: isTinyMobile ? "14px" : "16px", sm: "20px" },
                    color: "var(--main-color)",
                    fontWeight: isTranscribing ? 600 : 500,
                    textAlign: "center",
                    lineHeight: 1,
                  }}
                >
                  {isTranscribing
                    ? intl.formatMessage({
                        id: "chatbot.transcribing",
                        defaultMessage: "جاري التحويل",
                      })
                    : `0:${String(recordingTime).padStart(2, "0")}`}
                </Typography>

                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: "1px", sm: "2px" },
                    px: { xs: isTinyMobile ? "2px" : "4px", sm: "8px" },
                    minWidth: 0,
                    direction: "ltr",
                  }}
                >
                  {Array.from({
                    length: isTinyMobile ? 24 : isSmallMobile ? 32 : 44,
                  }).map((_, idx) => (
                    <Box
                      key={`voice-wave-${idx}`}
                      component="span"
                      sx={{
                        width: { xs: "1.5px", sm: "2px" },
                        borderRadius: "999px",
                        height: {
                          xs: `${6 + ((idx * 7) % 10)}px`,
                          sm: `${10 + ((idx * 7) % 16)}px`,
                        },
                        backgroundColor: isDarkMode
                          ? "rgba(148, 163, 184, 0.7)"
                          : "rgba(100, 116, 139, 0.55)",
                        transformOrigin: "center",
                        animation:
                          isListening && !isRecordingPaused
                            ? "voiceWavePulse 1.1s ease-in-out infinite"
                            : "none",
                        animationDelay: `${(idx % 12) * 0.06}s`,
                      }}
                    />
                  ))}
                </Box>

                {!isTranscribing && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: "2px", sm: "4px" },
                      flexShrink: 0,
                      [dir === "rtl" ? "marginRight" : "marginLeft"]: "2px",
                    }}
                  >
                    <IconButton
                      onClick={handleDeleteRecording}
                      size="small"
                      sx={{
                        color: "var(--main-color)",
                        width: { xs: 24, sm: 30 },
                        height: { xs: 24, sm: 30 },
                        "&:hover": {
                          backgroundColor: "rgba(9, 134, 237, 0.1)",
                        },
                      }}
                      title={
                        intl.formatMessage({
                          id: "chatbot.deleteRecording",
                        }) || "إلغاء التسجيل"
                      }
                    >
                      <Close sx={{ fontSize: { xs: 16, sm: 20 } }} />
                    </IconButton>
                    <IconButton
                      onClick={handleStopRecording}
                      size="small"
                      sx={{
                        color: "var(--main-color)",
                        width: { xs: 24, sm: 30 },
                        height: { xs: 24, sm: 30 },
                        "&:hover": {
                          backgroundColor: "rgba(9, 134, 237, 0.1)",
                        },
                      }}
                      title={
                        intl.formatMessage({
                          id: "chatbot.stopRecording",
                        }) || "تأكيد وإرسال التسجيل"
                      }
                    >
                      <CheckCircle sx={{ fontSize: { xs: 16, sm: 20 } }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
                gap: "12px",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <IconButton
                  className="chatbot-mic"
                  onClick={handleMicClick}
                  sx={{
                    position: "absolute",
                    [dir === "rtl" ? "right" : "left"]: "8px",
                    zIndex: 1,
                    color: isListening
                      ? isRecordingPaused
                        ? "#f59e0b"
                        : "#ef4444"
                      : "#C89637",
                    background: isListening
                      ? isRecordingPaused
                        ? "#fef3c7"
                        : "#fee2e2"
                      : "white",
                    width: { xs: "32px", sm: "36px" },
                    height: { xs: "32px", sm: "36px" },
                    animation:
                      isListening && !isRecordingPaused
                        ? "pulse 1.5s infinite"
                        : "none",
                    "&:hover": {
                      background: isListening
                        ? isRecordingPaused
                          ? "#fde68a"
                          : "#fecaca"
                        : "#f5f5f5",
                    },
                  }}
                  title={
                    isListening
                      ? isRecordingPaused
                        ? reachedMaxTime
                          ? intl.formatMessage({
                              id: "chatbot.maxTimeReached",
                            }) || "تم الوصول للحد الأقصى - لا يمكن الاستكمال"
                          : intl.formatMessage({
                              id: "chatbot.resumeRecording",
                            }) || "استكمال التسجيل"
                        : intl.formatMessage({
                            id: "chatbot.pauseRecording",
                          }) || "إيقاف مؤقت"
                      : intl.formatMessage({ id: "chatbot.startRecording" }) ||
                        "بدء التسجيل"
                  }
                  disabled={isListening && isRecordingPaused && reachedMaxTime}
                >
                  {isListening ? (
                    isRecordingPaused ? (
                      <PlayArrow fontSize="small" />
                    ) : (
                      <Pause fontSize="small" />
                    )
                  ) : (
                    <Mic fontSize="small" />
                  )}
                </IconButton>

                <Box sx={{ position: "relative", width: "100%" }}>
                  <TextField
                    inputRef={inputFieldRef}
                    fullWidth
                    placeholder={intl.formatMessage({
                      id: "chatbot.askQuestion",
                    })}
                    defaultValue=""
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    className="chatbot-textfield"
                    multiline
                    maxRows={3}
                    disabled={isListening || isTranscribing}
                    error={inputValue.length >= MAX_INPUT_LENGTH}
                    helperText={
                      inputValue.length >= MAX_INPUT_LENGTH
                        ? intl.formatMessage({
                            id: "chatbot.maxLengthReached",
                            defaultMessage:
                              "تم الوصول إلى الحد الأقصى لعدد الأحرف",
                          })
                        : // : inputValue.length > MAX_INPUT_LENGTH * 0.9
                          // ? `${inputValue.length} / ${MAX_INPUT_LENGTH}`
                          ""
                    }
                    FormHelperTextProps={{
                      sx: {
                        position: "absolute",
                        bottom: "-20px",
                        right: dir === "rtl" ? "0" : "auto",
                        left: dir === "rtl" ? "auto" : "0",
                        margin: 0,
                        fontSize: "0.75rem",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        paddingLeft: dir === "rtl" ? "14px" : "50px",
                        paddingRight: dir === "rtl" ? "50px" : "14px",
                        paddingBottom: inputValue.length > 0 ? "24px" : "16px",
                      },
                    }}
                  />
                  {/* Character counter - shown from first character */}
                  {inputValue.length > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom:
                          inputValue.length >= MAX_INPUT_LENGTH ? "2px" : "4px",
                        right: dir === "rtl" ? "auto" : "8px",
                        left: dir === "rtl" ? "8px" : "auto",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        pointerEvents: "none",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          color:
                            inputValue.length >= MAX_INPUT_LENGTH
                              ? "#d32f2f"
                              : inputValue.length > MAX_INPUT_LENGTH * 0.95
                              ? "#ed6c02"
                              : "#666",
                          fontWeight:
                            inputValue.length >= MAX_INPUT_LENGTH ? 600 : 400,
                          transition: "color 0.2s ease-in-out",
                        }}
                      >
                        {inputValue.length} / {MAX_INPUT_LENGTH}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              {isStreaming ? (
                <Button
                  onClick={handleStopStreaming}
                  className="chatbot-stop"
                  variant="contained"
                  color="error"
                >
                  <Stop />
                </Button>
              ) : (
                <Button
                  onClick={handleSendMessage}
                  disabled={
                    !inputValue.trim() ||
                    isListening ||
                    isTranscribing ||
                    isStreaming
                  }
                  className="chatbot-send"
                  variant="contained"
                >
                  <Send
                    sx={{
                      transform:
                        dir === "rtl" ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </Button>
              )}

              {/* <Button
                onClick={handleSendMessage}
                disabled={
                  !inputValue.trim() ||
                  isListening ||
                  isTranscribing ||
                  isStreaming
                }
                className="chatbot-send"
                variant="contained"
              >
                <Send
                  sx={{
                    transform:
                      dir === "rtl" ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </Button> */}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Conversation Confirmation — DELETE /api/conversations/{conv_id} */}
      <Dialog
        open={Boolean(deleteConfirmChat)}
        onClose={closeDeleteChatConfirm}
        maxWidth="xs"
        fullWidth
        className="chatbot-delete-confirm-dialog"
        dir={dir}
        sx={{ zIndex: 100001 }}
        PaperProps={{
          className: "chatbot-delete-confirm-paper",
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(15, 23, 42, 0.22)",
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.45)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <DialogContent sx={{ pt: 3.5, px: 3, pb: 1.5 }}>
          <Box className="chatbot-delete-confirm-body">
            <Box className="chatbot-delete-confirm-icon" aria-hidden>
              <Delete />
            </Box>
            <Typography
              variant="h6"
              className="chatbot-delete-confirm-title"
              sx={{
                fontFamily: "Tajawal, sans-serif",
                fontWeight: 700,
                fontSize: "1.15rem",
                color: "#0f172a",
                textAlign: "center",
                mb: 1,
              }}
            >
              {intl.formatMessage({
                id: "chatbot.deleteChatTitle",
                defaultMessage:
                  dir === "rtl" ? "حذف المحادثة؟" : "Delete conversation?",
              })}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Tajawal, sans-serif",
                color: "#64748b",
                textAlign: "center",
                lineHeight: 1.7,
                mb: 1.5,
              }}
            >
              {intl.formatMessage({
                id: "chatbot.deleteChatConfirm",
                defaultMessage:
                  dir === "rtl"
                    ? "سيتم حذف هذه المحادثة نهائياً ولا يمكن استرجاعها."
                    : "This conversation will be permanently deleted and cannot be recovered.",
              })}
            </Typography>
            {deleteConfirmChat?.title ? (
              <Box className="chatbot-delete-confirm-preview">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Tajawal, sans-serif",
                    fontWeight: 600,
                    color: "#1e293b",
                    fontSize: "13px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                  title={deleteConfirmChat.title}
                >
                  {deleteConfirmChat.title}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
            gap: 1.25,
            justifyContent: "stretch",
          }}
        >
          <Button
            onClick={closeDeleteChatConfirm}
            disabled={isDeletingChat}
            className="chatbot-delete-cancel-btn"
            fullWidth
            sx={{
              fontFamily: "Tajawal, sans-serif",
              fontWeight: 600,
              borderRadius: "12px",
              textTransform: "none",
              py: 1.1,
              color: "#475569",
              backgroundColor: "#f1f5f9",
              "&:hover": {
                backgroundColor: "#e2e8f0",
              },
            }}
          >
            {intl.formatMessage({
              id: "chatbot.deleteChatCancel",
              defaultMessage: dir === "rtl" ? "إلغاء" : "Cancel",
            })}
          </Button>
          <Button
            onClick={confirmDeleteChatFromHistory}
            disabled={isDeletingChat}
            className="chatbot-delete-confirm-btn"
            fullWidth
            variant="contained"
            disableElevation
            sx={{
              fontFamily: "Tajawal, sans-serif",
              fontWeight: 700,
              borderRadius: "12px",
              textTransform: "none",
              py: 1.1,
              // display: "flex",
              // alignItems: "center",
              // gap: 0.5,
              background:
                "linear-gradient(135deg, #ef4444 0%, #dc2626 55%, #b91c1c 100%)",
              boxShadow: "0 8px 20px rgba(220, 38, 38, 0.28)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #dc2626 0%, #b91c1c 55%, #991b1b 100%)",
                boxShadow: "0 10px 24px rgba(185, 28, 28, 0.35)",
              },
              "&.Mui-disabled": {
                background: "rgba(239, 68, 68, 0.45)",
                color: "#fff",
              },
            }}
            endIcon={
              isDeletingChat ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <Delete fontSize="small" />
              )
            }
          >
            {isDeletingChat
              ? intl.formatMessage({
                  id: "chatbot.deletingChat",
                  defaultMessage:
                    dir === "rtl" ? "جاري الحذف..." : "Deleting...",
                })
              : intl.formatMessage({
                  id: "chatbot.deleteChat",
                  defaultMessage:
                    dir === "rtl" ? "حذف المحادثة" : "Delete conversation",
                })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login Required Dialog */}
      <Dialog
        open={showLoginDialog}
        onClose={handleCloseLoginDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px", sm: "20px", md: "24px" },
            background: isDarkMode
              ? muiTheme.palette.background.paper
              : "#ffffff",
            boxShadow: isDarkMode
              ? "0 25px 80px rgba(0, 0, 0, 0.5)"
              : "0 25px 80px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            position: "fixed",
            right: { xs: "10px", sm: "15px", md: "20px" },
            bottom: { xs: "80px", sm: "90px", md: "100px" },
            maxWidth: {
              xs: "calc(100% - 20px)",
              sm: "calc(100% - 30px)",
              md: "420px",
            },
            width: {
              xs: "calc(100% - 20px)",
              sm: "calc(100% - 30px)",
              md: "420px",
            },
            maxHeight: { xs: "70vh", sm: "75vh", md: "80vh" },
            direction: dir,
          },
        }}
        className="login-required-dialog"
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "transparent",
          },
        }}
      >
        <IconButton
          onClick={handleCloseLoginDialog}
          sx={{
            position: "absolute",
            top: { xs: "18px", sm: "20px", md: "22px" },
            left:
              dir === "rtl" ? { xs: "22px", sm: "24px", md: "25px" } : "auto",
            right:
              dir === "rtl" ? "auto" : { xs: "22px", sm: "24px", md: "25px" },
            color: isDarkMode ? muiTheme.palette.text.secondary : "#666",
            zIndex: 10,
            background: isDarkMode
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.05)",
            width: { xs: "32px", sm: "36px", md: "40px" },
            height: { xs: "32px", sm: "36px", md: "40px" },
            "&:hover": {
              background: isDarkMode
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <Close sx={{ fontSize: { xs: "18px", sm: "20px", md: "24px" } }} />
        </IconButton>

        <DialogContent
          sx={{
            padding: { xs: "16px", sm: "20px", md: "24px" },
            background: isDarkMode
              ? muiTheme.palette.background.paper
              : "#ffffff",
            position: "relative",
            color: isDarkMode ? muiTheme.palette.text.primary : "inherit",
          }}
          dir={dir}
        >
          {/* Main Content Container */}
          <Box
            sx={{
              display: "flex",
              flexDirection: dir === "rtl" ? "row-reverse" : "row",
              alignItems: "center",
              gap: { xs: "4px", sm: "6px", md: "8px" },
              flexWrap: { xs: "wrap", sm: "nowrap" },
              backgroundColor: isDarkMode
                ? muiTheme.palette.background.paper
                : "#ffffff",
            }}
          >
            {/* Welcome Text - Left side */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                flex: 1,
                marginInlineStart:
                  dir === "rtl"
                    ? { xs: "-20px", sm: "-30px", md: "-40px" }
                    : "auto",
                marginInlineEnd:
                  dir === "rtl"
                    ? "auto"
                    : { xs: "-40px", sm: "-60px", md: "-80px" },
                position: "relative",
                zIndex: 1,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "'Tajawal', sans-serif",
                  fontWeight: 700,
                  color: "transparent",
                  background:
                    "linear-gradient(245.97deg, #22599F 22.73%, #C89637 79.29%)",
                  backgroundClip: "text",
                  textFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "16px", sm: "18px", md: "20px" },
                  lineHeight: 1.3,
                }}
              >
                <FormattedMessage
                  id="login-required-title"
                  defaultMessage="مرحباً بك في"
                />
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "'Tajawal', sans-serif",
                  fontWeight: 700,
                  background:
                    "linear-gradient(245.97deg, #22599F 22.73%, #C89637 79.29%)",
                  backgroundClip: "text",
                  textFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                  fontSize: { xs: "16px", sm: "18px", md: "20px" },
                  lineHeight: 1.3,
                }}
              >
                <FormattedMessage
                  id="login-required-subtitle"
                  defaultMessage="نظام الدعم الذكي"
                />
              </Typography>
            </Box>

            {/* Robot with Video - Right side */}
            <Box
              sx={{
                position: "relative",
                width: { xs: "120px", sm: "180px", md: "240px" },
                height: { xs: "120px", sm: "180px", md: "240px" },
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <video
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              >
                <source src="/chatbot.mp4" type="video/mp4" />
              </video>
            </Box>
          </Box>

          {/* Message Box */}
          <Box
            sx={{
              marginTop: { xs: "-30px", sm: "-40px", md: "-55px" },
              padding: { xs: "12px", sm: "14px", md: "16px" },
              textAlign: "center",
              background: isDarkMode
                ? muiTheme.palette.background.default
                : "#ffffff",
              borderRadius: { xs: "6px", sm: "7px", md: "8px" },
              boxShadow: isDarkMode
                ? "0 2px 8px rgba(0, 0, 0, 0.5)"
                : "0 2px 8px rgba(0, 0, 0, 0.2)",
              position: "relative",
              zIndex: 1,
              border: isDarkMode
                ? `1px solid ${muiTheme.palette.divider}`
                : "none",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontFamily: "'Tajawal', sans-serif",
                color: isDarkMode ? muiTheme.palette.text.primary : "#21589E",
                fontSize: { xs: "12px", sm: "14px", md: "16px" },
                fontWeight: 500,
              }}
            >
              <FormattedMessage
                id="login-required-message"
                defaultMessage="يرجى تسجيل الدخول أو إنشاء حساب جديد للمتابعة للحصول على خدمات"
              />
            </Typography>
          </Box>

          {/* Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
              marginTop: "20px",
            }}
          >
            <Button
              variant="contained"
              onClick={handleLoginClick}
              fullWidth
              sx={{
                background: "var(--main-color)",
                color: "white",
                padding: { xs: "10px", sm: "11px", md: "12px" },
                borderRadius: { xs: "6px", sm: "7px", md: "8px" },
                fontSize: { xs: "14px", sm: "15px", md: "16px" },
                fontWeight: 600,
                fontFamily: "'Tajawal', sans-serif",
                textTransform: "none",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "#0a6bb8",
                  boxShadow: "0 2px 8px rgba(9, 134, 237, 0.3)",
                },
              }}
            >
              <FormattedMessage id="login" defaultMessage="تسجيل الدخول" />
            </Button>

            <Button
              variant="contained"
              onClick={handleCreateAccountClick}
              fullWidth
              sx={{
                background: "var(--main-color)",
                color: "white",
                padding: { xs: "10px", sm: "11px", md: "12px" },
                borderRadius: { xs: "6px", sm: "7px", md: "8px" },
                fontSize: { xs: "14px", sm: "15px", md: "16px" },
                fontWeight: 600,
                fontFamily: "'Tajawal', sans-serif",
                textTransform: "none",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "#0a6bb8",
                  boxShadow: "0 2px 8px rgba(9, 134, 237, 0.3)",
                },
              }}
            >
              <FormattedMessage
                id="create-account"
                defaultMessage="إنشاء حساب"
              />
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {typeof document !== "undefined" &&
        createPortal(
          <ToastContainer
            position={getToastPosition()}
            autoClose={10000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={locale === "ar"}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
            limit={3}
            className="chatbot-toast-container"
            toastClassName="chatbot-feedback-toast"
            bodyClassName="chatbot-feedback-toast-body"
            progressClassName="chatbot-feedback-toast-progress"
            style={{
              // Above chatbot dialog (99999) and delete-confirm dialog (100001)
              zIndex: 2000000,
            }}
          />,
          document.body
        )}
    </>
  );
};

export default ChatBot;
